import { SamClient, SamClient as SamClient$1 } from "@virdx/sam-client";
import { ClipType, EndType, FillRule, JoinType, PolyTree64, booleanOpWithPolyTree, inflatePaths, isPositive } from "clipper2-ts";
import * as zarr from "zarrita";
import earcut from "earcut";

//#region src/coord-buffer.ts
function coordCount(buf) {
	return buf.length / 2;
}
function getX(buf, i) {
	return buf[i * 2];
}
function getY(buf, i) {
	return buf[i * 2 + 1];
}
function createCoordBuffer(count) {
	return new Int32Array(count * 2);
}
function fromPointArray(points) {
	const buf = new Int32Array(points.length * 2);
	for (let i = 0; i < points.length; i++) {
		buf[i * 2] = Math.round(points[i].x);
		buf[i * 2 + 1] = Math.round(points[i].y);
	}
	return buf;
}
function toPointArray(buf) {
	const n = buf.length / 2;
	const result = new Array(n);
	for (let i = 0; i < n; i++) result[i] = {
		x: buf[i * 2],
		y: buf[i * 2 + 1]
	};
	return result;
}
function cloneCoords(buf) {
	return new Int32Array(buf);
}
function equalCoords(a, b) {
	if (a.length !== b.length) return false;
	for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
	return true;
}

//#endregion
//#region src/utils.ts
/**
* Create a default transform with no offset, rotation, or cropping
* @param imageWidth - Width of the image at full resolution
* @param imageHeight - Height of the image at full resolution
*/
function createDefaultTransform(imageWidth, imageHeight) {
	return {
		translateX: 0,
		translateY: 0,
		rotation: 0,
		cropLeft: 0,
		cropTop: 0,
		cropRight: imageWidth,
		cropBottom: imageHeight
	};
}
/**
* Clamp a value between min and max
*/
function clamp(value, min, max) {
	return Math.max(min, Math.min(max, value));
}

//#endregion
//#region src/annotations.ts
const EPSILON = 1e-7;
const DEFAULT_ANNOTATION_STYLE = {
	strokeColor: "#ff4d4f",
	strokeOpacity: 1,
	fillColor: "#ff4d4f",
	fillOpacity: .2,
	strokeWidth: 2
};
function computeIntersection(p1x, p1y, p2x, p2y, p3x, p3y, p4x, p4y) {
	const denominator = (p1x - p2x) * (p3y - p4y) - (p1y - p2y) * (p3x - p4x);
	if (Math.abs(denominator) < EPSILON) return null;
	const t = ((p1x - p3x) * (p3y - p4y) - (p1y - p3y) * (p3x - p4x)) / denominator;
	const u = ((p1x - p3x) * (p1y - p2y) - (p1y - p3y) * (p1x - p2x)) / denominator;
	if (t < -EPSILON || t > 1 + EPSILON || u < -EPSILON || u > 1 + EPSILON) return null;
	return {
		x: p1x + t * (p2x - p1x),
		y: p1y + t * (p2y - p1y)
	};
}
function findFirstSelfIntersection(points) {
	const n = coordCount(points);
	if (n < 4) return null;
	const segStartX = points[(n - 2) * 2];
	const segStartY = points[(n - 2) * 2 + 1];
	const segEndX = points[(n - 1) * 2];
	const segEndY = points[(n - 1) * 2 + 1];
	for (let i = 0; i <= n - 4; i++) {
		const priorStartX = points[i * 2];
		const priorStartY = points[i * 2 + 1];
		const priorEndX = points[(i + 1) * 2];
		const priorEndY = points[(i + 1) * 2 + 1];
		const intersection = computeIntersection(segStartX, segStartY, segEndX, segEndY, priorStartX, priorStartY, priorEndX, priorEndY);
		if (intersection) return {
			intersection,
			segmentStartIndex: i
		};
	}
	return null;
}
function extractClosedLoop(points, intersection) {
	const n = coordCount(points);
	const closedPts = [Math.round(intersection.intersection.x), Math.round(intersection.intersection.y)];
	for (let i = intersection.segmentStartIndex + 1; i <= n - 2; i++) {
		const px = points[i * 2];
		const py = points[i * 2 + 1];
		const lastIdx = closedPts.length - 2;
		const lastX = closedPts[lastIdx];
		const lastY = closedPts[lastIdx + 1];
		if (Math.abs(lastX - px) > EPSILON || Math.abs(lastY - py) > EPSILON) closedPts.push(px, py);
	}
	return new Int32Array(closedPts);
}
function polygonArea(points) {
	const n = coordCount(points);
	if (n < 3) return 0;
	let sum = 0;
	for (let i = 0; i < n; i++) {
		const j = (i + 1) % n;
		const p1x = points[i * 2];
		const p1y = points[i * 2 + 1];
		const p2x = points[j * 2];
		const p2y = points[j * 2 + 1];
		sum += p1x * p2y - p2x * p1y;
	}
	return Math.abs(sum) * .5;
}
function computeBoundingBox(points) {
	const n = coordCount(points);
	if (n === 0) return {
		left: 0,
		top: 0,
		right: 0,
		bottom: 0
	};
	let left = points[0];
	let right = points[0];
	let top = points[1];
	let bottom = points[1];
	for (let i = 1; i < n; i++) {
		const x = points[i * 2];
		const y = points[i * 2 + 1];
		if (x < left) left = x;
		if (x > right) right = x;
		if (y < top) top = y;
		if (y > bottom) bottom = y;
	}
	return {
		left,
		top,
		right,
		bottom
	};
}
function bboxIntersects(a, b) {
	return !(a.left > b.right || a.right < b.left || a.top > b.bottom || a.bottom < b.top);
}
function pointInPolygon(point, polygon) {
	const n = coordCount(polygon);
	if (n < 3) return false;
	let inside = false;
	for (let i = 0, j = n - 1; i < n; j = i++) {
		const pix = polygon[i * 2];
		const piy = polygon[i * 2 + 1];
		const pjx = polygon[j * 2];
		const pjy = polygon[j * 2 + 1];
		const intersects = piy > point.y !== pjy > point.y && point.x < (pjx - pix) * (point.y - piy) / (pjy - piy || EPSILON) + pix;
		if (intersects) inside = !inside;
	}
	return inside;
}
function translatePoints(points, dx, dy) {
	const result = new Int32Array(points.length);
	const rdx = Math.round(dx);
	const rdy = Math.round(dy);
	for (let i = 0; i < points.length; i += 2) {
		result[i] = points[i] + rdx;
		result[i + 1] = points[i + 1] + rdy;
	}
	return result;
}
function cloneAnnotation(annotation) {
	return {
		...annotation,
		points: cloneCoords(annotation.points),
		holes: annotation.holes?.map((hole) => cloneCoords(hole)),
		style: { ...annotation.style },
		bbox: { ...annotation.bbox }
	};
}
function normalizeStyle(style, fallback = DEFAULT_ANNOTATION_STYLE) {
	return {
		strokeColor: style.strokeColor ?? fallback.strokeColor,
		strokeOpacity: clamp(style.strokeOpacity ?? fallback.strokeOpacity, 0, 1),
		fillColor: style.fillColor ?? fallback.fillColor,
		fillOpacity: clamp(style.fillOpacity ?? fallback.fillOpacity, 0, 1),
		strokeWidth: Math.max(.5, style.strokeWidth ?? fallback.strokeWidth ?? 2)
	};
}
function sanitizeAnnotation(annotation) {
	const points = cloneCoords(annotation.points);
	const holes = annotation.holes?.map((hole) => cloneCoords(hole));
	const bbox = computeBoundingBox(points);
	const style = normalizeStyle(annotation.style);
	return {
		...annotation,
		points,
		holes,
		style,
		bbox
	};
}
function quantizeAnnotation(annotation, _precision) {
	const points = cloneCoords(annotation.points);
	const holes = annotation.holes?.map((hole) => cloneCoords(hole));
	return {
		...annotation,
		points,
		holes,
		bbox: computeBoundingBox(points)
	};
}
function orientationVal(ax, ay, bx, by, cx, cy) {
	return (bx - ax) * (cy - ay) - (by - ay) * (cx - ax);
}
function onSegmentVal(ax, ay, bx, by, cx, cy) {
	return Math.min(ax, cx) - EPSILON <= bx && bx <= Math.max(ax, cx) + EPSILON && Math.min(ay, cy) - EPSILON <= by && by <= Math.max(ay, cy) + EPSILON;
}
function segmentsIntersectBuf(a1x, a1y, a2x, a2y, b1x, b1y, b2x, b2y) {
	const o1 = orientationVal(a1x, a1y, a2x, a2y, b1x, b1y);
	const o2 = orientationVal(a1x, a1y, a2x, a2y, b2x, b2y);
	const o3 = orientationVal(b1x, b1y, b2x, b2y, a1x, a1y);
	const o4 = orientationVal(b1x, b1y, b2x, b2y, a2x, a2y);
	if ((o1 > EPSILON && o2 < -EPSILON || o1 < -EPSILON && o2 > EPSILON) && (o3 > EPSILON && o4 < -EPSILON || o3 < -EPSILON && o4 > EPSILON)) return true;
	if (Math.abs(o1) <= EPSILON && onSegmentVal(a1x, a1y, b1x, b1y, a2x, a2y)) return true;
	if (Math.abs(o2) <= EPSILON && onSegmentVal(a1x, a1y, b2x, b2y, a2x, a2y)) return true;
	if (Math.abs(o3) <= EPSILON && onSegmentVal(b1x, b1y, a1x, a1y, b2x, b2y)) return true;
	if (Math.abs(o4) <= EPSILON && onSegmentVal(b1x, b1y, a2x, a2y, b2x, b2y)) return true;
	return false;
}
function polygonsIntersect(polygonA, polygonB) {
	const nA = coordCount(polygonA);
	const nB = coordCount(polygonB);
	if (nA < 3 || nB < 3) return false;
	const bboxA = computeBoundingBox(polygonA);
	const bboxB = computeBoundingBox(polygonB);
	if (!bboxIntersects(bboxA, bboxB)) return false;
	for (let i = 0, iPrev = nA - 1; i < nA; iPrev = i++) {
		const a1x = polygonA[iPrev * 2];
		const a1y = polygonA[iPrev * 2 + 1];
		const a2x = polygonA[i * 2];
		const a2y = polygonA[i * 2 + 1];
		for (let j = 0, jPrev = nB - 1; j < nB; jPrev = j++) {
			const b1x = polygonB[jPrev * 2];
			const b1y = polygonB[jPrev * 2 + 1];
			const b2x = polygonB[j * 2];
			const b2y = polygonB[j * 2 + 1];
			if (segmentsIntersectBuf(a1x, a1y, a2x, a2y, b1x, b1y, b2x, b2y)) return true;
		}
	}
	return pointInPolygon({
		x: polygonA[0],
		y: polygonA[1]
	}, polygonB) || pointInPolygon({
		x: polygonB[0],
		y: polygonB[1]
	}, polygonA);
}
var AnnotationSpatialIndex = class {
	constructor(cellSize = 2048) {
		this.cellSize = void 0;
		this.bucketsByImage = new Map();
		this.cellsById = new Map();
		this.cellSize = cellSize;
	}
	clear(imageName) {
		if (!imageName) {
			this.bucketsByImage.clear();
			this.cellsById.clear();
			return;
		}
		this.bucketsByImage.delete(imageName);
		for (const [id, cellInfo] of this.cellsById.entries()) if (cellInfo.imageName === imageName) this.cellsById.delete(id);
	}
	upsert(annotation) {
		this.remove(annotation.id);
		const imageBuckets = this.getOrCreateImageBuckets(annotation.imageName);
		const keys = this.getBucketKeys(annotation.bbox);
		for (const key of keys) {
			let bucket = imageBuckets.get(key);
			if (!bucket) {
				bucket = new Set();
				imageBuckets.set(key, bucket);
			}
			bucket.add(annotation.id);
		}
		this.cellsById.set(annotation.id, {
			imageName: annotation.imageName,
			bbox: { ...annotation.bbox },
			cells: keys
		});
	}
	remove(id) {
		const cellInfo = this.cellsById.get(id);
		if (!cellInfo) return;
		const imageBuckets = this.bucketsByImage.get(cellInfo.imageName);
		if (imageBuckets) {
			for (const key of cellInfo.cells) {
				const bucket = imageBuckets.get(key);
				if (!bucket) continue;
				bucket.delete(id);
				if (bucket.size === 0) imageBuckets.delete(key);
			}
			if (imageBuckets.size === 0) this.bucketsByImage.delete(cellInfo.imageName);
		}
		this.cellsById.delete(id);
	}
	query(imageName, viewport) {
		const imageBuckets = this.bucketsByImage.get(imageName);
		if (!imageBuckets) return new Set();
		const matches = new Set();
		const keys = this.getBucketKeys(viewport);
		for (const key of keys) {
			const bucket = imageBuckets.get(key);
			if (!bucket) continue;
			for (const id of bucket) {
				const cellInfo = this.cellsById.get(id);
				if (cellInfo && bboxIntersects(cellInfo.bbox, viewport)) matches.add(id);
			}
		}
		return matches;
	}
	getOrCreateImageBuckets(imageName) {
		let imageBuckets = this.bucketsByImage.get(imageName);
		if (!imageBuckets) {
			imageBuckets = new Map();
			this.bucketsByImage.set(imageName, imageBuckets);
		}
		return imageBuckets;
	}
	getBucketKeys(viewport) {
		const minX = Math.floor(viewport.left / this.cellSize);
		const maxX = Math.floor(viewport.right / this.cellSize);
		const minY = Math.floor(viewport.top / this.cellSize);
		const maxY = Math.floor(viewport.bottom / this.cellSize);
		const keys = [];
		for (let y = minY; y <= maxY; y++) for (let x = minX; x <= maxX; x++) keys.push(`${x}:${y}`);
		return keys;
	}
};

//#endregion
//#region src/clipper-ops.ts
const SCALE = 100;
function toPath64(buf) {
	const n = coordCount(buf);
	const path = new Array(n);
	for (let i = 0; i < n; i++) path[i] = {
		x: buf[i * 2] * SCALE,
		y: buf[i * 2 + 1] * SCALE
	};
	return path;
}
function fromPath64(path) {
	const buf = new Int32Array(path.length * 2);
	for (let i = 0; i < path.length; i++) {
		buf[i * 2] = Math.round(path[i].x / SCALE);
		buf[i * 2 + 1] = Math.round(path[i].y / SCALE);
	}
	return buf;
}
function reversePath(path) {
	return [...path].reverse();
}
function toSubjectPaths(polygons, reverseHoles = false) {
	const paths = [];
	for (const poly of polygons) {
		const outerPath = toPath64(poly.outer);
		paths.push(isPositive(outerPath) ? outerPath : reversePath(outerPath));
		if (poly.holes) for (const hole of poly.holes) {
			const holePath = toPath64(hole);
			if (reverseHoles) paths.push(isPositive(holePath) ? reversePath(holePath) : holePath);
			else paths.push(holePath);
		}
	}
	return paths;
}
function extractPolyTree(tree) {
	const results = [];
	for (let i = 0; i < tree.count; i++) {
		const outerNode = tree.child(i);
		if (!outerNode.polygon || outerNode.polygon.length < 3) continue;
		const holes = [];
		for (let j = 0; j < outerNode.count; j++) {
			const holeNode = outerNode.child(j);
			if (!holeNode.polygon || holeNode.polygon.length < 3) continue;
			holes.push(fromPath64(holeNode.polygon));
			if (holeNode.count > 0) {
				const nested = extractPolyTree(holeNode);
				results.push(...nested);
			}
		}
		results.push({
			outer: fromPath64(outerNode.polygon),
			holes
		});
	}
	return results;
}
/**
* Merge overlapping same-label polygons into unified polygons with holes.
*/
function clipperUnion(polygons) {
	if (polygons.length === 0) return [];
	if (polygons.length === 1 && (!polygons[0].holes || polygons[0].holes.length === 0)) return [{
		outer: cloneCoords(polygons[0].outer),
		holes: []
	}];
	const subject = toSubjectPaths(polygons, true);
	const tree = new PolyTree64();
	booleanOpWithPolyTree(ClipType.Union, subject, null, tree, FillRule.NonZero);
	return extractPolyTree(tree);
}
/**
* Cut subject polygon by clip polygon (different-label cut).
* Returns remaining pieces of the subject after removing the clip area.
*/
function clipperDifference(subject, clip) {
	const subjectPaths = toSubjectPaths([subject]);
	const clipPaths = toSubjectPaths([clip]);
	const tree = new PolyTree64();
	booleanOpWithPolyTree(ClipType.Difference, subjectPaths, clipPaths, tree, FillRule.EvenOdd);
	return extractPolyTree(tree);
}
/**
* Clip polygons to an image bounding rectangle [0,0]→[width,height].
* Used to prevent brush-inflated polygons from extending outside image bounds.
*/
function clipperIntersectRect(polygons, bounds) {
	return clipperIntersectRects(polygons, [{
		left: 0,
		top: 0,
		right: bounds.width,
		bottom: bounds.height
	}]);
}
/**
* Clip polygons to the union of axis-aligned rectangles (e.g. edit regions).
* Overlapping rectangles are fine (NonZero fill); an empty list clips everything away.
*/
function clipperIntersectRects(polygons, rects) {
	if (polygons.length === 0 || rects.length === 0) return [];
	const rectPaths = rects.map((rect) => {
		const left = Math.round(rect.left * SCALE);
		const top = Math.round(rect.top * SCALE);
		const right = Math.round(rect.right * SCALE);
		const bottom = Math.round(rect.bottom * SCALE);
		return [
			{
				x: left,
				y: top
			},
			{
				x: right,
				y: top
			},
			{
				x: right,
				y: bottom
			},
			{
				x: left,
				y: bottom
			}
		];
	});
	const subjectPaths = toSubjectPaths(polygons, true);
	const tree = new PolyTree64();
	booleanOpWithPolyTree(ClipType.Intersection, subjectPaths, rectPaths, tree, FillRule.NonZero);
	return extractPolyTree(tree);
}
/**
* Draw tool: inflate a drawn path by radius to produce a thick-stroke polygon.
* Uses the same round-buffering as the eraser brush but returns the shape directly.
*/
function clipperInflatePath(brushPath, radius) {
	if (coordCount(brushPath) === 0 || radius <= 0) return [];
	const scaledRadius = Math.round(radius * SCALE);
	const scaledBrush = toPath64(brushPath);
	const inflated = inflatePaths([scaledBrush], scaledRadius, JoinType.Round, EndType.Round);
	if (inflated.length === 0) return [];
	const tree = new PolyTree64();
	booleanOpWithPolyTree(ClipType.Union, inflated, null, tree, FillRule.NonZero);
	return extractPolyTree(tree);
}
/**
* Erase tool: subtract a buffered brush path from a polygon.
* The brush path is inflated by radius to create the clip shape.
*/
function clipperSubtractBrush(subject, brushPath, radius) {
	if (coordCount(brushPath) === 0 || radius <= 0) return [{
		outer: cloneCoords(subject.outer),
		holes: [...subject.holes ?? []]
	}];
	const scaledRadius = Math.round(radius * SCALE);
	const scaledBrush = toPath64(brushPath);
	const inflated = inflatePaths([scaledBrush], scaledRadius, JoinType.Round, EndType.Round);
	if (inflated.length === 0) return [{
		outer: cloneCoords(subject.outer),
		holes: [...subject.holes ?? []]
	}];
	const subjectPaths = toSubjectPaths([subject]);
	const tree = new PolyTree64();
	booleanOpWithPolyTree(ClipType.Difference, subjectPaths, inflated, tree, FillRule.EvenOdd);
	return extractPolyTree(tree);
}

//#endregion
//#region src/colormaps.ts
/**
* Predefined colormaps for overlay rendering.
* Each colormap is a Float32Array of 1024 floats (256 RGBA entries, normalized 0-1).
* Label 0 is always transparent (background).
*/
/**
* Linear interpolation between color control points.
* Returns [r, g, b] in 0-255 range.
*/
function interpolateColor(index, controlPoints) {
	for (let i = 0; i < controlPoints.length - 1; i++) {
		const curr = controlPoints[i];
		const next = controlPoints[i + 1];
		if (index >= curr.pos && index <= next.pos) {
			const t = (index - curr.pos) / (next.pos - curr.pos);
			return [
				curr.rgb[0] + t * (next.rgb[0] - curr.rgb[0]),
				curr.rgb[1] + t * (next.rgb[1] - curr.rgb[1]),
				curr.rgb[2] + t * (next.rgb[2] - curr.rgb[2])
			];
		}
	}
	return controlPoints[controlPoints.length - 1].rgb;
}
/**
* Builds a colormap from control points.
* Returns Float32Array of 1024 floats (256 × RGBA).
*/
function buildColormap(controlPoints) {
	const data = new Float32Array(1024);
	data[0] = 0;
	data[1] = 0;
	data[2] = 0;
	data[3] = 0;
	for (let i = 1; i < 256; i++) {
		const rgb = interpolateColor(i, controlPoints);
		const base = i * 4;
		data[base + 0] = rgb[0] / 255;
		data[base + 1] = rgb[1] / 255;
		data[base + 2] = rgb[2] / 255;
		data[base + 3] = 1;
	}
	return data;
}
/**
* Builds a categorical colormap with repeating colors.
*/
function buildCategoricalColormap(colors) {
	const data = new Float32Array(1024);
	data[0] = 0;
	data[1] = 0;
	data[2] = 0;
	data[3] = 0;
	for (let i = 1; i < 256; i++) {
		const color = colors[(i - 1) % colors.length];
		const base = i * 4;
		data[base + 0] = color[0] / 255;
		data[base + 1] = color[1] / 255;
		data[base + 2] = color[2] / 255;
		data[base + 3] = 1;
	}
	return data;
}
/**
* Tab10 — 10 distinct categorical colors (matplotlib default).
* Colors cycle for labels > 10.
*/
const TAB10 = buildCategoricalColormap([
	[
		31,
		119,
		180
	],
	[
		255,
		127,
		14
	],
	[
		44,
		160,
		44
	],
	[
		214,
		39,
		40
	],
	[
		148,
		103,
		189
	],
	[
		140,
		86,
		75
	],
	[
		227,
		119,
		194
	],
	[
		127,
		127,
		127
	],
	[
		188,
		189,
		34
	],
	[
		23,
		190,
		207
	]
]);
/**
* Viridis — perceptually uniform sequential (yellow→green→blue→purple).
*/
const VIRIDIS = buildColormap([
	{
		pos: 1,
		rgb: [
			68,
			1,
			84
		]
	},
	{
		pos: 32,
		rgb: [
			72,
			35,
			116
		]
	},
	{
		pos: 64,
		rgb: [
			64,
			67,
			135
		]
	},
	{
		pos: 96,
		rgb: [
			52,
			94,
			141
		]
	},
	{
		pos: 128,
		rgb: [
			42,
			120,
			142
		]
	},
	{
		pos: 160,
		rgb: [
			33,
			145,
			140
		]
	},
	{
		pos: 192,
		rgb: [
			53,
			183,
			121
		]
	},
	{
		pos: 224,
		rgb: [
			122,
			209,
			81
		]
	},
	{
		pos: 255,
		rgb: [
			253,
			231,
			37
		]
	}
]);
/**
* Magma — sequential (yellow→red→purple→black).
*/
const MAGMA = buildColormap([
	{
		pos: 1,
		rgb: [
			0,
			0,
			4
		]
	},
	{
		pos: 32,
		rgb: [
			28,
			16,
			68
		]
	},
	{
		pos: 64,
		rgb: [
			79,
			18,
			123
		]
	},
	{
		pos: 96,
		rgb: [
			129,
			37,
			129
		]
	},
	{
		pos: 128,
		rgb: [
			181,
			54,
			122
		]
	},
	{
		pos: 160,
		rgb: [
			229,
			80,
			100
		]
	},
	{
		pos: 192,
		rgb: [
			251,
			135,
			97
		]
	},
	{
		pos: 224,
		rgb: [
			254,
			194,
			135
		]
	},
	{
		pos: 255,
		rgb: [
			252,
			253,
			191
		]
	}
]);
/**
* Inferno — sequential (yellow→orange→red→purple→black).
*/
const INFERNO = buildColormap([
	{
		pos: 1,
		rgb: [
			0,
			0,
			4
		]
	},
	{
		pos: 32,
		rgb: [
			31,
			12,
			72
		]
	},
	{
		pos: 64,
		rgb: [
			85,
			15,
			109
		]
	},
	{
		pos: 96,
		rgb: [
			139,
			41,
			129
		]
	},
	{
		pos: 128,
		rgb: [
			187,
			55,
			84
		]
	},
	{
		pos: 160,
		rgb: [
			227,
			89,
			51
		]
	},
	{
		pos: 192,
		rgb: [
			249,
			142,
			8
		]
	},
	{
		pos: 224,
		rgb: [
			249,
			201,
			50
		]
	},
	{
		pos: 255,
		rgb: [
			252,
			255,
			164
		]
	}
]);
/**
* Plasma — sequential (yellow→pink→purple→blue).
*/
const PLASMA = buildColormap([
	{
		pos: 1,
		rgb: [
			13,
			8,
			135
		]
	},
	{
		pos: 32,
		rgb: [
			75,
			3,
			161
		]
	},
	{
		pos: 64,
		rgb: [
			125,
			3,
			168
		]
	},
	{
		pos: 96,
		rgb: [
			168,
			34,
			150
		]
	},
	{
		pos: 128,
		rgb: [
			203,
			70,
			121
		]
	},
	{
		pos: 160,
		rgb: [
			229,
			107,
			93
		]
	},
	{
		pos: 192,
		rgb: [
			248,
			148,
			65
		]
	},
	{
		pos: 224,
		rgb: [
			253,
			195,
			40
		]
	},
	{
		pos: 255,
		rgb: [
			240,
			249,
			33
		]
	}
]);
/**
* Coolwarm — diverging (blue→white→red).
*/
const COOLWARM = buildColormap([
	{
		pos: 1,
		rgb: [
			59,
			76,
			192
		]
	},
	{
		pos: 42,
		rgb: [
			98,
			130,
			234
		]
	},
	{
		pos: 85,
		rgb: [
			156,
			179,
			255
		]
	},
	{
		pos: 128,
		rgb: [
			221,
			221,
			221
		]
	},
	{
		pos: 171,
		rgb: [
			247,
			172,
			156
		]
	},
	{
		pos: 213,
		rgb: [
			238,
			108,
			98
		]
	},
	{
		pos: 255,
		rgb: [
			180,
			4,
			38
		]
	}
]);
const COLORMAPS = {
	tab10: TAB10,
	viridis: VIRIDIS,
	magma: MAGMA,
	inferno: INFERNO,
	plasma: PLASMA,
	coolwarm: COOLWARM
};
const COLORMAP_NAMES = [
	"tab10",
	"viridis",
	"magma",
	"inferno",
	"plasma",
	"coolwarm"
];
/**
* Get the Float32Array (1024 floats, 256 RGBA entries) for a colormap.
*/
function getColormapArray(name) {
	return COLORMAPS[name];
}
/**
* Build a sparse indexed colormap from OME-Zarr `region_palette_rgb` metadata.
* Labels wrap into the 256-entry table via `((label - 1) % 255) + 1`; label 0
* stays transparent.
*/
function buildLabelColormap(palette) {
	const data = new Float32Array(1024);
	for (const [label, rgb] of palette) {
		const roundedLabel = Math.round(label);
		if (!Number.isFinite(roundedLabel) || roundedLabel <= 0) continue;
		const index = (roundedLabel - 1) % 255 + 1;
		const base = index * 4;
		data[base] = Math.max(0, Math.min(255, rgb[0])) / 255;
		data[base + 1] = Math.max(0, Math.min(255, rgb[1])) / 255;
		data[base + 2] = Math.max(0, Math.min(255, rgb[2])) / 255;
		data[base + 3] = 1;
	}
	return data;
}
/** Apply independent per-label alpha values without mutating a shared colormap. */
function applyLabelOpacities(colormap, opacities) {
	const result = colormap.slice();
	const labelCount = Math.min(256, opacities.length);
	for (let label = 1; label < labelCount; label++) {
		const opacity = opacities[label];
		if (opacity === void 0 || !Number.isFinite(opacity)) continue;
		const alphaIndex = label * 4 + 3;
		const alpha = result[alphaIndex];
		if (alpha === void 0) continue;
		result[alphaIndex] = alpha * Math.max(0, Math.min(1, opacity));
	}
	return result;
}

//#endregion
//#region src/crc32.ts
/**
* Table-based IEEE 802.3 CRC-32 (same polynomial/parameters as zlib.crc32).
* Returns an unsigned 32-bit integer.
*/
let TABLE = null;
function getTable() {
	if (TABLE) return TABLE;
	const table = new Uint32Array(256);
	for (let n = 0; n < 256; n++) {
		let c = n;
		for (let k = 0; k < 8; k++) c = c & 1 ? 3988292384 ^ c >>> 1 : c >>> 1;
		table[n] = c >>> 0;
	}
	TABLE = table;
	return table;
}
/**
* Compute the CRC-32 of `bytes`. Pass the previous result as `seed` to
* continue a running checksum across multiple buffers.
*/
function crc32(bytes, seed = 0) {
	const table = getTable();
	let crc = (seed ^ 4294967295) >>> 0;
	for (let i = 0; i < bytes.length; i++) crc = table[(crc ^ bytes[i]) & 255] ^ crc >>> 8;
	return (crc ^ 4294967295) >>> 0;
}

//#endregion
//#region src/edit-regions.ts
/**
* Clamp regions to a `width × height` array, snap to whole pixels (outward, so
* a region edge never loses a pixel it covers) and drop empty ones. `null` or
* an empty list means "unrestricted" and returns `undefined`; a non-empty list
* whose regions all fall outside the array returns `[]` (nothing editable).
*/
function normalizeEditRegions(regions, width, height) {
	if (!regions || regions.length === 0) return void 0;
	const out = [];
	for (const region of regions) {
		const left = Math.max(0, Math.floor(Math.min(region.left, region.right)));
		const right = Math.min(width, Math.ceil(Math.max(region.left, region.right)));
		const top = Math.max(0, Math.floor(Math.min(region.top, region.bottom)));
		const bottom = Math.min(height, Math.ceil(Math.max(region.top, region.bottom)));
		if (right > left && bottom > top) out.push({
			left,
			top,
			right,
			bottom
		});
	}
	return out;
}
function pointInAnyRegion(regions, x, y) {
	for (const region of regions) if (x >= region.left && x < region.right && y >= region.top && y < region.bottom) return true;
	return false;
}
function rectIntersectsAnyRegion(regions, rect) {
	for (const region of regions) if (rect.left < region.right && region.left < rect.right && rect.top < region.bottom && region.top < rect.bottom) return true;
	return false;
}
/** Smallest rectangle containing every region, or null for an empty list. */
function unionRegionBounds(regions) {
	let out = null;
	for (const region of regions) out = out ? {
		left: Math.min(out.left, region.left),
		top: Math.min(out.top, region.top),
		right: Math.max(out.right, region.right),
		bottom: Math.max(out.bottom, region.bottom)
	} : { ...region };
	return out;
}
/**
* Allowed spans of row `y` (array coordinates) restricted to the local
* inclusive range `[xStart, xEnd]` of a chunk whose left edge is `originX`.
* Returned spans are chunk-local, sorted and merged, so they never overlap.
*/
function regionSpansForRow(regions, y, originX, xStart, xEnd) {
	const spans = [];
	for (const region of regions) {
		if (y < region.top || y >= region.bottom) continue;
		const x0 = Math.max(xStart, region.left - originX);
		const x1 = Math.min(xEnd, region.right - 1 - originX);
		if (x0 <= x1) spans.push([x0, x1]);
	}
	if (spans.length <= 1) return spans;
	spans.sort((a, b) => a[0] - b[0]);
	const merged = [spans[0]];
	for (let i = 1; i < spans.length; i++) {
		const span = spans[i];
		const last = merged[merged.length - 1];
		if (span[0] <= last[1] + 1) last[1] = Math.max(last[1], span[1]);
		else merged.push(span);
	}
	return merged;
}
/**
* Per-pixel "inside a region" mask for a `width × height` chunk whose top-left
* pixel is `(originX, originY)` in array coordinates.
*/
function buildRegionMask(regions, originX, originY, width, height) {
	const mask = new Uint8Array(width * height);
	for (let y = 0; y < height; y++) {
		const spans = regionSpansForRow(regions, originY + y, originX, 0, width - 1);
		for (const [x0, x1] of spans) mask.fill(1, y * width + x0, y * width + x1 + 1);
	}
	return mask;
}
/**
* Rectangles covering `outer` minus the union of `holes`: the area to dim so
* only the regions stay bright. Exact for overlapping holes (a scanline over
* the holes' vertical edges), and only ever needs solid rectangles to draw.
*/
function computeDimRects(outer, holes) {
	const clipped = [];
	for (const hole of holes) {
		const left = Math.max(outer.left, hole.left);
		const right = Math.min(outer.right, hole.right);
		const top = Math.max(outer.top, hole.top);
		const bottom = Math.min(outer.bottom, hole.bottom);
		if (right > left && bottom > top) clipped.push({
			left,
			top,
			right,
			bottom
		});
	}
	if (outer.right <= outer.left || outer.bottom <= outer.top) return [];
	if (clipped.length === 0) return [{
		x: outer.left,
		y: outer.top,
		width: outer.right - outer.left,
		height: outer.bottom - outer.top
	}];
	const edges = new Set([outer.top, outer.bottom]);
	for (const hole of clipped) {
		edges.add(hole.top);
		edges.add(hole.bottom);
	}
	const ys = [...edges].sort((a, b) => a - b);
	const out = [];
	for (let i = 0; i + 1 < ys.length; i++) {
		const y0 = ys[i];
		const y1 = ys[i + 1];
		if (y1 <= y0) continue;
		const covering = [];
		for (const hole of clipped) if (hole.top <= y0 && hole.bottom >= y1) covering.push([hole.left, hole.right]);
		covering.sort((a, b) => a[0] - b[0]);
		let cursor = outer.left;
		for (const [left, right] of covering) {
			if (left > cursor) out.push({
				x: cursor,
				y: y0,
				width: left - cursor,
				height: y1 - y0
			});
			cursor = Math.max(cursor, right);
		}
		if (cursor < outer.right) out.push({
			x: cursor,
			y: y0,
			width: outer.right - cursor,
			height: y1 - y0
		});
	}
	return out;
}

//#endregion
//#region src/events.ts
var EventEmitter = class {
	constructor() {
		this.handlers = new Map();
	}
	on(event, handler) {
		if (!this.handlers.has(event)) this.handlers.set(event, new Set());
		this.handlers.get(event)?.add(handler);
	}
	off(event, handler) {
		this.handlers.get(event)?.delete(handler);
	}
	emit(event, ...args) {
		const eventHandlers = this.handlers.get(event);
		if (eventHandlers) for (const handler of eventHandlers) if (args.length === 0) handler();
		else handler(args[0]);
	}
	removeAllListeners() {
		this.handlers.clear();
	}
};

//#endregion
//#region src/label-aliases.ts
const EMPTY_ALIASES = new Map();
/**
* Sanitize a resolver result: drop self-mappings, non-integers, and chains
* (an alias whose target is itself an alias is resolved to the final target).
*/
function normalizeLabelAliases(aliases) {
	if (!aliases || aliases.size === 0) return EMPTY_ALIASES;
	const out = new Map();
	for (const [rawFrom, rawTo] of aliases) {
		if (!(Number.isInteger(rawFrom) && Number.isInteger(rawTo)) || rawFrom < 0 || rawTo < 0) continue;
		let target = rawTo;
		for (let hops = 0; hops < 8; hops++) {
			const next = aliases.get(target);
			if (next === void 0 || next === target) break;
			target = next;
		}
		if (target === rawFrom) continue;
		out.set(rawFrom, target);
	}
	return out.size === 0 ? EMPTY_ALIASES : out;
}
/** Canonical class for a raw label value. */
function canonicalLabelValue(aliases, value) {
	if (!aliases || aliases.size === 0) return value;
	return aliases.get(value) ?? value;
}
/** All raw values (including `canonical` itself) that resolve to `canonical`, ascending. */
function labelValuesForCanonical(aliases, canonical) {
	const values = [canonical];
	if (aliases) {
		for (const [from, to] of aliases) if (to === canonical && from !== canonical) values.push(from);
	}
	return values.sort((a, b) => a - b);
}
/**
* Region metadata with alias ids given their canonical class's name and colour,
* so a palette colormap built from it renders aliases like their base class.
* Names / colours the raw metadata declares for an alias id are replaced.
*/
function collapseRegionMetadata(regions, aliases) {
	if (aliases.size === 0) return regions;
	const names = new Map(regions.names);
	const palette = new Map(regions.palette);
	for (const [from, to] of aliases) {
		const name = regions.names.get(to);
		if (name !== void 0) names.set(from, name);
		else names.delete(from);
		const rgb = regions.palette.get(to);
		if (rgb) palette.set(from, [
			rgb[0],
			rgb[1],
			rgb[2]
		]);
		else palette.delete(from);
	}
	return {
		...regions,
		names,
		palette
	};
}
/**
* Dense lookup table (`lut[raw] = canonical`) for per-pixel hot loops. `size`
* is the dtype range (256 for uint8, 65536 for uint16). Null when there are
* no aliases so callers can skip the indirection entirely.
*/
function buildLabelAliasLut(aliases, size) {
	if (!aliases || aliases.size === 0) return null;
	const lut = new Uint16Array(size);
	for (let i = 0; i < size; i++) lut[i] = i;
	for (const [from, to] of aliases) if (from >= 0 && from < size && to >= 0 && to < size) lut[from] = to;
	return lut;
}

//#endregion
//#region src/label-raster-diff.ts
/**
* Clipped extent of chunk `[cy, cx]` in array coordinates. Edge chunks are
* truncated to the array shape.
*/
function clippedChunkExtent(shape, chunkShape, cy, cx) {
	const y0 = cy * chunkShape[0];
	const x0 = cx * chunkShape[1];
	return {
		y0,
		x0,
		width: Math.max(0, Math.min(chunkShape[1], shape[1] - x0)),
		height: Math.max(0, Math.min(chunkShape[0], shape[0] - y0))
	};
}
/**
* Compute the sparse runs turning `original` into `current`. Adjacent changed
* pixels with the same new value merge into one run (runs may cross row
* boundaries — offsets are flat). Returns null when nothing changed.
*/
function computeChunkRuns(original, current, width) {
	const n = Math.min(original.length, current.length);
	const triples = [];
	let changedPixels = 0;
	let minY = Number.POSITIVE_INFINITY;
	let minX = Number.POSITIVE_INFINITY;
	let maxY = -1;
	let maxX = -1;
	const safeWidth = Math.max(1, width);
	let i = 0;
	while (i < n) {
		if (original[i] === current[i]) {
			i++;
			continue;
		}
		const value = current[i];
		const start = i;
		while (i < n && original[i] !== current[i] && current[i] === value) {
			const y = Math.floor(i / safeWidth);
			const x = i - y * safeWidth;
			if (y < minY) minY = y;
			if (y > maxY) maxY = y;
			if (x < minX) minX = x;
			if (x > maxX) maxX = x;
			i++;
		}
		const length = i - start;
		triples.push(start, length, value);
		changedPixels += length;
	}
	if (triples.length === 0) return null;
	return {
		runs: Int32Array.from(triples),
		changedPixels,
		bbox: [
			minY,
			minX,
			maxY + 1,
			maxX + 1
		]
	};
}
/** Apply runs in place: `target[offset .. offset+length) = value`. */
function applyChunkRuns(target, runs) {
	if (!runs) return;
	for (let i = 0; i + 2 < runs.length; i += 3) {
		const offset = runs[i];
		const length = runs[i + 1];
		const value = runs[i + 2];
		const end = Math.min(target.length, offset + length);
		if (offset < 0 || offset >= end) continue;
		target.fill(value, offset, end);
	}
}
const IS_LITTLE_ENDIAN = new Uint8Array(new Uint16Array([1]).buffer)[0] === 1;
/** Little-endian native-dtype bytes of a label array (a view when possible). */
function labelArrayToLEBytes(data) {
	if (data instanceof Uint8Array) return data;
	if (IS_LITTLE_ENDIAN) return new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
	const out = new Uint8Array(data.length * 2);
	for (let i = 0; i < data.length; i++) {
		const v = data[i];
		out[i * 2] = v & 255;
		out[i * 2 + 1] = v >>> 8 & 255;
	}
	return out;
}
/** CRC-32 over the little-endian native-dtype bytes of a clipped chunk block. */
function chunkCrc32(data) {
	return crc32(labelArrayToLEBytes(data));
}
function runsEqual(a, b) {
	if (!(a || b)) return true;
	if (!(a && b)) return false;
	if (a.length !== b.length) return false;
	for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
	return true;
}
function chunkKey(cy, cx) {
	return `${cy},${cx}`;
}
/** Keys (`"cy,cx"`) of chunks whose runs differ between two diffs. */
function diffChunkKeys(prev, next) {
	const prevMap = new Map();
	const nextMap = new Map();
	for (const chunk of prev?.chunks ?? []) prevMap.set(chunkKey(chunk.index[0], chunk.index[1]), chunk.runs);
	for (const chunk of next?.chunks ?? []) nextMap.set(chunkKey(chunk.index[0], chunk.index[1]), chunk.runs);
	const keys = new Set();
	for (const [key, runs] of prevMap) if (!runsEqual(runs, nextMap.get(key) ?? null)) keys.add(key);
	for (const [key, runs] of nextMap) if (!runsEqual(runs, prevMap.get(key) ?? null)) keys.add(key);
	return [...keys].sort();
}
/** Sorted unique values written by all runs in a diff. */
function collectClassesUsed(chunks) {
	const values = new Set();
	for (const chunk of chunks) for (let i = 2; i < chunk.runs.length; i += 3) values.add(chunk.runs[i]);
	return [...values].sort((a, b) => a - b);
}
/** Allocate a label array of the right dtype. */
function createLabelArray(dtype, length) {
	return dtype === "uint16" ? new Uint16Array(length) : new Uint8Array(length);
}

//#endregion
//#region src/ome-zarr.ts
const FALLBACK_COLORS = [
	"FF0000",
	"00FF00",
	"0000FF",
	"FFFF00",
	"00FFFF",
	"FF00FF"
];
function clamp$2(value, min, max) {
	return Math.max(min, Math.min(max, value));
}
function clampByte(value) {
	const n = Number(value);
	if (!Number.isFinite(n)) return 0;
	return Math.max(0, Math.min(255, Math.round(n)));
}
function normalizeColor(value, index) {
	if (typeof value === "string" && /^[0-9a-fA-F]{6}$/.test(value)) return value.toUpperCase();
	return FALLBACK_COLORS[index % FALLBACK_COLORS.length] ?? "FFFFFF";
}
function normalizeWindow(value) {
	if (value === null || value === void 0) return null;
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : null;
}
/**
* Parse virdx region metadata (`virdx_attrs.misc.region_ids` /
* `region_classes` / `region_palette_rgb`) from a Zarr node's attributes into
* lookup maps for label naming + coloring. Returns null when absent/invalid.
*/
function parseRegionMetadata(attrs) {
	if (!attrs) return null;
	const virdx = attrs.virdx_attrs;
	const misc = virdx?.misc;
	if (!misc) return null;
	const ids = misc.region_ids;
	const classes = misc.region_classes;
	const palette = misc.region_palette_rgb;
	if (!Array.isArray(ids)) return null;
	const names = new Map();
	const paletteMap = new Map();
	for (let i = 0; i < ids.length; i++) {
		const id = Number(ids[i]);
		if (!Number.isFinite(id)) continue;
		if (Array.isArray(classes)) {
			const name = classes[i];
			if (typeof name === "string" && name.length > 0) names.set(id, name);
		}
		if (Array.isArray(palette)) {
			const rgb = palette[i];
			if (Array.isArray(rgb) && rgb.length >= 3) paletteMap.set(id, [
				clampByte(rgb[0]),
				clampByte(rgb[1]),
				clampByte(rgb[2])
			]);
		}
	}
	if (names.size === 0 && paletteMap.size === 0) return null;
	const unknownRaw = Number(misc.unknown_id);
	return {
		names,
		palette: paletteMap,
		unknownId: Number.isInteger(unknownRaw) ? unknownRaw : null,
		taxonomy: typeof misc.taxonomy === "string" ? misc.taxonomy : null,
		pipelineVersion: typeof misc.pipeline_version === "string" ? misc.pipeline_version : null
	};
}
/**
* Legend name for the declared `unknown_id` when the palette does not name it.
* Pixels with this value were removed by an annotator (or never labelled) and
* must not be confused with background (0); they render transparent.
*/
const UNANNOTATED_LABEL_NAME = "unannotated";
/**
* Legend name for label 0 when the palette does not name it. Background is a
* real class (true slide background, distinct from "unannotated") that label
* rasters always list and that annotators may paint; it renders transparent.
*/
const BACKGROUND_LABEL_NAME = "background";
/**
* Class name for a label value, falling back to {@link BACKGROUND_LABEL_NAME}
* for 0 and {@link UNANNOTATED_LABEL_NAME} for the declared unknown id (which
* production palettes leave unnamed).
*/
function getRegionLabelName(regions, value) {
	if (!regions) return void 0;
	const name = regions.names.get(value);
	if (name !== void 0) return name;
	if (value === 0) return BACKGROUND_LABEL_NAME;
	return regions.unknownId !== null && value === regions.unknownId ? UNANNOTATED_LABEL_NAME : void 0;
}
/** Convert a physical length in the given (UDUNITS-ish) unit to micrometers. */
function lengthToMicrons(value, unit) {
	const u = (unit ?? "").toString().trim().toLowerCase();
	switch (u) {
		case "nm":
		case "nanometer":
		case "nanometre": return value / 1e3;
		case "µm":
		case "um":
		case "micron":
		case "micrometer":
		case "micrometre": return value;
		case "mm":
		case "millimeter":
		case "millimetre": return value * 1e3;
		case "cm":
		case "centimeter":
		case "centimetre": return value * 1e4;
		case "m":
		case "meter":
		case "metre": return value * 1e6;
		default: return value;
	}
}
function getAxisUnit(firstMultiscale, axisIndex) {
	const axesArr = firstMultiscale?.axes;
	if (!Array.isArray(axesArr)) return null;
	const axis = axesArr[axisIndex];
	return typeof axis?.unit === "string" ? axis.unit : null;
}
/**
* Extract full-resolution microns-per-pixel (along x) from an OME-Zarr
* multiscale's finest dataset `coordinateTransformations` scale, converting via
* the axis unit. Returns undefined when no usable scale transform is present.
*/
function parseMicronsPerPixel(firstMultiscale, datasets, axes) {
	const dataset0 = datasets[0];
	const msTransforms = firstMultiscale?.coordinateTransformations;
	const transforms = [...Array.isArray(dataset0?.coordinateTransformations) ? dataset0.coordinateTransformations : [], ...Array.isArray(msTransforms) ? msTransforms : []];
	let scale;
	for (const transform of transforms) if (transform && typeof transform === "object" && transform.type === "scale" && Array.isArray(transform.scale)) {
		scale = transform.scale;
		break;
	}
	if (!Array.isArray(scale)) return;
	const raw = Number(scale[axes.x]);
	if (!Number.isFinite(raw) || raw <= 0) return;
	const microns = lengthToMicrons(raw, getAxisUnit(firstMultiscale, axes.x));
	return Number.isFinite(microns) && microns > 0 ? microns : void 0;
}
function getDataTypeDefaultWindow(dtype) {
	switch (dtype) {
		case "uint8": return {
			start: 0,
			end: 255
		};
		case "int8": return {
			start: -128,
			end: 127
		};
		case "uint16": return {
			start: 0,
			end: 65535
		};
		case "int16": return {
			start: -32768,
			end: 32767
		};
		case "uint32": return {
			start: 0,
			end: 4294967295
		};
		case "int32": return {
			start: -2147483648,
			end: 2147483647
		};
		case "float32":
		case "float64": return {
			start: 0,
			end: 1
		};
		case "bool": return {
			start: 0,
			end: 1
		};
		default: return {
			start: 0,
			end: 255
		};
	}
}
/**
* Validate OME-Zarr multiscale axes. Accepts 2-4 axes drawn from `c`, `z`,
* `y`, `x` (unique); `y` and `x` are required. Missing axes are -1.
*/
function validateAxes(axesValue) {
	if (!Array.isArray(axesValue)) throw new Error("OME-Zarr multiscales[0].axes must be an array");
	const names = axesValue.map((axis) => {
		if (!axis || typeof axis !== "object" || typeof axis.name !== "string") throw new Error("OME-Zarr axis entries must include a string 'name'");
		return axis.name;
	});
	if (names.length < 2 || names.length > 4) throw new Error("Unsupported OME-Zarr axes: expected y,x with optional c and/or z axes");
	const c = names.indexOf("c");
	const z = names.indexOf("z");
	const y = names.indexOf("y");
	const x = names.indexOf("x");
	if (y === -1 || x === -1) throw new Error("Unsupported OME-Zarr axes: must include 'y' and 'x'");
	if (new Set(names).size !== names.length) throw new Error("Unsupported OME-Zarr axes: axis names must be unique");
	if (names.some((name) => ![
		"c",
		"z",
		"y",
		"x"
	].includes(name))) throw new Error("Unsupported OME-Zarr axes: only 'c', 'z', 'y', and 'x' are supported");
	return {
		c,
		z,
		y,
		x
	};
}
function normalizeChannels(rawChannels, channelCount, defaultWindow) {
	const source = Array.isArray(rawChannels) ? rawChannels : [];
	const channels = [];
	for (let index = 0; index < channelCount; index++) {
		const raw = source[index] ?? {};
		const start = normalizeWindow(raw.window?.start) ?? defaultWindow.start;
		const end = normalizeWindow(raw.window?.end) ?? defaultWindow.end;
		channels.push({
			index,
			label: typeof raw.label === "string" && raw.label.length > 0 ? raw.label : `Channel ${index + 1}`,
			color: normalizeColor(raw.color, index),
			active: typeof raw.active === "boolean" ? raw.active : index < 3,
			windowStart: start,
			windowEnd: end !== start ? end : defaultWindow.end
		});
	}
	return channels;
}
function toNumber(value) {
	if (typeof value === "number") return value;
	if (typeof value === "bigint") return Number(value);
	return value ? 1 : 0;
}
function parseHexColor(color) {
	const normalized = normalizeColor(color, 0);
	return [
		parseInt(normalized.slice(0, 2), 16),
		parseInt(normalized.slice(2, 4), 16),
		parseInt(normalized.slice(4, 6), 16)
	];
}
function compositeChannelsToRgba(width, height, selectedChannels, channelData, channelInfo) {
	const pixelCount = width * height;
	const rgba = new Uint8ClampedArray(pixelCount * 4);
	const selectedInfo = selectedChannels.map((index) => channelInfo[index]).filter((channel) => Boolean(channel));
	for (let i = 0; i < pixelCount; i++) {
		let r = 0;
		let g = 0;
		let b = 0;
		for (const channel of selectedInfo) {
			const values = channelData.get(channel.index);
			if (!values) continue;
			const value = toNumber(values[i] ?? 0);
			const windowDelta = channel.windowEnd - channel.windowStart;
			const normalized = windowDelta !== 0 ? clamp$2((value - channel.windowStart) / windowDelta, 0, 1) : 0;
			const [cr, cg, cb] = parseHexColor(channel.color);
			r += normalized * cr;
			g += normalized * cg;
			b += normalized * cb;
		}
		const offset = i * 4;
		rgba[offset] = clamp$2(Math.round(r), 0, 255);
		rgba[offset + 1] = clamp$2(Math.round(g), 0, 255);
		rgba[offset + 2] = clamp$2(Math.round(b), 0, 255);
		rgba[offset + 3] = 255;
	}
	return rgba;
}
function resolveSelectedChannels(requested, channels) {
	if (requested && requested.length > 0) {
		const deduped = [...new Set(requested.filter((index) => Number.isInteger(index)))];
		const filtered = deduped.filter((index) => index >= 0 && index < channels.length);
		if (filtered.length > 0) return filtered;
	}
	const active = channels.filter((channel) => channel.active).map((channel) => channel.index);
	if (active.length > 0) return active;
	return channels.length > 0 ? [0] : [];
}
/** Wrap a label value into the 1..255 colormap index space (0 stays 0). */
function labelToColormapIndex(value) {
	if (value <= 0) return 0;
	return (Math.round(value) - 1) % 255 + 1;
}
/**
* Convert decoded label values to single-channel palette indices (0 = transparent
* background, otherwise `((v - 1) % 255) + 1`). Suitable for R8 texture upload.
*/
function labelsToIndexedBytes(data, out) {
	const result = out && out.length >= data.length ? out : new Uint8Array(data.length);
	for (let i = 0; i < data.length; i++) result[i] = labelToColormapIndex(toNumber(data[i] ?? 0));
	return result;
}
/**
* Render an integer label mask (e.g. a segmentation array) to RGBA. Label 0 is
* treated as background (transparent). With a palette, labels take their palette
* color (labels absent from the palette stay transparent); otherwise non-zero
* labels cycle through tab10. When `indexed` is true, RGB stores the wrapped
* label index so the overlay colormap shader can apply any colormap.
*/
function labelToRgba(width, height, data, palette, indexed = false) {
	const pixelCount = width * height;
	const rgba = new Uint8ClampedArray(pixelCount * 4);
	const cmap = getColormapArray("tab10");
	for (let i = 0; i < pixelCount; i++) {
		const value = toNumber(data[i] ?? 0);
		if (value <= 0) continue;
		const offset = i * 4;
		const idx = labelToColormapIndex(value);
		if (indexed) {
			rgba[offset] = idx;
			rgba[offset + 1] = idx;
			rgba[offset + 2] = idx;
			rgba[offset + 3] = 255;
			continue;
		}
		if (palette) {
			const rgb = palette.get(Math.round(value));
			if (!rgb) continue;
			rgba[offset] = rgb[0];
			rgba[offset + 1] = rgb[1];
			rgba[offset + 2] = rgb[2];
			rgba[offset + 3] = 255;
			continue;
		}
		const base = idx * 4;
		rgba[offset] = cmap[base] * 255;
		rgba[offset + 1] = cmap[base + 1] * 255;
		rgba[offset + 2] = cmap[base + 2] * 255;
		rgba[offset + 3] = 255;
	}
	return rgba;
}
/**
* Decide how a Zarr dataset should be rendered and build its channel metadata.
* - No channel axis + integer dtype -> categorical label overlay.
* - No channel axis + float dtype   -> single grayscale channel.
* - Channel axis present            -> multi-channel composite.
*/
function buildZarrChannels(dtype, hasChannelAxis, channelCount, omeroChannels) {
	const defaultWindow = getDataTypeDefaultWindow(dtype);
	if (!hasChannelAxis) {
		const isInteger = !/^float/.test(dtype);
		if (isInteger) return {
			renderMode: "label",
			channels: [{
				index: 0,
				label: "Labels",
				color: "FFFFFF",
				active: true,
				windowStart: 0,
				windowEnd: defaultWindow.end
			}]
		};
		return {
			renderMode: "composite",
			channels: normalizeChannels(null, 1, defaultWindow)
		};
	}
	return {
		renderMode: "composite",
		channels: normalizeChannels(omeroChannels, channelCount, defaultWindow)
	};
}
/**
* Normalize a window of decoded Zarr data to row-major y,x order (y outer, x
* inner). When the array's x axis precedes its y axis (e.g. axes `x,y`), the
* sliced data comes back with x as the outer dimension and must be transposed.
*/
function toRowMajorYX(data, width, height, xBeforeY) {
	if (!xBeforeY) return data;
	const Ctor = data.constructor;
	const out = new Ctor(width * height);
	for (let xi = 0; xi < width; xi++) {
		const base = xi * height;
		for (let yi = 0; yi < height; yi++) out[yi * width + xi] = data[base + yi];
	}
	return out;
}

//#endregion
//#region src/label-raster-editor.ts
/** The brush / eraser / picker underlying a tool (fill variants collapse onto their base). */
function labelRasterToolBase(tool) {
	return tool === "fill" ? "brush" : tool === "erase_fill" ? "erase" : tool;
}
/** Whether the Fill modifier is on for a tool. */
function isLabelRasterFillTool(tool) {
	return tool === "fill" || tool === "erase_fill";
}
/**
* Combine a base tool with the Fill modifier. The picker has no fill variant:
* turning fill on from pick yields the class fill.
*/
function withLabelRasterFillMode(base, fill) {
	if (!fill) return base;
	return base === "erase" ? "erase_fill" : "fill";
}
/**
* Resolve the eraser value and brush overwritable set from an (optional) explicit
* tool state and the zarr's declared `unknown_id`. The brush may always repaint
* background and whatever the eraser writes.
*/
function resolveLabelRasterEditRules(state, declaredUnknownId) {
	const unannotatedId = Math.max(0, Math.round(state?.unannotatedId ?? declaredUnknownId ?? 0));
	const overwritableIds = state?.overwritableIds ? [...state.overwritableIds] : unannotatedId === 0 ? [0] : [0, unannotatedId];
	return {
		unannotatedId,
		overwritableIds
	};
}
/**
* Default fill budget: none. A host may cap fills via `maxFillPixels`
* ({@link LabelRasterEditorOptions} / `LabelRasterViewerConfig`), in which case
* an oversized region is reverted and reported as `"too-large"`.
*/
const DEFAULT_MAX_FILL_PIXELS = Number.POSITIVE_INFINITY;
const DEFAULT_MAX_RESIDENT_CHUNKS = 32;
function yieldToEventLoop() {
	return new Promise((resolve) => setTimeout(resolve, 0));
}
function inChunkBounds(bounds, cy, cx) {
	return cy >= bounds.cy0 && cy <= bounds.cy1 && cx >= bounds.cx0 && cx <= bounds.cx1;
}
function unionRect(a, b) {
	if (!a) return [
		b[0],
		b[1],
		b[2],
		b[3]
	];
	return [
		Math.min(a[0], b[0]),
		Math.min(a[1], b[1]),
		Math.max(a[2], b[2]),
		Math.max(a[3], b[3])
	];
}
var LabelRasterEditSession = class {
	constructor(source, options = {}) {
		this.info = void 0;
		this.source = void 0;
		this.maxResidentChunks = void 0;
		this.maxFillPixels = void 0;
		this.now = void 0;
		this.gridRows = void 0;
		this.gridCols = void 0;
		this.chunks = new Map();
		this.stroke = null;
		this.fillState = null;
		this.visibleChunks = new Set();
		this.fillTask = null;
		this.touchCounter = 0;
		this.crcMismatches = new Map();
		this.editedAt = null;
		this.disposed = false;
		this.labelAliases = normalizeLabelAliases(null);
		this.labelAliasLut = null;
		this.source = source;
		this.info = source.info;
		this.maxResidentChunks = Math.max(1, options.maxResidentChunks ?? DEFAULT_MAX_RESIDENT_CHUNKS);
		this.maxFillPixels = Math.max(1, Math.round(options.maxFillPixels ?? DEFAULT_MAX_FILL_PIXELS));
		this.now = options.now ?? (() => Date.now());
		this.gridRows = Math.ceil(this.info.shape[0] / this.info.chunkShape[0]);
		this.gridCols = Math.ceil(this.info.shape[1] / this.info.chunkShape[1]);
	}
	inGrid(cy, cx) {
		return cy >= 0 && cx >= 0 && cy < this.gridRows && cx < this.gridCols;
	}
	getOrCreateChunk(cy, cx) {
		const key = chunkKey(cy, cx);
		let state = this.chunks.get(key);
		if (!state) {
			const extent = clippedChunkExtent(this.info.shape, this.info.chunkShape, cy, cx);
			state = {
				cy,
				cx,
				width: extent.width,
				height: extent.height,
				runs: null,
				changedPixels: 0,
				bbox: null,
				crc: null,
				expectedCrc: null,
				buffers: null,
				textureDirty: false,
				dirtyRect: null,
				pending: [],
				loading: null,
				lastTouched: ++this.touchCounter
			};
			this.chunks.set(key, state);
		}
		return state;
	}
	touch(state) {
		state.lastTouched = ++this.touchCounter;
	}
	markDirtyFull(state) {
		state.textureDirty = true;
		state.dirtyRect = null;
	}
	markDirtyRect(state, rect) {
		if (state.textureDirty && state.dirtyRect === null) return;
		state.textureDirty = true;
		state.dirtyRect = unionRect(state.dirtyRect, rect);
	}
	dropEmptyState(state) {
		if (state.runs === null && state.buffers === null && state.pending.length === 0 && state.loading === null) this.chunks.delete(chunkKey(state.cy, state.cx));
	}
	/** Whether the renderer should draw this chunk from the edit session. */
	getChunkRenderState(cy, cx) {
		const state = this.chunks.get(chunkKey(cy, cx));
		if (!state) return "none";
		if (state.buffers) return "resident";
		return state.runs ? "needsLoad" : "none";
	}
	/** Grid indices `[cy, cx]` of chunks the session currently tracks (edited or resident). */
	getTrackedChunks() {
		return [...this.chunks.values()].filter((s) => s.runs || s.buffers).map((s) => [s.cy, s.cx]);
	}
	/** Decode (or re-materialize) the chunk buffers. Resolves when resident. */
	ensureResident(cy, cx) {
		if (this.disposed || !this.inGrid(cy, cx)) return Promise.resolve();
		const state = this.getOrCreateChunk(cy, cx);
		this.touch(state);
		if (state.buffers) return Promise.resolve();
		if (state.loading) return state.loading;
		const task = this.source.readChunk(cy, cx).then((chunk) => {
			if (this.disposed) return;
			const length = state.width * state.height;
			let original;
			if (chunk && chunk.data.length === length) original = chunk.data.slice();
			else {
				original = createLabelArray(this.info.dtype, length);
				if (this.info.fillValue !== 0) original.fill(this.info.fillValue);
			}
			const crc = chunkCrc32(original);
			state.crc = crc;
			if (state.expectedCrc !== null && state.expectedCrc !== crc) this.crcMismatches.set(chunkKey(cy, cx), [cy, cx]);
			const current = original.slice();
			applyChunkRuns(current, state.runs);
			state.buffers = {
				original,
				current
			};
			const pending = state.pending;
			state.pending = [];
			for (const op of pending) this.applyStamp(state, op);
			this.markDirtyFull(state);
			this.touch(state);
			this.evictBuffersIfNeeded(state);
		}).catch(() => {
			state.pending = [];
		}).finally(() => {
			state.loading = null;
			this.dropEmptyState(state);
		});
		state.loading = task;
		return task;
	}
	/**
	* Chunks currently on screen. The renderer calls this every frame it draws
	* from the session; eviction never drops a visible chunk, otherwise a screen
	* with more edited chunks than `maxResidentChunks` would evict / reload them
	* forever (each reload evicting the next visible chunk). Memory therefore
	* scales with the visible working set instead of a fixed cap.
	*/
	setVisibleChunks(chunks) {
		const next = new Set();
		for (const [cy, cx] of chunks) next.add(chunkKey(cy, cx));
		this.visibleChunks = next;
	}
	/** Evict least-recently-touched buffers beyond `maxResidentChunks` (visible / in-stroke chunks are kept). */
	evictBuffersIfNeeded(keep) {
		const resident = [...this.chunks.values()].filter((s) => s.buffers !== null);
		if (resident.length <= this.maxResidentChunks) return;
		resident.sort((a, b) => a.lastTouched - b.lastTouched);
		let excess = resident.length - this.maxResidentChunks;
		for (const state of resident) {
			if (excess <= 0) break;
			if (state === keep) continue;
			const key = chunkKey(state.cy, state.cx);
			if (this.visibleChunks.has(key)) continue;
			if (this.stroke?.touched.has(key) || this.fillState?.touched.has(key)) continue;
			state.buffers = null;
			state.textureDirty = false;
			state.dirtyRect = null;
			excess--;
			this.dropEmptyState(state);
		}
	}
	/** Kick decoding for chunks under/around a disc so the first stroke there is instant. */
	prefetchAround(x, y, radiusOverlayPx) {
		if (this.disposed) return;
		const r = Math.max(1, radiusOverlayPx);
		const [ch, cw] = this.info.chunkShape;
		const cy0 = Math.max(0, Math.floor((y - r) / ch));
		const cy1 = Math.min(this.gridRows - 1, Math.floor((y + r) / ch));
		const cx0 = Math.max(0, Math.floor((x - r) / cw));
		const cx1 = Math.min(this.gridCols - 1, Math.floor((x + r) / cw));
		for (let cy = cy0; cy <= cy1; cy++) for (let cx = cx0; cx <= cx1; cx++) {
			const state = this.chunks.get(chunkKey(cy, cx));
			if (state?.buffers || state?.loading) continue;
			this.ensureResident(cy, cx);
		}
	}
	/**
	* Raw → canonical class mapping used by the brush / eraser comparisons (see
	* the module doc). Pass null to clear. Takes effect for the next stroke.
	*/
	setLabelAliases(aliases) {
		this.labelAliases = normalizeLabelAliases(aliases);
		this.labelAliasLut = buildLabelAliasLut(this.labelAliases, this.info.dtype === "uint16" ? 65536 : 256);
	}
	getLabelAliases() {
		return this.labelAliases;
	}
	/** The zarr's declared `unknown_id`, when any (from `virdx_attrs.misc`). */
	getDeclaredUnknownId() {
		return this.info.attrs.unknownId ?? this.info.regions?.unknownId ?? null;
	}
	/** Effective eraser value + brush overwritable set for `state` (defaults from the source metadata). */
	getEditRules(state) {
		return resolveLabelRasterEditRules(state, this.getDeclaredUnknownId());
	}
	/**
	* Start a stroke. `overwritableIds` / `unannotatedId` default to the rules from
	* {@link getEditRules}; both are clamped to the dtype range. `overwrite`
	* switches the stroke to overwrite mode (see the module doc).
	*/
	beginStroke(tool, classId, radiusOverlayPx, options = {}) {
		if (this.disposed) return;
		const maxValue = this.info.dtype === "uint16" ? 65535 : 255;
		const rules = this.getEditRules(options);
		this.stroke = {
			tool,
			value: Math.max(0, Math.min(maxValue, Math.round(classId))),
			radius: Math.max(.5, radiusOverlayPx),
			overwritable: new Set(rules.overwritableIds),
			unannotated: Math.min(maxValue, rules.unannotatedId),
			overwrite: options.overwrite ?? false,
			regions: this.normalizeEditRegions(options.regions),
			last: null,
			touched: new Set(),
			before: new Map()
		};
	}
	isStroking() {
		return this.stroke !== null;
	}
	/** Extend the stroke to overlay pixel coordinates `(x, y)`, stamping discs along the segment. */
	strokeTo(x, y) {
		const stroke = this.stroke;
		if (!stroke || this.disposed) return;
		if (!stroke.last) {
			this.stamp(x, y);
			stroke.last = {
				x,
				y
			};
			return;
		}
		const dx = x - stroke.last.x;
		const dy = y - stroke.last.y;
		const distance = Math.hypot(dx, dy);
		const step = Math.max(1, stroke.radius / 2);
		if (distance <= step) this.stamp(x, y);
		else {
			const steps = Math.ceil(distance / step);
			for (let i = 1; i <= steps; i++) {
				const t = i / steps;
				this.stamp(stroke.last.x + dx * t, stroke.last.y + dy * t);
			}
		}
		stroke.last = {
			x,
			y
		};
	}
	stamp(x, y) {
		const stroke = this.stroke;
		if (!stroke) return;
		const r = stroke.radius;
		const [ch, cw] = this.info.chunkShape;
		const cy0 = Math.max(0, Math.floor((y - r) / ch));
		const cy1 = Math.min(this.gridRows - 1, Math.floor((y + r) / ch));
		const cx0 = Math.max(0, Math.floor((x - r) / cw));
		const cx1 = Math.min(this.gridCols - 1, Math.floor((x + r) / cw));
		const regions = stroke.regions;
		if (regions && !rectIntersectsAnyRegion(regions, {
			left: x - r,
			top: y - r,
			right: x + r + 1,
			bottom: y + r + 1
		})) return;
		for (let cy = cy0; cy <= cy1; cy++) for (let cx = cx0; cx <= cx1; cx++) {
			if (regions && !rectIntersectsAnyRegion(regions, {
				left: cx * cw,
				top: cy * ch,
				right: (cx + 1) * cw,
				bottom: (cy + 1) * ch
			})) continue;
			const state = this.getOrCreateChunk(cy, cx);
			const key = chunkKey(cy, cx);
			if (!stroke.touched.has(key)) {
				stroke.touched.add(key);
				stroke.before.set(key, state.runs ? state.runs.slice() : null);
			}
			const op = {
				tool: stroke.tool,
				value: stroke.value,
				x: x - cx * cw,
				y: y - cy * ch,
				radius: r,
				overwritable: stroke.overwritable,
				unannotated: stroke.unannotated,
				overwrite: stroke.overwrite,
				canonical: this.labelAliasLut,
				regions
			};
			this.touch(state);
			if (state.buffers) this.applyStamp(state, op);
			else {
				state.pending.push(op);
				this.ensureResident(cy, cx);
			}
		}
	}
	/**
	* Per-pixel conditional disc stamp on a resident chunk. With edit regions,
	* each disc row is clamped to the regions' spans on that row, so no pixel
	* outside a region is ever examined or written.
	*/
	applyStamp(state, op) {
		const buffers = state.buffers;
		if (!buffers) return;
		if (op.tool === "erase" && !op.overwrite && op.value === op.unannotated) return;
		const { width, height } = state;
		const current = buffers.current;
		const r = op.radius;
		const r2 = r * r;
		const yStart = Math.max(0, Math.ceil(op.y - r));
		const yEnd = Math.min(height - 1, Math.floor(op.y + r));
		const [chunkHeight, chunkWidth] = this.info.chunkShape;
		const originX = state.cx * chunkWidth;
		const originY = state.cy * chunkHeight;
		const lut = op.canonical;
		let minX = width;
		let maxX = -1;
		let minY = height;
		let maxY = -1;
		for (let py = yStart; py <= yEnd; py++) {
			const dy = py - op.y;
			const span2 = r2 - dy * dy;
			if (span2 < 0) continue;
			const hw = Math.sqrt(span2);
			const xStart = Math.max(0, Math.ceil(op.x - hw));
			const xEnd = Math.min(width - 1, Math.floor(op.x + hw));
			if (xStart > xEnd) continue;
			const spans = op.regions ? regionSpansForRow(op.regions, originY + py, originX, xStart, xEnd) : [[xStart, xEnd]];
			if (spans.length === 0) continue;
			const rowBase = py * width;
			let rowChanged = false;
			for (const [sx, ex] of spans) if (op.tool === "brush") for (let px = sx; px <= ex; px++) {
				const idx = rowBase + px;
				const raw = current[idx];
				const cur = lut ? lut[raw] : raw;
				if (cur === op.value || !(op.overwrite || op.overwritable.has(cur))) continue;
				current[idx] = op.value;
				rowChanged = true;
				if (px < minX) minX = px;
				if (px > maxX) maxX = px;
			}
			else if (op.overwrite) for (let px = sx; px <= ex; px++) {
				const idx = rowBase + px;
				const raw = current[idx];
				const cur = lut ? lut[raw] : raw;
				if (cur === 0 || cur === op.unannotated) continue;
				current[idx] = op.unannotated;
				rowChanged = true;
				if (px < minX) minX = px;
				if (px > maxX) maxX = px;
			}
			else for (let px = sx; px <= ex; px++) {
				const idx = rowBase + px;
				const raw = current[idx];
				if ((lut ? lut[raw] : raw) !== op.value) continue;
				current[idx] = op.unannotated;
				rowChanged = true;
				if (px < minX) minX = px;
				if (px > maxX) maxX = px;
			}
			if (rowChanged) {
				if (py < minY) minY = py;
				if (py > maxY) maxY = py;
			}
		}
		if (maxX >= 0) this.markDirtyRect(state, [
			minY,
			minX,
			maxY + 1,
			maxX + 1
		]);
	}
	/**
	* Finish the stroke: wait for touched chunks to decode, recompute their runs,
	* and report which chunks changed.
	*/
	async endStroke() {
		const stroke = this.stroke;
		if (!stroke) return {
			chunks: [],
			changed: false
		};
		this.stroke = null;
		const touched = [...stroke.touched].map((key) => this.chunks.get(key)).filter((s) => Boolean(s));
		await Promise.all(touched.map((state) => state.loading ?? Promise.resolve()));
		let changed = false;
		const chunks = [];
		for (const state of touched) {
			chunks.push([state.cy, state.cx]);
			if (!state.buffers) continue;
			this.recomputeRuns(state);
			if (!runsEqual(stroke.before.get(chunkKey(state.cy, state.cx)) ?? null, state.runs)) changed = true;
			this.dropEmptyState(state);
		}
		if (changed) this.editedAt = new Date(this.now()).toISOString();
		this.evictBuffersIfNeeded();
		return {
			chunks,
			changed
		};
	}
	recomputeRuns(state) {
		if (!state.buffers) return;
		const result = computeChunkRuns(state.buffers.original, state.buffers.current, state.width);
		if (result) {
			state.runs = result.runs;
			state.changedPixels = result.changedPixels;
			state.bbox = result.bbox;
		} else {
			state.runs = null;
			state.changedPixels = 0;
			state.bbox = null;
		}
	}
	isFilling() {
		return this.fillState !== null;
	}
	/**
	* Flood-fill the 4-connected component containing overlay pixel `(x, y)` with
	* `options.classId` (see {@link LabelRasterFillOptions} for how the component
	* is defined). Spans chunk boundaries; processes one chunk at a time and yields
	* to the event loop between chunks. Chunks outside `options.chunkBounds` are
	* never entered (the result reports `clipped`). When more than `maxPixels`
	* would change, every touched chunk is restored and the result is `"too-large"`.
	*
	* Only one fill runs at a time per session; a second call while one is in
	* progress rejects.
	*/
	fill(x, y, options) {
		if (this.fillTask) return Promise.reject(new Error("A fill is already in progress"));
		const task = this.runFill(x, y, options).finally(() => {
			if (this.fillTask === task) this.fillTask = null;
			this.fillState = null;
		});
		this.fillTask = task;
		return task;
	}
	async runFill(x, y, options) {
		const maxPixels = Math.max(1, Math.round(options.maxPixels ?? this.maxFillPixels));
		const noop = (status) => ({
			status,
			chunks: [],
			changed: false,
			filledPixels: 0,
			maxPixels,
			clipped: false,
			clippedByRegion: false
		});
		if (this.disposed) return noop("cancelled");
		const px = Math.floor(x);
		const py = Math.floor(y);
		if (px < 0 || py < 0 || px >= this.info.shape[1] || py >= this.info.shape[0]) return noop("noop");
		const regions = this.normalizeEditRegions(options.regions);
		if (regions && !pointInAnyRegion(regions, px, py)) return noop("noop");
		const maxValue = this.info.dtype === "uint16" ? 65535 : 255;
		const selected = Math.max(0, Math.min(maxValue, Math.round(options.classId)));
		const rules = this.getEditRules({
			overwritableIds: options.overwritableIds,
			unannotatedId: options.unannotatedId
		});
		const unannotated = Math.min(maxValue, rules.unannotatedId);
		const erase = options.erase ?? false;
		const value = erase ? unannotated : selected;
		const [ch, cw] = this.info.chunkShape;
		const seedCy = Math.floor(py / ch);
		const seedCx = Math.floor(px / cw);
		const bounds = this.normalizeChunkBounds(options.chunkBounds);
		if (!inChunkBounds(bounds, seedCy, seedCx)) return noop("noop");
		await this.ensureResident(seedCy, seedCx);
		if (this.disposed) return noop("cancelled");
		const seedState = this.chunks.get(chunkKey(seedCy, seedCx));
		if (!seedState?.buffers) return noop("noop");
		const localX = px - seedCx * cw;
		const localY = py - seedCy * ch;
		const seedRaw = seedState.buffers.current[localY * seedState.width + localX];
		const lut = this.labelAliasLut;
		const canonical = (raw) => lut ? lut[raw] : raw;
		const seedClass = canonical(seedRaw);
		const member = new Uint8Array(maxValue + 1);
		const overwritable = new Set(rules.overwritableIds);
		if (erase) {
			if (seedClass === 0 || seedClass === unannotated) return noop("noop");
			if (!options.overwrite && seedClass !== selected) return noop("noop");
			for (let raw = 0; raw <= maxValue; raw++) if (canonical(raw) === seedClass) member[raw] = 1;
		} else if (options.overwrite && !overwritable.has(seedClass)) {
			if (seedClass === value) return noop("noop");
			for (let raw = 0; raw <= maxValue; raw++) if (canonical(raw) === seedClass) member[raw] = 1;
		} else for (let raw = 0; raw <= maxValue; raw++) {
			const cls = canonical(raw);
			if (cls !== value && overwritable.has(cls)) member[raw] = 1;
		}
		member[value] = 0;
		if (!member[seedRaw]) return noop("noop");
		const fillState = {
			touched: new Set(),
			before: new Map(),
			queue: new Map()
		};
		this.fillState = fillState;
		let clipped = false;
		let clippedByRegion = false;
		const enqueue = (cy, cx, span) => {
			if (!this.inGrid(cy, cx)) return;
			if (regions && !rectIntersectsAnyRegion(regions, {
				left: cx * cw,
				top: cy * ch,
				right: (cx + 1) * cw,
				bottom: (cy + 1) * ch
			})) {
				clippedByRegion = true;
				return;
			}
			if (!inChunkBounds(bounds, cy, cx)) {
				clipped = true;
				return;
			}
			const key = chunkKey(cy, cx);
			const unit = fillState.queue.get(key);
			if (unit) unit.spans.push(span);
			else fillState.queue.set(key, {
				cy,
				cx,
				spans: [span]
			});
		};
		enqueue(seedCy, seedCx, {
			y: localY,
			x0: localX,
			x1: localX
		});
		let filledPixels = 0;
		let tooLarge = false;
		while (fillState.queue.size > 0) {
			if (this.disposed) return noop("cancelled");
			const [key, unit] = fillState.queue.entries().next().value;
			fillState.queue.delete(key);
			const state = this.getOrCreateChunk(unit.cy, unit.cx);
			if (!state.buffers) {
				await this.ensureResident(unit.cy, unit.cx);
				if (this.disposed) return noop("cancelled");
				if (!state.buffers) continue;
			}
			if (!fillState.touched.has(key)) {
				fillState.touched.add(key);
				fillState.before.set(key, state.runs ? state.runs.slice() : null);
			}
			this.touch(state);
			const chunkResult = this.fillChunk(state, unit.spans, member, value, enqueue, regions);
			filledPixels += chunkResult.count;
			if (chunkResult.clippedByRegion) clippedByRegion = true;
			if (filledPixels > maxPixels) {
				tooLarge = true;
				break;
			}
			options.onProgress?.();
			if (fillState.queue.size > 0) await yieldToEventLoop();
		}
		if (this.disposed) return noop("cancelled");
		const touched = [...fillState.touched].map((key) => this.chunks.get(key)).filter((s) => Boolean(s));
		if (tooLarge) {
			for (const state of touched) {
				if (!state.buffers) continue;
				state.buffers.current = state.buffers.original.slice();
				applyChunkRuns(state.buffers.current, fillState.before.get(chunkKey(state.cy, state.cx)) ?? null);
				this.markDirtyFull(state);
				this.dropEmptyState(state);
			}
			this.evictBuffersIfNeeded();
			return {
				status: "too-large",
				chunks: [],
				changed: false,
				filledPixels,
				maxPixels,
				clipped,
				clippedByRegion
			};
		}
		let changed = false;
		const chunks = [];
		for (const state of touched) {
			if (!state.buffers) continue;
			this.recomputeRuns(state);
			if (!runsEqual(fillState.before.get(chunkKey(state.cy, state.cx)) ?? null, state.runs)) {
				changed = true;
				chunks.push([state.cy, state.cx]);
			}
			this.dropEmptyState(state);
		}
		if (changed) this.editedAt = new Date(this.now()).toISOString();
		this.evictBuffersIfNeeded();
		return {
			status: changed ? "filled" : "noop",
			chunks,
			changed,
			filledPixels,
			maxPixels,
			clipped,
			clippedByRegion
		};
	}
	/** Edit regions clamped to the array; `undefined` when unrestricted. */
	normalizeEditRegions(regions) {
		return normalizeEditRegions(regions, this.info.shape[1], this.info.shape[0]);
	}
	/**
	* Clamp caller bounds to the chunk grid and order the corners, so the result
	* is always a non-empty rectangle inside the grid; `undefined` means the whole array.
	*/
	normalizeChunkBounds(bounds) {
		if (!bounds) return {
			cy0: 0,
			cx0: 0,
			cy1: this.gridRows - 1,
			cx1: this.gridCols - 1
		};
		const clampRow = (v) => Math.max(0, Math.min(this.gridRows - 1, Math.floor(v)));
		const clampCol = (v) => Math.max(0, Math.min(this.gridCols - 1, Math.floor(v)));
		const [cy0, cy1] = [clampRow(bounds.cy0), clampRow(bounds.cy1)].sort((a, b) => a - b);
		const [cx0, cx1] = [clampCol(bounds.cx0), clampCol(bounds.cx1)].sort((a, b) => a - b);
		return {
			cy0,
			cx0,
			cy1,
			cx1
		};
	}
	/**
	* Scanline flood fill inside one resident chunk starting from `spans`. Spans
	* that reach a chunk edge are handed to `enqueue` as seeds for the neighbour
	* chunk (in that chunk's local coordinates). With `regions`, a pixel outside
	* every region is treated like a non-member (and noted as `clippedByRegion`
	* when it would otherwise have been filled). Returns the number of pixels written.
	*/
	fillChunk(state, spans, member, value, enqueue, regions) {
		const buffers = state.buffers;
		if (!buffers) return {
			count: 0,
			clippedByRegion: false
		};
		const { width, height, cy, cx } = state;
		const current = buffers.current;
		const [chunkHeight, chunkWidth] = this.info.chunkShape;
		const allowed = regions ? buildRegionMask(regions, cx * chunkWidth, cy * chunkHeight, width, height) : null;
		let clippedByRegion = false;
		const fillable = allowed ? (idx) => {
			if (!member[current[idx]]) return false;
			if (allowed[idx]) return true;
			clippedByRegion = true;
			return false;
		} : (idx) => member[current[idx]] === 1;
		const stack = [];
		const scanRow = (rowBase, x0, x1) => {
			let inRun = false;
			for (let sx = x0; sx <= x1; sx++) if (fillable(rowBase + sx)) {
				if (!inRun) stack.push(rowBase + sx);
				inRun = true;
			} else inRun = false;
		};
		for (const span of spans) {
			if (span.y < 0 || span.y >= height) continue;
			scanRow(span.y * width, Math.max(0, span.x0), Math.min(width - 1, span.x1));
		}
		if (stack.length === 0) return {
			count: 0,
			clippedByRegion
		};
		let count = 0;
		let minX = width;
		let maxX = -1;
		let minY = height;
		let maxY = -1;
		while (stack.length > 0) {
			const idx = stack.pop();
			if (!fillable(idx)) continue;
			const yRow = Math.floor(idx / width);
			const rowBase = yRow * width;
			let l = idx;
			let r = idx;
			while (l > rowBase && fillable(l - 1)) l--;
			while (r < rowBase + width - 1 && fillable(r + 1)) r++;
			current.fill(value, l, r + 1);
			count += r - l + 1;
			const xl = l - rowBase;
			const xr = r - rowBase;
			if (xl < minX) minX = xl;
			if (xr > maxX) maxX = xr;
			if (yRow < minY) minY = yRow;
			if (yRow > maxY) maxY = yRow;
			if (yRow > 0) scanRow(rowBase - width, xl, xr);
			else if (cy > 0) enqueue(cy - 1, cx, {
				y: chunkHeight - 1,
				x0: xl,
				x1: xr
			});
			if (yRow < height - 1) scanRow(rowBase + width, xl, xr);
			else if (cy < this.gridRows - 1) enqueue(cy + 1, cx, {
				y: 0,
				x0: xl,
				x1: xr
			});
			if (xl === 0 && cx > 0) enqueue(cy, cx - 1, {
				y: yRow,
				x0: chunkWidth - 1,
				x1: chunkWidth - 1
			});
			if (xr === width - 1 && cx < this.gridCols - 1) enqueue(cy, cx + 1, {
				y: yRow,
				x0: 0,
				x1: 0
			});
		}
		if (count > 0) this.markDirtyRect(state, [
			minY,
			minX,
			maxY + 1,
			maxX + 1
		]);
		return {
			count,
			clippedByRegion
		};
	}
	/** Read the current (edited) label at overlay pixel coordinates. */
	async readValueAt(x, y) {
		const px = Math.floor(x);
		const py = Math.floor(y);
		if (px < 0 || py < 0 || px >= this.info.shape[1] || py >= this.info.shape[0]) return null;
		const [ch, cw] = this.info.chunkShape;
		const cy = Math.floor(py / ch);
		const cx = Math.floor(px / cw);
		await this.ensureResident(cy, cx);
		const state = this.chunks.get(chunkKey(cy, cx));
		if (!state?.buffers) return null;
		const value = state.buffers.current[(py - cy * ch) * state.width + (px - cx * cw)];
		return value === void 0 ? null : value;
	}
	/** Full-chunk palette-index bytes when resident, else null. */
	getIndexedChunk(cy, cx) {
		const state = this.chunks.get(chunkKey(cy, cx));
		if (!state?.buffers) return null;
		return labelsToIndexedBytes(state.buffers.current);
	}
	/** Drain pending texture updates (call once per frame before drawing). */
	consumeDirtyTextures() {
		const result = [];
		for (const state of this.chunks.values()) {
			if (!(state.textureDirty && state.buffers)) continue;
			const rect = state.dirtyRect;
			state.textureDirty = false;
			state.dirtyRect = null;
			if (!rect) {
				result.push({
					cy: state.cy,
					cx: state.cx,
					width: state.width,
					height: state.height,
					rect: null,
					indexed: labelsToIndexedBytes(state.buffers.current)
				});
				continue;
			}
			const [y0, x0, y1, x1] = rect;
			const rw = x1 - x0;
			const rh = y1 - y0;
			const indexed = new Uint8Array(rw * rh);
			const current = state.buffers.current;
			for (let ry = 0; ry < rh; ry++) {
				const srcBase = (y0 + ry) * state.width + x0;
				labelsToIndexedBytes(current.subarray(srcBase, srcBase + rw), indexed.subarray(ry * rw, ry * rw + rw));
			}
			result.push({
				cy: state.cy,
				cx: state.cx,
				width: state.width,
				height: state.height,
				rect,
				indexed
			});
		}
		return result;
	}
	hasEdits() {
		for (const state of this.chunks.values()) if (state.runs) return true;
		return false;
	}
	getStats() {
		let chunks = 0;
		let changedPixels = 0;
		for (const state of this.chunks.values()) {
			if (!state.runs) continue;
			chunks++;
			changedPixels += state.changedPixels;
		}
		return {
			chunks,
			changedPixels
		};
	}
	/** Chunks whose decoded original did not match the CRC recorded in an applied diff. */
	getCrcMismatches() {
		return [...this.crcMismatches.values()];
	}
	/** Build the sparse diff from runs (no buffers required). Null when there are no edits. */
	getDiff() {
		const chunks = [];
		for (const state of this.chunks.values()) {
			if (!(state.runs && state.bbox)) continue;
			chunks.push({
				index: [state.cy, state.cx],
				originalCrc32: state.crc ?? state.expectedCrc ?? 0,
				runs: state.runs.slice(),
				changedPixels: state.changedPixels,
				bbox: [
					state.bbox[0],
					state.bbox[1],
					state.bbox[2],
					state.bbox[3]
				]
			});
		}
		if (chunks.length === 0) return null;
		chunks.sort((a, b) => a.index[0] - b.index[0] || a.index[1] - b.index[1]);
		return {
			version: 1,
			source: {
				seriesId: this.info.sourceName,
				arrayPath: this.info.arrayPath,
				shape: [this.info.shape[0], this.info.shape[1]],
				chunkShape: [this.info.chunkShape[0], this.info.chunkShape[1]],
				dtype: this.info.dtype,
				axes: ["y", "x"],
				taxonomy: this.info.attrs.taxonomy,
				pipelineVersion: this.info.attrs.pipelineVersion
			},
			chunks,
			classesUsed: collectClassesUsed(chunks),
			editedAt: this.editedAt ?? new Date(this.now()).toISOString()
		};
	}
	/**
	* Replace the edit state with `diff` (null clears all edits). Chunks whose
	* runs are unchanged are skipped, so undo/redo of one stroke touches only the
	* chunks that stroke changed; `changedChunks` lists the ones that did change
	* (resident or not), so the renderer can drop textures of evicted chunks.
	*/
	async applyDiff(diff) {
		if (this.disposed) return {
			appliedChunks: 0,
			changedChunks: [],
			crcMismatches: []
		};
		if (this.fillTask) await this.fillTask.catch(() => {});
		const loading = [...this.chunks.values()].map((s) => s.loading).filter((p) => Boolean(p));
		if (loading.length > 0) await Promise.all(loading);
		let applied = 0;
		const changedChunks = [];
		const incoming = new Map();
		for (const chunk of diff?.chunks ?? []) {
			if (!this.inGrid(chunk.index[0], chunk.index[1])) continue;
			incoming.set(chunkKey(chunk.index[0], chunk.index[1]), chunk);
		}
		for (const state of [...this.chunks.values()]) {
			const key = chunkKey(state.cy, state.cx);
			if (incoming.has(key) || !state.runs) continue;
			state.runs = null;
			state.changedPixels = 0;
			state.bbox = null;
			state.expectedCrc = null;
			state.pending = [];
			if (state.buffers) {
				state.buffers.current = state.buffers.original.slice();
				this.markDirtyFull(state);
			}
			applied++;
			changedChunks.push([state.cy, state.cx]);
			this.dropEmptyState(state);
		}
		for (const chunk of incoming.values()) {
			const state = this.getOrCreateChunk(chunk.index[0], chunk.index[1]);
			if (runsEqual(state.runs, chunk.runs)) continue;
			state.runs = chunk.runs.slice();
			state.changedPixels = chunk.changedPixels;
			state.bbox = [
				chunk.bbox[0],
				chunk.bbox[1],
				chunk.bbox[2],
				chunk.bbox[3]
			];
			state.expectedCrc = chunk.originalCrc32;
			state.pending = [];
			if (state.crc !== null && state.crc !== chunk.originalCrc32) this.crcMismatches.set(chunkKey(state.cy, state.cx), [state.cy, state.cx]);
			if (state.buffers) {
				state.buffers.current = state.buffers.original.slice();
				applyChunkRuns(state.buffers.current, state.runs);
				this.markDirtyFull(state);
			}
			this.touch(state);
			applied++;
			changedChunks.push([state.cy, state.cx]);
		}
		if (diff?.editedAt) this.editedAt = diff.editedAt;
		else if (!diff) this.editedAt = null;
		return {
			appliedChunks: applied,
			changedChunks,
			crcMismatches: this.getCrcMismatches()
		};
	}
	dispose() {
		this.disposed = true;
		this.stroke = null;
		this.fillState = null;
		this.chunks.clear();
		this.crcMismatches.clear();
	}
};

//#endregion
//#region src/logger.ts
let currentLevel = "off";
const COLORS = {
	visible: "#4CAF50",
	prefetch: "#2196F3",
	loaded: "#9C27B0",
	velocity: "#FF9800",
	cache: "#607D8B"
};
function formatTile(imageName, level, x, y) {
	return `${imageName}:L${level}(${x},${y})`;
}
function isEnabled(minimum) {
	if (currentLevel === "off") return false;
	if (minimum === "debug") return currentLevel === "debug";
	return true;
}
function print(message, style) {
	console.debug(`%c[wsi-viewer] ${message}`, style);
}
const Logger = {
	setLevel(level) {
		currentLevel = level;
		if (level !== "off") print(`Logging enabled: ${level}`, "color: #666; font-weight: bold");
	},
	enable() {
		this.setLevel("info");
	},
	enableDebug() {
		this.setLevel("debug");
	},
	disable() {
		this.setLevel("off");
	},
	getLevel() {
		return currentLevel;
	},
	visibleScheduled(imageName, level, x, y) {
		if (isEnabled("info")) print(`[VISIBLE] ${formatTile(imageName, level, x, y)}`, `color: ${COLORS.visible}; font-weight: bold`);
	},
	prefetchScheduled(imageName, level, x, y, probability) {
		if (isEnabled("info")) print(`[PREFETCH] ${formatTile(imageName, level, x, y)} prob=${(probability * 100).toFixed(1)}%`, `color: ${COLORS.prefetch}`);
	},
	tileLoaded(imageName, level, x, y, priority) {
		if (isEnabled("info")) print(`[LOADED] ${formatTile(imageName, level, x, y)} (${priority})`, `color: ${COLORS.loaded}`);
	},
	velocity(vx, vy, varX, varY) {
		if (isEnabled("debug")) print(`[VELOCITY] vx=${vx.toFixed(3)} vy=${vy.toFixed(3)} σx=${Math.sqrt(varX).toFixed(3)} σy=${Math.sqrt(varY).toFixed(3)}`, `color: ${COLORS.velocity}`);
	},
	prefetchBatch(count, topProbability) {
		if (count > 0 && isEnabled("info")) print(`[PREFETCH] Queued ${count} tiles (top prob: ${(topProbability * 100).toFixed(1)}%)`, `color: ${COLORS.prefetch}; font-style: italic`);
	},
	cacheStats(visible, prefetch, recent, total) {
		if (isEnabled("debug")) print(`[CACHE] visible=${visible} prefetch=${prefetch} recent=${recent} total=${total}`, `color: ${COLORS.cache}`);
	},
	prefetchCancelled(count) {
		if (count > 0 && isEnabled("info")) print(`[PREFETCH] Cancelled ${count} pending prefetch requests`, `color: ${COLORS.prefetch}; font-style: italic`);
	}
};

//#endregion
//#region src/math.ts
/**
* Mathematical utilities for prefetch probability calculations
*/
/**
* Error function (erf) approximation using Abramowitz-Stegun method.
* Maximum error: 1.5×10⁻⁷
*
* @param x Input value
* @returns Approximation of erf(x)
*/
function erf(x) {
	const sign = x >= 0 ? 1 : -1;
	x = Math.abs(x);
	const a1 = .254829592;
	const a2 = -.284496736;
	const a3 = 1.421413741;
	const a4 = -1.453152027;
	const a5 = 1.061405429;
	const p = .3275911;
	const t = 1 / (1 + p * x);
	const t2 = t * t;
	const t3 = t2 * t;
	const t4 = t3 * t;
	const t5 = t4 * t;
	const y = 1 - (a1 * t + a2 * t2 + a3 * t3 + a4 * t4 + a5 * t5) * Math.exp(-x * x);
	return sign * y;
}
/**
* Cumulative distribution function (CDF) for standard normal distribution.
*
* @param x Input value
* @returns Probability that a standard normal random variable is less than x
*/
function normalCdf(x) {
	return .5 * (1 + erf(x / Math.SQRT2));
}
/**
* CDF for normal distribution with given mean and standard deviation.
*
* @param x Input value
* @param mean Mean of the distribution
* @param stddev Standard deviation of the distribution
* @returns Probability that a normal(mean, stddev) random variable is less than x
*/
function normalCdfWithParams(x, mean, stddev) {
	if (stddev <= 0) return x >= mean ? 1 : 0;
	return normalCdf((x - mean) / stddev);
}

//#endregion
//#region src/priority-cache.ts
/**
* Priority-aware tile cache with three-tier eviction strategy.
*
* Priorities:
* - VISIBLE: Currently on screen (highest priority, never evicted)
* - PREFETCH: Predicted to be needed soon
* - RECENT: LRU fallback for recently accessed tiles
*
* Eviction rules:
* - VISIBLE tiles are never evicted
* - VISIBLE can evict PREFETCH or RECENT
* - PREFETCH can only evict RECENT
* - Within a tier, evict by LRU (oldest lastAccessed first)
*/
/** Tile priority levels */
let TilePriority = /* @__PURE__ */ function(TilePriority$1) {
	TilePriority$1[TilePriority$1["VISIBLE"] = 0] = "VISIBLE";
	TilePriority$1[TilePriority$1["PREFETCH"] = 1] = "PREFETCH";
	TilePriority$1[TilePriority$1["RECENT"] = 2] = "RECENT";
	return TilePriority$1;
}({});
const DEFAULT_MAX_TILES = 500;
const DEFAULT_VISIBLE_RESERVE = 100;
const DEFAULT_PREFETCH_RESERVE = 200;
/**
* Priority-aware tile cache with tier-based eviction.
*/
var PriorityCache = class {
	constructor(config = {}) {
		this.cache = new Map();
		this.loading = new Set();
		this.visibleKeys = new Set();
		this.maxSize = void 0;
		this.visibleReserve = void 0;
		this.prefetchReserve = void 0;
		this.maxSize = config.maxTiles ?? DEFAULT_MAX_TILES;
		this.visibleReserve = config.visibleReserve ?? DEFAULT_VISIBLE_RESERVE;
		this.prefetchReserve = config.prefetchReserve ?? DEFAULT_PREFETCH_RESERVE;
	}
	/**
	* Generate a cache key for a tile
	*/
	static getKey(imageName, level, x, y) {
		return `${imageName}:${level}:${x}:${y}`;
	}
	/**
	* Check if a tile is in the cache
	*/
	has(key) {
		return this.cache.has(key);
	}
	/**
	* Get a tile from the cache, updating last access time
	*/
	get(key) {
		const entry = this.cache.get(key);
		if (entry) {
			entry.lastAccessed = performance.now();
			return entry.image;
		}
	}
	/**
	* Get cache entry with metadata (doesn't update access time)
	*/
	getEntry(key) {
		return this.cache.get(key);
	}
	/**
	* Add a tile to the cache with specified priority.
	* Will evict lower-priority or older tiles if needed.
	*
	* @param key Cache key
	* @param image Tile image
	* @param priority Tile priority
	* @param probability Optional prefetch probability (for PREFETCH tier)
	* @returns true if added, false if cannot add (would evict higher priority)
	*/
	set(key, image, priority = TilePriority.RECENT, probability) {
		if (this.cache.has(key)) {
			const entry = this.cache.get(key);
			entry.image = image;
			entry.lastAccessed = performance.now();
			if (priority < entry.priority) entry.priority = priority;
			entry.probability = probability ?? entry.probability;
			return true;
		}
		if (this.cache.size >= this.maxSize) {
			const evicted = this.evictOne(priority);
			if (!evicted) return false;
		}
		this.cache.set(key, {
			image,
			priority,
			lastAccessed: performance.now(),
			probability
		});
		return true;
	}
	/**
	* Mark tiles as currently visible. Other tiles in VISIBLE tier are demoted.
	*
	* @param keys Set of keys that are currently visible
	*/
	markVisible(keys) {
		const now = performance.now();
		this.visibleKeys = new Set(keys);
		for (const [key, entry] of this.cache) if (keys.has(key)) {
			entry.priority = TilePriority.VISIBLE;
			entry.lastAccessed = now;
		} else if (entry.priority === TilePriority.VISIBLE) entry.priority = TilePriority.RECENT;
	}
	/**
	* Set priority for a specific tile.
	*
	* @param key Cache key
	* @param priority New priority
	* @param probability Optional prefetch probability
	*/
	setPriority(key, priority, probability) {
		const entry = this.cache.get(key);
		if (entry) {
			entry.priority = priority;
			if (probability !== void 0) entry.probability = probability;
		}
	}
	/**
	* Evict one tile to make room for a new entry with given priority.
	*
	* @param incomingPriority Priority of the tile we want to add
	* @returns true if eviction succeeded, false if no evictable tiles
	*/
	evictOne(incomingPriority) {
		const candidates = [];
		for (const [key, entry] of this.cache) {
			if (entry.priority === TilePriority.VISIBLE) continue;
			if (incomingPriority === TilePriority.VISIBLE) candidates.push({
				key,
				entry
			});
			else if (incomingPriority === TilePriority.PREFETCH) {
				if (entry.priority === TilePriority.RECENT) candidates.push({
					key,
					entry
				});
			} else if (entry.priority === TilePriority.RECENT) candidates.push({
				key,
				entry
			});
		}
		if (candidates.length === 0) return false;
		candidates.sort((a, b) => {
			if (a.entry.priority !== b.entry.priority) return b.entry.priority - a.entry.priority;
			return a.entry.lastAccessed - b.entry.lastAccessed;
		});
		this.cache.delete(candidates[0].key);
		return true;
	}
	/**
	* Check if a tile is currently being loaded
	*/
	isLoading(key) {
		return this.loading.has(key);
	}
	/**
	* Mark a tile as loading
	*/
	startLoading(key) {
		this.loading.add(key);
	}
	/**
	* Mark a tile as done loading
	*/
	doneLoading(key) {
		this.loading.delete(key);
	}
	/**
	* Clear all cached tiles and loading state
	*/
	clear() {
		this.cache.clear();
		this.loading.clear();
		this.visibleKeys.clear();
	}
	/**
	* Get all cache keys
	*/
	keys() {
		return this.cache.keys();
	}
	/**
	* Get current cache size
	*/
	get size() {
		return this.cache.size;
	}
	/**
	* Get cache statistics by tier
	*/
	getStats() {
		let visible = 0;
		let prefetch = 0;
		let recent = 0;
		for (const entry of this.cache.values()) switch (entry.priority) {
			case TilePriority.VISIBLE:
				visible++;
				break;
			case TilePriority.PREFETCH:
				prefetch++;
				break;
			case TilePriority.RECENT:
				recent++;
				break;
		}
		return {
			visible,
			prefetch,
			recent,
			loading: this.loading.size,
			total: this.cache.size,
			maxSize: this.maxSize
		};
	}
	/**
	* Get all keys at a specific priority level
	*/
	getKeysByPriority(priority) {
		const keys = [];
		for (const [key, entry] of this.cache) if (entry.priority === priority) keys.push(key);
		return keys;
	}
	/**
	* Demote all PREFETCH tiles to RECENT.
	* Called when velocity changes significantly or prefetch is disabled.
	*/
	demotePrefetch() {
		for (const entry of this.cache.values()) if (entry.priority === TilePriority.PREFETCH) entry.priority = TilePriority.RECENT;
	}
	/**
	* Check if cache has room for tiles at a given priority.
	*
	* @param priority Priority level to check
	* @param count Number of tiles to add
	* @returns true if there's room or eviction is possible
	*/
	hasRoom(priority, count = 1) {
		if (this.cache.size + count <= this.maxSize) return true;
		let evictable = 0;
		for (const entry of this.cache.values()) {
			if (entry.priority === TilePriority.VISIBLE) continue;
			if (priority === TilePriority.VISIBLE) evictable++;
			else if (priority === TilePriority.PREFETCH) {
				if (entry.priority === TilePriority.RECENT) evictable++;
			} else if (entry.priority === TilePriority.RECENT) evictable++;
		}
		return evictable >= count;
	}
};

//#endregion
//#region src/prefetch-manager.ts
const DEFAULT_PREDICTION_WINDOW = 2e3;
const DEFAULT_PROBABILITY_CUTOFF = .05;
const DEFAULT_MAX_TILES_PER_DIRECTION = 10;
/**
* Calculate probability that tile at offset i will be needed.
*
* Uses the error function to compute CDF of normal distribution
* parameterized by velocity mean and variance.
*
* @param offset Tile offset from current position (positive = forward)
* @param fraction Fractional position within current tile (0-1)
* @param velocityState Velocity state for this dimension
* @param tileSize Tile size in level 0 coordinates
* @param predictionMs Prediction window in milliseconds
* @returns Probability (0-1) that this tile will be needed
*/
function computeTileProbability(offset, fraction, velocityState, tileSize, predictionMs) {
	const adjustedMean = velocityState.mean / tileSize * predictionMs;
	const adjustedStddev = Math.sqrt(2 * velocityState.variance) / tileSize * predictionMs;
	if (adjustedStddev < .001) if (offset > 0) return adjustedMean >= offset - fraction ? 1 : 0;
	else return adjustedMean <= offset + 1 - fraction ? 1 : 0;
	if (offset > 0) {
		const x = offset - fraction;
		return 1 - .5 * (1 + erf((x - adjustedMean) / adjustedStddev));
	} else {
		const x = offset + 1 - fraction;
		return .5 * (1 + erf((x - adjustedMean) / adjustedStddev));
	}
}
/**
* Prefetch manager for computing which tiles to prefetch based on velocity.
*/
var PrefetchManager = class {
	constructor(config = {}) {
		this.enabled = void 0;
		this.predictionWindow = void 0;
		this.probabilityCutoff = void 0;
		this.maxTilesPerDirection = void 0;
		this.enabled = config.enabled ?? false;
		this.predictionWindow = config.predictionWindow ?? DEFAULT_PREDICTION_WINDOW;
		this.probabilityCutoff = config.probabilityCutoff ?? DEFAULT_PROBABILITY_CUTOFF;
		this.maxTilesPerDirection = config.maxTilesPerDirection ?? DEFAULT_MAX_TILES_PER_DIRECTION;
	}
	/**
	* Check if prefetching is enabled.
	*/
	isEnabled() {
		return this.enabled;
	}
	/**
	* Enable or disable prefetching.
	*/
	setEnabled(enabled) {
		this.enabled = enabled;
	}
	/**
	* Update configuration.
	*/
	configure(config) {
		if (config.enabled !== void 0) this.enabled = config.enabled;
		if (config.predictionWindow !== void 0) this.predictionWindow = config.predictionWindow;
		if (config.probabilityCutoff !== void 0) this.probabilityCutoff = config.probabilityCutoff;
		if (config.maxTilesPerDirection !== void 0) this.maxTilesPerDirection = config.maxTilesPerDirection;
	}
	/**
	* Compute tiles to prefetch for a single-image viewer.
	*
	* @param viewport Current viewport state
	* @param velocity Current velocity state
	* @param visibleTileKeys Set of currently visible tile keys (to exclude)
	* @param cachedKeys Set of already cached tile keys (to exclude)
	* @returns Array of tile requests sorted by probability
	*/
	computePrefetchTiles(viewport, velocity, visibleTileKeys, cachedKeys) {
		if (!(this.enabled && viewport.imageName)) return [];
		const vx = velocity.x.velocity;
		const vy = velocity.y.velocity;
		const velocityMagnitude = Math.sqrt(vx * vx + vy * vy);
		const timeSinceUpdate = performance.now() - velocity.lastTime;
		if (velocityMagnitude < .001 || timeSinceUpdate > 500) return [];
		const requests = [];
		const levelCenterX = viewport.centerX / viewport.levelScale;
		const levelCenterY = viewport.centerY / viewport.levelScale;
		const currentTileX = Math.floor(levelCenterX / viewport.tileWidth);
		const currentTileY = Math.floor(levelCenterY / viewport.tileHeight);
		const fractionX = levelCenterX / viewport.tileWidth - currentTileX;
		const fractionY = levelCenterY / viewport.tileHeight - currentTileY;
		const tileSizeX = viewport.tileWidth * viewport.levelScale;
		const tileSizeY = viewport.tileHeight * viewport.levelScale;
		const maxTileX = Math.ceil(viewport.levelWidth / viewport.tileWidth);
		const maxTileY = Math.ceil(viewport.levelHeight / viewport.tileHeight);
		for (let dx = 1; dx <= this.maxTilesPerDirection; dx++) {
			const forwardProb = computeTileProbability(dx, fractionX, velocity.x, tileSizeX, this.predictionWindow);
			if (forwardProb >= this.probabilityCutoff) {
				const tileX = currentTileX + dx;
				if (tileX >= 0 && tileX < maxTileX) this.addTileIfNeeded(requests, viewport.imageName, viewport.level, tileX, currentTileY, forwardProb, visibleTileKeys, cachedKeys);
			}
			const backwardProb = computeTileProbability(-dx, fractionX, velocity.x, tileSizeX, this.predictionWindow);
			if (backwardProb >= this.probabilityCutoff) {
				const tileX = currentTileX - dx;
				if (tileX >= 0 && tileX < maxTileX) this.addTileIfNeeded(requests, viewport.imageName, viewport.level, tileX, currentTileY, backwardProb, visibleTileKeys, cachedKeys);
			}
		}
		for (let dy = 1; dy <= this.maxTilesPerDirection; dy++) {
			const forwardProb = computeTileProbability(dy, fractionY, velocity.y, tileSizeY, this.predictionWindow);
			if (forwardProb >= this.probabilityCutoff) {
				const tileY = currentTileY + dy;
				if (tileY >= 0 && tileY < maxTileY) this.addTileIfNeeded(requests, viewport.imageName, viewport.level, currentTileX, tileY, forwardProb, visibleTileKeys, cachedKeys);
			}
			const backwardProb = computeTileProbability(-dy, fractionY, velocity.y, tileSizeY, this.predictionWindow);
			if (backwardProb >= this.probabilityCutoff) {
				const tileY = currentTileY - dy;
				if (tileY >= 0 && tileY < maxTileY) this.addTileIfNeeded(requests, viewport.imageName, viewport.level, currentTileX, tileY, backwardProb, visibleTileKeys, cachedKeys);
			}
		}
		for (let dx = 1; dx <= this.maxTilesPerDirection / 2; dx++) for (let dy = 1; dy <= this.maxTilesPerDirection / 2; dy++) {
			const probX = computeTileProbability(dx, fractionX, velocity.x, tileSizeX, this.predictionWindow);
			const probY = computeTileProbability(dy, fractionY, velocity.y, tileSizeY, this.predictionWindow);
			const prob = probX * probY;
			if (prob >= this.probabilityCutoff) {
				const diagonals = [
					[dx, dy],
					[dx, -dy],
					[-dx, dy],
					[-dx, -dy]
				];
				for (const [ddx, ddy] of diagonals) {
					const tileX = currentTileX + ddx;
					const tileY = currentTileY + ddy;
					if (tileX >= 0 && tileX < maxTileX && tileY >= 0 && tileY < maxTileY) this.addTileIfNeeded(requests, viewport.imageName, viewport.level, tileX, tileY, prob, visibleTileKeys, cachedKeys);
				}
			}
		}
		return requests.sort((a, b) => (b.probability ?? 0) - (a.probability ?? 0));
	}
	/**
	* Compute tiles to prefetch for composite viewer with multiple images.
	*
	* @param viewport Composite viewport state
	* @param velocity Current velocity state
	* @param visibleTileKeys Set of currently visible tile keys
	* @param cachedKeys Set of already cached tile keys
	* @returns Array of tile requests sorted by probability
	*/
	computeCompositePrefetchTiles(viewport, velocity, visibleTileKeys, cachedKeys) {
		if (!this.enabled) return [];
		const vx = velocity.x.velocity;
		const vy = velocity.y.velocity;
		const velocityMagnitude = Math.sqrt(vx * vx + vy * vy);
		const timeSinceUpdate = performance.now() - velocity.lastTime;
		if (velocityMagnitude < .001 || timeSinceUpdate > 500) return [];
		const requests = [];
		for (let row = 0; row < viewport.rows; row++) for (let col = 0; col < viewport.columns; col++) {
			const index = row * viewport.columns + col;
			const imageName = viewport.images[index];
			if (!imageName) continue;
			const gridOffsetX = col * viewport.imageWidth;
			const gridOffsetY = row * viewport.imageHeight;
			const relativeCenterX = viewport.centerX - gridOffsetX;
			const relativeCenterY = viewport.centerY - gridOffsetY;
			const imageViewport = {
				centerX: relativeCenterX,
				centerY: relativeCenterY,
				zoom: viewport.zoom,
				level: viewport.level,
				levelScale: viewport.levelScale,
				tileWidth: viewport.tileWidth,
				tileHeight: viewport.tileHeight,
				levelWidth: viewport.levelWidth,
				levelHeight: viewport.levelHeight,
				imageName
			};
			const imageRequests = this.computePrefetchTiles(imageViewport, velocity, visibleTileKeys, cachedKeys);
			requests.push(...imageRequests);
		}
		return requests.sort((a, b) => (b.probability ?? 0) - (a.probability ?? 0));
	}
	/**
	* Add a tile request if not already visible or cached.
	*/
	addTileIfNeeded(requests, imageName, level, x, y, probability, visibleTileKeys, cachedKeys) {
		const key = `${imageName}:${level}:${x}:${y}`;
		if (visibleTileKeys.has(key) || cachedKeys.has(key)) return;
		if (requests.some((r) => r.key === key)) return;
		requests.push({
			key,
			imageName,
			level,
			x,
			y,
			priority: TilePriority.PREFETCH,
			probability
		});
	}
};

//#endregion
//#region src/tile-cache.ts
/**
* Simple LRU-style tile cache
* Uses Map's insertion order for eviction (first-in, first-out)
*/
var TileCache = class {
	constructor(maxSize = 500) {
		this.cache = new Map();
		this.loading = new Set();
		this.maxSize = void 0;
		this.maxSize = maxSize;
	}
	/**
	* Generate a cache key for a tile
	*/
	static getKey(imageName, level, x, y) {
		return `${imageName}:${level}:${x}:${y}`;
	}
	/**
	* Check if a tile is in the cache
	*/
	has(key) {
		return this.cache.has(key);
	}
	/**
	* Get a tile from the cache
	*/
	get(key) {
		return this.cache.get(key);
	}
	/**
	* Add a tile to the cache, evicting oldest if necessary
	*/
	set(key, img) {
		while (this.cache.size >= this.maxSize) {
			const firstKey = this.cache.keys().next().value;
			if (firstKey) this.cache.delete(firstKey);
			else break;
		}
		this.cache.set(key, img);
	}
	/**
	* Check if a tile is currently being loaded
	*/
	isLoading(key) {
		return this.loading.has(key);
	}
	/**
	* Mark a tile as loading
	*/
	startLoading(key) {
		this.loading.add(key);
	}
	/**
	* Mark a tile as done loading
	*/
	doneLoading(key) {
		this.loading.delete(key);
	}
	/**
	* Clear all cached tiles and loading state
	*/
	clear() {
		this.cache.clear();
		this.loading.clear();
	}
	/**
	* Get current cache size
	*/
	get size() {
		return this.cache.size;
	}
};

//#endregion
//#region src/tile-client.ts
const DEFAULT_TILE_URL_PATTERN = "/api/images/{name}/tile/{level}/{x}/{y}";
const DEFAULT_INFO_URL_PATTERN = "/api/images/{name}/info";
const DEFAULT_THUMBNAIL_URL_PATTERN = "/api/images/{name}/thumbnail";
const DEFAULT_SOURCE_TYPE = "tiff";
const DEFAULT_LABEL_CHUNK_CACHE_SIZE = 48;
function normalizeBase(base) {
	return base.endsWith("/") ? base.slice(0, -1) : base;
}
function normalizePrefix(prefix) {
	if (!prefix) return "";
	const normalized = prefix.startsWith("/") ? prefix : `/${prefix}`;
	return normalized.endsWith("/") ? normalized.slice(0, -1) : normalized;
}
function toAbsoluteUrl(url) {
	try {
		return new URL(url).toString();
	} catch {
		if (typeof window !== "undefined" && window.location?.origin) return new URL(url, window.location.origin).toString();
		return url;
	}
}
function buildTileCanvas(rgba, width, height) {
	if (typeof document === "undefined") return null;
	const canvas = document.createElement("canvas");
	canvas.width = width;
	canvas.height = height;
	const ctx = canvas.getContext("2d");
	if (!ctx) return null;
	const imageData = ctx.createImageData(width, height);
	imageData.data.set(rgba);
	ctx.putImageData(imageData, 0, 0);
	return canvas;
}
function canvasToImage(canvas) {
	return new Promise((resolve) => {
		const img = new Image();
		img.onload = () => {
			URL.revokeObjectURL(img.src);
			resolve(img);
		};
		img.onerror = () => resolve(null);
		canvas.toBlob((blob) => {
			if (!blob) {
				resolve(null);
				return;
			}
			img.src = URL.createObjectURL(blob);
		}, "image/png");
	});
}
function toLabelArray(data, dtype) {
	if (dtype === "uint16") {
		if (data instanceof Uint16Array) return data.slice();
		const out$1 = new Uint16Array(data.length);
		for (let i = 0; i < data.length; i++) out$1[i] = Number(data[i] ?? 0);
		return out$1;
	}
	if (data instanceof Uint8Array) return data.slice();
	const out = new Uint8Array(data.length);
	for (let i = 0; i < data.length; i++) out[i] = Number(data[i] ?? 0);
	return out;
}
var TileClient = class {
	constructor(config) {
		this.baseUrl = void 0;
		this.tileUrlPattern = void 0;
		this.infoUrlPattern = void 0;
		this.thumbnailUrlPattern = void 0;
		this.fetchFn = void 0;
		this.fetchOptions = void 0;
		this.sourceType = void 0;
		this.zarrPathPrefix = void 0;
		this.labelChunkCacheSize = void 0;
		this.selectedChannels = null;
		this.zarrContexts = new Map();
		this.zarrContextErrors = new Map();
		this.zarrContextErrorLogged = new Set();
		this.zarrThumbnailCache = new Map();
		this.primedInfo = new Map();
		this.labelChunkCache = new Map();
		this.labelChunkPending = new Map();
		this.baseUrl = config.baseUrl;
		this.tileUrlPattern = config.tileUrlPattern ?? DEFAULT_TILE_URL_PATTERN;
		this.infoUrlPattern = config.infoUrlPattern ?? DEFAULT_INFO_URL_PATTERN;
		this.thumbnailUrlPattern = config.thumbnailUrlPattern ?? DEFAULT_THUMBNAIL_URL_PATTERN;
		this.fetchFn = config.fetch ?? globalThis.fetch.bind(globalThis);
		this.fetchOptions = config.fetchOptions ?? {};
		this.sourceType = config.sourceType ?? DEFAULT_SOURCE_TYPE;
		this.zarrPathPrefix = normalizePrefix(config.zarr?.pathPrefix ?? "");
		this.labelChunkCacheSize = Math.max(1, config.zarr?.labelChunkCacheSize ?? DEFAULT_LABEL_CHUNK_CACHE_SIZE);
	}
	setSourceType(sourceType) {
		if (this.sourceType === sourceType) return;
		this.sourceType = sourceType;
		this.selectedChannels = null;
		this.zarrThumbnailCache.clear();
		if (sourceType !== "omezarr") {
			this.zarrContexts.clear();
			this.zarrContextErrors.clear();
			this.zarrContextErrorLogged.clear();
			this.labelChunkCache.clear();
		}
	}
	getSourceType() {
		return this.sourceType;
	}
	setSelectedChannels(channels) {
		const normalized = [...new Set(channels.filter((value) => Number.isInteger(value) && value >= 0))];
		this.selectedChannels = normalized.length > 0 ? normalized : null;
		this.zarrThumbnailCache.clear();
	}
	getSelectedChannels() {
		return this.selectedChannels ? [...this.selectedChannels] : [];
	}
	getAvailableChannels(name) {
		if (!name) return [];
		const info = this.primedInfo.get(name);
		return info?.channels ? [...info.channels] : [];
	}
	primeImageInfo(info) {
		this.primedInfo.set(info.name, info);
		this.zarrContextErrors.delete(info.name);
		this.zarrContextErrorLogged.delete(info.name);
	}
	destroy() {
		this.zarrContexts.clear();
		this.zarrContextErrors.clear();
		this.zarrContextErrorLogged.clear();
		this.zarrThumbnailCache.clear();
		this.labelChunkCache.clear();
		this.labelChunkPending.clear();
	}
	/**
	* Get the URL for a specific tile
	*/
	getTileUrl(name, level, x, y, query) {
		const path = this.tileUrlPattern.replace("{name}", encodeURIComponent(name)).replace("{level}", String(level)).replace("{x}", String(x)).replace("{y}", String(y));
		const url = this.baseUrl + path;
		return query ? `${url}?${query}` : url;
	}
	/**
	* Get the URL for image info
	*/
	getInfoUrl(name) {
		const path = this.infoUrlPattern.replace("{name}", encodeURIComponent(name));
		return this.baseUrl + path;
	}
	/**
	* Get the URL for a thumbnail
	*/
	getThumbnailUrl(name) {
		const path = this.thumbnailUrlPattern.replace("{name}", encodeURIComponent(name));
		return this.baseUrl + path;
	}
	shouldFetchImageBlobs() {
		const headers = new Headers(this.fetchOptions.headers);
		return headers.has("Authorization") || headers.has("authorization");
	}
	loadImageElementFromSrc(url, signal) {
		return new Promise((resolve) => {
			if (signal?.aborted) {
				resolve(null);
				return;
			}
			const img = new Image();
			const onAbort = () => {
				img.src = "";
				resolve(null);
			};
			signal?.addEventListener("abort", onAbort, { once: true });
			img.onload = () => {
				signal?.removeEventListener("abort", onAbort);
				resolve(img);
			};
			img.onerror = () => {
				signal?.removeEventListener("abort", onAbort);
				resolve(null);
			};
			img.src = url;
		});
	}
	async loadImageElement(url, signal) {
		if (!this.shouldFetchImageBlobs() || typeof URL === "undefined" || typeof URL.createObjectURL !== "function" || typeof URL.revokeObjectURL !== "function") return this.loadImageElementFromSrc(url, signal);
		let response;
		try {
			response = await this.fetchFn(url, {
				...this.fetchOptions,
				signal
			});
		} catch {
			return null;
		}
		if (!response.ok) return null;
		const blob = await response.blob();
		const objectUrl = URL.createObjectURL(blob);
		return new Promise((resolve) => {
			if (signal?.aborted) {
				URL.revokeObjectURL(objectUrl);
				resolve(null);
				return;
			}
			const img = new Image();
			const onAbort = () => {
				URL.revokeObjectURL(objectUrl);
				img.src = "";
				resolve(null);
			};
			signal?.addEventListener("abort", onAbort, { once: true });
			img.onload = () => {
				signal?.removeEventListener("abort", onAbort);
				URL.revokeObjectURL(objectUrl);
				resolve(img);
			};
			img.onerror = () => {
				signal?.removeEventListener("abort", onAbort);
				URL.revokeObjectURL(objectUrl);
				resolve(null);
			};
			img.src = objectUrl;
		});
	}
	/**
	* Fetch image metadata
	*/
	async fetchImageInfo(name) {
		if (this.sourceType === "omezarr") {
			const context = await this.getOrCreateZarrContext(name);
			const info$1 = this.buildInfoFromZarrContext(context);
			this.primedInfo.set(name, info$1);
			return info$1;
		}
		const url = this.getInfoUrl(name);
		const response = await this.fetchFn(url, this.fetchOptions);
		if (!response.ok) throw new Error(`Failed to fetch image info: ${response.statusText}`);
		const info = await response.json();
		info.sourceType ?? (info.sourceType = "tiff");
		this.primedInfo.set(name, info);
		return info;
	}
	/**
	* Return the parsed virdx region metadata (label names + palette) for an
	* OME-Zarr source, or null when it carries no region metadata.
	*/
	async getRegionMetadata(name) {
		if (this.sourceType !== "omezarr") return null;
		try {
			const context = await this.getOrCreateZarrContext(name);
			return context.regions;
		} catch {
			return null;
		}
	}
	/**
	* Read the integer label value at full-resolution image coordinates from the
	* finest level of a label-rendered OME-Zarr source. Returns null when the
	* source isn't a label mask or the point is out of bounds.
	*/
	async readLabelValueAt(name, imageX, imageY) {
		if (this.sourceType !== "omezarr") return null;
		let context;
		try {
			context = await this.getOrCreateZarrContext(name);
		} catch {
			return null;
		}
		if (context.renderMode !== "label") return null;
		const level = context.levels[0];
		if (!level) return null;
		const x = Math.floor(imageX);
		const y = Math.floor(imageY);
		if (x < 0 || y < 0 || x >= level.width || y >= level.height) return null;
		if (level.tileWidth > 0 && level.tileHeight > 0) {
			const cy = Math.floor(y / level.tileHeight);
			const cx = Math.floor(x / level.tileWidth);
			const cached = this.labelChunkCache.get(`${name}:${cy}:${cx}`);
			if (cached) {
				const lx = x - cx * level.tileWidth;
				const ly = y - cy * level.tileHeight;
				return cached.data[ly * cached.width + lx] ?? null;
			}
		}
		const selection = this.buildWindowSelection(context.axes, level.array.shape.length, x, y, x + 1, y + 1, 0, this.getLevelZIndex(context, level));
		const chunk = await this.readChannelChunk(level.array, selection);
		if (!chunk || typeof chunk !== "object" || !("data" in chunk)) return null;
		const raw = chunk.data[0];
		if (raw === void 0) return null;
		if (typeof raw === "bigint") return Number(raw);
		if (typeof raw === "boolean") return raw ? 1 : 0;
		return raw;
	}
	/**
	* Describe a 2-D integer label zarr for in-place editing. Returns null unless
	* the source is a label-mode, 2-D (y,x only) uint8/uint16 array.
	*/
	async getLabelRasterSourceInfo(name) {
		if (this.sourceType !== "omezarr") return null;
		let context;
		try {
			context = await this.getOrCreateZarrContext(name);
		} catch {
			return null;
		}
		return this.buildLabelRasterSourceInfo(context);
	}
	buildLabelRasterSourceInfo(context) {
		if (context.renderMode !== "label") return null;
		if (context.axes.c !== -1 || context.axes.z !== -1) return null;
		const level = context.levels[0];
		if (!level || level.array.shape.length !== 2) return null;
		if (context.dtype !== "uint8" && context.dtype !== "uint16") return null;
		if (level.tileWidth <= 0 || level.tileHeight <= 0) return null;
		return {
			sourceName: context.name,
			arrayPath: level.path,
			shape: [level.height, level.width],
			chunkShape: [level.tileHeight, level.tileWidth],
			dtype: context.dtype,
			fillValue: 0,
			regions: context.regions,
			attrs: {
				taxonomy: context.regions?.taxonomy ?? void 0,
				pipelineVersion: context.regions?.pipelineVersion ?? void 0,
				unknownId: context.regions?.unknownId ?? void 0
			}
		};
	}
	/**
	* Decode one finest-level chunk of a 2-D label zarr as a row-major, clipped
	* label array. Results are LRU-cached (shared with display decoding). Returns
	* null for out-of-range chunks or non-label sources; missing chunks resolve
	* to a fill-value-filled block.
	*/
	async readLabelChunk(name, cy, cx, signal) {
		if (this.sourceType !== "omezarr") return null;
		const key = `${name}:${cy}:${cx}`;
		const cached = this.labelChunkCache.get(key);
		if (cached) {
			this.labelChunkCache.delete(key);
			this.labelChunkCache.set(key, cached);
			return cached;
		}
		const pending = this.labelChunkPending.get(key);
		if (pending) return pending;
		const task = this.decodeLabelChunk(name, cy, cx, signal).finally(() => {
			this.labelChunkPending.delete(key);
		});
		this.labelChunkPending.set(key, task);
		return task;
	}
	async decodeLabelChunk(name, cy, cx, signal) {
		let context;
		try {
			context = await this.getOrCreateZarrContext(name);
		} catch {
			return null;
		}
		const sourceInfo = this.buildLabelRasterSourceInfo(context);
		const level = context.levels[0];
		if (!(sourceInfo && level)) return null;
		const extent = clippedChunkExtent(sourceInfo.shape, sourceInfo.chunkShape, cy, cx);
		if (extent.width <= 0 || extent.height <= 0 || cy < 0 || cx < 0) return null;
		const selection = this.buildWindowSelection(context.axes, level.array.shape.length, extent.x0, extent.y0, extent.x0 + extent.width, extent.y0 + extent.height, 0, 0);
		const chunk = await this.readChannelChunk(level.array, selection, signal);
		let data;
		if (chunk && typeof chunk === "object" && "data" in chunk) data = toLabelArray(toRowMajorYX(chunk.data, extent.width, extent.height, context.xBeforeY), sourceInfo.dtype);
		else {
			if (signal?.aborted) return null;
			data = createLabelArray(sourceInfo.dtype, extent.width * extent.height);
			if (sourceInfo.fillValue !== 0) data.fill(sourceInfo.fillValue);
		}
		const result = {
			cy,
			cx,
			width: extent.width,
			height: extent.height,
			data
		};
		this.storeLabelChunk(name, result);
		return result;
	}
	storeLabelChunk(name, chunk) {
		const key = `${name}:${chunk.cy}:${chunk.cx}`;
		this.labelChunkCache.delete(key);
		this.labelChunkCache.set(key, chunk);
		while (this.labelChunkCache.size > this.labelChunkCacheSize) {
			const oldest = this.labelChunkCache.keys().next().value;
			if (oldest === void 0) break;
			this.labelChunkCache.delete(oldest);
		}
	}
	/**
	* Load a tile
	* @returns Promise that resolves to the loaded image, or null on error
	*/
	async loadTile(name, level, x, y, signal, query) {
		if (this.sourceType === "omezarr") try {
			const indexedLabels = query !== void 0 && new URLSearchParams(query).get("indexedLabels") === "true";
			return await this.loadZarrTile(name, level, x, y, signal, indexedLabels);
		} catch {
			return null;
		}
		return this.loadImageElement(this.getTileUrl(name, level, x, y, query), signal);
	}
	async loadThumbnail(name) {
		if (this.sourceType !== "omezarr") return this.loadImageElement(this.getThumbnailUrl(name));
		let context;
		try {
			context = await this.getOrCreateZarrContext(name);
		} catch {
			return null;
		}
		const selected = resolveSelectedChannels(this.selectedChannels, context.channels);
		const cacheKey = `${name}:${selected.join(",")}`;
		const cached = this.zarrThumbnailCache.get(cacheKey);
		if (cached) return cached;
		const task = this.loadZarrThumbnailImage(context, context.levels[context.levels.length - 1], 0, 0, context.levels[context.levels.length - 1].width, context.levels[context.levels.length - 1].height);
		this.zarrThumbnailCache.set(cacheKey, task);
		return task;
	}
	buildInfoFromZarrContext(context) {
		return {
			name: context.name,
			sourceType: "omezarr",
			channels: [...context.channels],
			renderMode: context.renderMode,
			dtype: context.dtype,
			micronsPerPixel: context.micronsPerPixel,
			levels: context.levels.map((level, index) => ({
				index,
				width: level.width,
				height: level.height,
				tileWidth: level.tileWidth,
				tileHeight: level.tileHeight
			}))
		};
	}
	getZarrRootUrl(name) {
		const base = normalizeBase(this.baseUrl);
		const encodedName = encodeURIComponent(name);
		const raw = `${base}${this.zarrPathPrefix}/${encodedName}`;
		return toAbsoluteUrl(raw);
	}
	async getOrCreateZarrContext(name) {
		const previousError = this.zarrContextErrors.get(name);
		if (previousError) throw previousError;
		const existing = this.zarrContexts.get(name);
		if (existing) return existing;
		const pending = this.openZarrContext(name);
		this.zarrContexts.set(name, pending);
		try {
			const context = await pending;
			this.zarrContextErrors.delete(name);
			return context;
		} catch (error) {
			this.zarrContexts.delete(name);
			const normalized = error instanceof Error ? error : new Error(String(error));
			this.zarrContextErrors.set(name, normalized);
			throw normalized;
		}
	}
	async openZarrContext(name) {
		const rootUrl = this.getZarrRootUrl(name);
		const store = new zarr.FetchStore(rootUrl, { overrides: this.fetchOptions });
		const node = await zarr.open.v3(store);
		const context = node.kind === "array" ? this.buildZarrArrayContext(name, node) : await this.openZarrGroupContext(name, node);
		this.primedInfo.set(name, this.buildInfoFromZarrContext(context));
		return context;
	}
	/**
	* Build a context for a bare single Zarr array (no group / no multiscales),
	* e.g. a 2-D `y,x` segmentation mask. Rendered as a single-level categorical
	* label overlay (integer dtypes) or a grayscale single channel (float dtypes).
	*/
	buildZarrArrayContext(name, array) {
		let axes;
		if (array.shape.length === 2) axes = {
			c: -1,
			z: -1,
			y: 0,
			x: 1
		};
		else if (array.shape.length === 3) axes = {
			c: 0,
			z: -1,
			y: 1,
			x: 2
		};
		else throw new Error("Unsupported bare Zarr array dimensionality: expected 2-D (y,x) or 3-D (c,y,x)");
		const channelCount = axes.c === -1 ? 1 : array.shape[axes.c] ?? 1;
		const { renderMode, channels } = buildZarrChannels(array.dtype, axes.c !== -1, channelCount, null);
		const level = {
			path: "",
			array,
			width: array.shape[axes.x] ?? 0,
			height: array.shape[axes.y] ?? 0,
			tileWidth: array.chunks[axes.x] ?? 0,
			tileHeight: array.chunks[axes.y] ?? 0,
			depth: 1
		};
		return {
			name,
			axes,
			channelCount,
			channels,
			zCount: 1,
			levels: [level],
			renderMode,
			xBeforeY: axes.x < axes.y,
			regions: parseRegionMetadata(array.attrs),
			dtype: array.dtype
		};
	}
	async openZarrGroupContext(name, group) {
		const attrs = group.attrs;
		const omeAttrs = attrs.ome;
		const multiscales = omeAttrs?.multiscales ?? attrs.multiscales;
		if (!Array.isArray(multiscales) || multiscales.length === 0) throw new Error("OME-Zarr metadata missing attributes.multiscales");
		const firstMultiscale = multiscales[0];
		if (!firstMultiscale || typeof firstMultiscale !== "object") throw new Error("Invalid OME-Zarr multiscales metadata");
		const axes = validateAxes(firstMultiscale.axes);
		const datasets = firstMultiscale.datasets;
		if (!Array.isArray(datasets) || datasets.length === 0) throw new Error("OME-Zarr metadata missing multiscale datasets");
		const hasChannelAxis = axes.c !== -1;
		const ndim = Math.max(axes.c, axes.z, axes.y, axes.x) + 1;
		const levels = [];
		let channelCount = 0;
		for (const dataset of datasets) {
			if (!dataset || typeof dataset !== "object") throw new Error("Invalid OME-Zarr dataset entry");
			const path = dataset.path;
			if (typeof path !== "string" || path.length === 0) throw new Error("OME-Zarr dataset path must be a non-empty string");
			const array = await zarr.open.v3(group.resolve(path), { kind: "array" });
			if (array.shape.length !== ndim) throw new Error(`Unsupported OME-Zarr array dimensionality: axes declare ${ndim} dimensions but '${path}' has ${array.shape.length}`);
			if (hasChannelAxis) {
				const levelChannelCount = array.shape[axes.c] ?? 0;
				if (levelChannelCount <= 0) throw new Error("Invalid OME-Zarr channel count");
				if (channelCount === 0) channelCount = levelChannelCount;
				else if (channelCount !== levelChannelCount) throw new Error("Inconsistent OME-Zarr channel count across levels");
			} else channelCount = 1;
			const depth = axes.z >= 0 ? array.shape[axes.z] ?? 0 : 1;
			if (depth <= 0) throw new Error(`Invalid OME-Zarr z depth in '${path}'`);
			levels.push({
				path,
				array,
				width: array.shape[axes.x] ?? 0,
				height: array.shape[axes.y] ?? 0,
				tileWidth: array.chunks[axes.x] ?? 0,
				tileHeight: array.chunks[axes.y] ?? 0,
				depth
			});
		}
		const omero = omeAttrs?.omero ?? attrs.omero;
		const dtype = levels[0].array.dtype;
		const { renderMode, channels } = buildZarrChannels(dtype, hasChannelAxis, channelCount, omero?.channels);
		return {
			name,
			axes,
			channelCount,
			channels,
			zCount: levels[0].depth,
			levels,
			renderMode,
			xBeforeY: axes.x < axes.y,
			regions: parseRegionMetadata(attrs) ?? parseRegionMetadata(levels[0].array.attrs),
			micronsPerPixel: parseMicronsPerPixel(firstMultiscale, datasets, axes),
			dtype
		};
	}
	async loadZarrTile(name, level, x, y, signal, indexedLabels = false) {
		try {
			let context;
			try {
				context = await this.getOrCreateZarrContext(name);
			} catch {
				if (!this.zarrContextErrorLogged.has(name)) this.zarrContextErrorLogged.add(name);
				return null;
			}
			const levelContext = context.levels[level];
			if (!levelContext) return null;
			const xStart = x * levelContext.tileWidth;
			const yStart = y * levelContext.tileHeight;
			const xEnd = Math.min(xStart + levelContext.tileWidth, levelContext.width);
			const yEnd = Math.min(yStart + levelContext.tileHeight, levelContext.height);
			if (xStart >= levelContext.width || yStart >= levelContext.height) return null;
			if (context.renderMode === "label" && level === 0 && this.buildLabelRasterSourceInfo(context)) {
				const chunk = await this.readLabelChunk(name, y, x, signal);
				if (!chunk) return null;
				const rgba = labelToRgba(chunk.width, chunk.height, chunk.data, context.regions?.palette ?? null, indexedLabels);
				return buildTileCanvas(rgba, chunk.width, chunk.height);
			}
			return await this.loadZarrWindow(context, levelContext, xStart, yStart, xEnd, yEnd, signal, indexedLabels);
		} catch {
			return null;
		}
	}
	async loadZarrThumbnailImage(context, levelContext, xStart, yStart, xEnd, yEnd) {
		const source = await this.loadZarrWindow(context, levelContext, xStart, yStart, xEnd, yEnd);
		if (!source) return null;
		if (source instanceof HTMLImageElement) return source;
		if (source instanceof HTMLCanvasElement) return canvasToImage(source);
		return null;
	}
	async loadZarrWindow(context, levelContext, xStart, yStart, xEnd, yEnd, signal, indexedLabels = false) {
		const width = Math.max(0, xEnd - xStart);
		const height = Math.max(0, yEnd - yStart);
		if (width === 0 || height === 0) return null;
		const zIndex = this.getLevelZIndex(context, levelContext);
		if (context.renderMode === "label") return this.loadZarrLabelWindow(context, levelContext, xStart, yStart, xEnd, yEnd, width, height, zIndex, signal, indexedLabels);
		const selected = resolveSelectedChannels(this.selectedChannels, context.channels);
		if (selected.length === 0) return null;
		const channelData = new Map();
		for (const channelIndex of selected) {
			if (channelIndex < 0 || channelIndex >= context.channelCount) continue;
			const selection = this.buildWindowSelection(context.axes, levelContext.array.shape.length, xStart, yStart, xEnd, yEnd, channelIndex, zIndex);
			const chunk = await this.readChannelChunk(levelContext.array, selection, signal);
			if (chunk && typeof chunk === "object" && "data" in chunk) channelData.set(channelIndex, toRowMajorYX(chunk.data, width, height, context.xBeforeY));
		}
		if (channelData.size === 0) return null;
		const rgba = compositeChannelsToRgba(width, height, selected, channelData, context.channels);
		return buildTileCanvas(rgba, width, height);
	}
	/** Build a zarrita selection for a y,x window and optional c/z axes. */
	buildWindowSelection(axes, ndim, xStart, yStart, xEnd, yEnd, channelIndex, zIndex) {
		const selection = new Array(ndim).fill(null);
		selection[axes.y] = zarr.slice(yStart, yEnd);
		selection[axes.x] = zarr.slice(xStart, xEnd);
		if (axes.c >= 0) selection[axes.c] = channelIndex;
		if (axes.z >= 0) selection[axes.z] = zIndex;
		return selection;
	}
	async loadZarrLabelWindow(context, levelContext, xStart, yStart, xEnd, yEnd, width, height, zIndex, signal, indexed = false) {
		const selection = this.buildWindowSelection(context.axes, levelContext.array.shape.length, xStart, yStart, xEnd, yEnd, 0, zIndex);
		const chunk = await this.readChannelChunk(levelContext.array, selection, signal);
		if (!chunk || typeof chunk !== "object" || !("data" in chunk)) return null;
		const rgba = labelToRgba(width, height, toRowMajorYX(chunk.data, width, height, context.xBeforeY), context.regions?.palette ?? null, indexed);
		return buildTileCanvas(rgba, width, height);
	}
	getContextZIndex(context) {
		return Math.max(0, Math.min(context.zCount - 1, Math.floor((context.zCount - 1) / 2)));
	}
	getLevelZIndex(context, level) {
		if (context.axes.z < 0 || level.depth <= 1) return 0;
		return Math.min(level.depth - 1, Math.floor(this.getContextZIndex(context) * level.depth / context.zCount));
	}
	async readChannelChunk(array, selection, signal) {
		try {
			const chunk = await zarr.get(array, selection, { opts: signal ? {
				...this.fetchOptions,
				signal
			} : this.fetchOptions });
			if (chunk && typeof chunk === "object" && "data" in chunk) return chunk;
		} catch {}
		return null;
	}
};

//#endregion
//#region src/tile-scheduler.ts
const DEFAULT_MAX_CONCURRENT = 6;
const DEFAULT_RETRY_ATTEMPTS = 2;
/**
* Priority-based tile download scheduler.
*/
var TileScheduler = class {
	constructor(loader, onLoad, config = {}) {
		this.queue = [];
		this.pending = new Map();
		this.loader = void 0;
		this.onLoad = void 0;
		this.maxConcurrent = void 0;
		this.retryAttempts = void 0;
		this.activeRequests = 0;
		this.destroyed = false;
		this.loader = loader;
		this.onLoad = onLoad;
		this.maxConcurrent = config.maxConcurrent ?? DEFAULT_MAX_CONCURRENT;
		this.retryAttempts = config.retryAttempts ?? DEFAULT_RETRY_ATTEMPTS;
	}
	/**
	* Schedule tile requests. Deduplicates and prioritizes.
	*
	* @param requests Array of tile requests to schedule
	*/
	schedule(requests) {
		if (this.destroyed) return;
		const newRequests = [];
		for (const request of requests) {
			if (this.pending.has(request.key)) {
				const pending = this.pending.get(request.key);
				if (request.priority < pending.request.priority) {
					pending.request.priority = request.priority;
					pending.request.probability = request.probability;
				}
				continue;
			}
			const existingIndex = this.queue.findIndex((r) => r.key === request.key);
			if (existingIndex >= 0) {
				const existing = this.queue[existingIndex];
				if (request.priority < existing.priority) {
					existing.priority = request.priority;
					existing.probability = request.probability;
				}
				continue;
			}
			newRequests.push(request);
		}
		this.queue.push(...newRequests);
		this.sortQueue();
		this.processQueue();
	}
	/**
	* Sort the queue by priority (VISIBLE first, then PREFETCH by probability)
	*/
	sortQueue() {
		this.queue.sort((a, b) => {
			if (a.priority !== b.priority) return a.priority - b.priority;
			if (a.priority === TilePriority.PREFETCH) {
				const probA = a.probability ?? 0;
				const probB = b.probability ?? 0;
				return probB - probA;
			}
			return 0;
		});
	}
	/**
	* Process the queue, starting new requests up to concurrency limit
	*/
	processQueue() {
		if (this.destroyed) return;
		while (this.activeRequests < this.maxConcurrent && this.queue.length > 0) {
			const request = this.queue.shift();
			this.startRequest(request);
		}
	}
	/**
	* Start loading a tile
	*/
	async startRequest(request) {
		if (this.destroyed) return;
		const controller = new AbortController();
		const pending = {
			request,
			retries: 0,
			aborted: false,
			controller
		};
		this.pending.set(request.key, pending);
		this.activeRequests++;
		try {
			const image = await this.loader(request.imageName, request.level, request.x, request.y, controller.signal, request.query, request.clientId);
			if (pending.aborted || this.destroyed) {
				this.activeRequests--;
				this.pending.delete(request.key);
				this.processQueue();
				return;
			}
			this.pending.delete(request.key);
			this.activeRequests--;
			this.onLoad(request, image);
		} catch (error) {
			if (pending.aborted || this.destroyed) {
				this.activeRequests--;
				this.pending.delete(request.key);
				this.processQueue();
				return;
			}
			if (pending.retries < this.retryAttempts) {
				pending.retries++;
				this.queue.unshift(request);
				this.pending.delete(request.key);
				this.activeRequests--;
			} else {
				this.pending.delete(request.key);
				this.activeRequests--;
				this.onLoad(request, null, error instanceof Error ? error : new Error(String(error)));
			}
		}
		this.processQueue();
	}
	/**
	* Cancel all pending prefetch requests.
	* Called when viewport changes rapidly.
	*/
	cancelPrefetch() {
		this.queue = this.queue.filter((r) => r.priority !== TilePriority.PREFETCH);
		for (const pending of this.pending.values()) if (pending.request.priority === TilePriority.PREFETCH) {
			pending.aborted = true;
			pending.controller.abort();
		}
	}
	/**
	* Cancel all pending requests (both visible and prefetch).
	*/
	cancelAll() {
		this.queue = [];
		for (const pending of this.pending.values()) {
			pending.aborted = true;
			pending.controller.abort();
		}
	}
	/**
	* Check if a tile is pending (queued or loading).
	*/
	isPending(key) {
		return this.pending.has(key) || this.queue.some((r) => r.key === key);
	}
	/**
	* Get number of pending requests.
	*/
	getPendingCount() {
		return this.queue.length + this.pending.size;
	}
	/**
	* Get number of active (currently loading) requests.
	*/
	getActiveCount() {
		return this.activeRequests;
	}
	/**
	* Get number of queued (waiting to start) requests.
	*/
	getQueuedCount() {
		return this.queue.length;
	}
	/**
	* Get queue statistics by priority.
	*/
	getStats() {
		let visible = 0;
		let prefetch = 0;
		let recent = 0;
		for (const request of this.queue) switch (request.priority) {
			case TilePriority.VISIBLE:
				visible++;
				break;
			case TilePriority.PREFETCH:
				prefetch++;
				break;
			case TilePriority.RECENT:
				recent++;
				break;
		}
		for (const pending of this.pending.values()) if (!pending.aborted) switch (pending.request.priority) {
			case TilePriority.VISIBLE:
				visible++;
				break;
			case TilePriority.PREFETCH:
				prefetch++;
				break;
			case TilePriority.RECENT:
				recent++;
				break;
		}
		return {
			visible,
			prefetch,
			recent,
			active: this.activeRequests
		};
	}
	/**
	* Clean up scheduler.
	*/
	destroy() {
		this.destroyed = true;
		this.cancelAll();
		this.queue = [];
		this.pending.clear();
	}
};

//#endregion
//#region src/gl-renderer/gl-context.ts
/**
* WebGL2 context initialization and utility functions.
*/
function createWebGL2Context(canvas) {
	const gl = canvas.getContext("webgl2", {
		alpha: false,
		antialias: true,
		stencil: true,
		premultipliedAlpha: false
	});
	if (!gl) throw new Error("WebGL2 is not supported in this browser");
	return gl;
}
function resizeViewport(gl, width, height) {
	gl.viewport(0, 0, width, height);
}
function clearCanvas(gl, bgColor) {
	gl.clearColor(bgColor[0], bgColor[1], bgColor[2], bgColor[3]);
	gl.clear(gl.COLOR_BUFFER_BIT | gl.STENCIL_BUFFER_BIT);
}
/**
* Build a 3x3 orthographic projection matrix mapping pixel coordinates
* to clip space [-1,1]. Y is flipped so (0,0) is top-left.
* Stored column-major as Float32Array for uniform upload.
*/
function orthoProjection(width, height) {
	return new Float32Array([
		2 / width,
		0,
		0,
		0,
		-2 / height,
		0,
		-1,
		1,
		1
	]);
}
const colorCache = new Map();
/**
* Parse a CSS color string (#hex or rgba()) to [r,g,b,a] float tuple.
*/
function parseColor(cssColor) {
	const cached = colorCache.get(cssColor);
	if (cached) return cached;
	let r = 0, g = 0, b = 0, a = 1;
	if (cssColor.startsWith("#")) {
		const hex = cssColor.slice(1);
		if (hex.length === 3) {
			r = parseInt(hex[0] + hex[0], 16) / 255;
			g = parseInt(hex[1] + hex[1], 16) / 255;
			b = parseInt(hex[2] + hex[2], 16) / 255;
		} else if (hex.length === 6) {
			r = parseInt(hex.slice(0, 2), 16) / 255;
			g = parseInt(hex.slice(2, 4), 16) / 255;
			b = parseInt(hex.slice(4, 6), 16) / 255;
		} else if (hex.length === 8) {
			r = parseInt(hex.slice(0, 2), 16) / 255;
			g = parseInt(hex.slice(2, 4), 16) / 255;
			b = parseInt(hex.slice(4, 6), 16) / 255;
			a = parseInt(hex.slice(6, 8), 16) / 255;
		}
	} else if (cssColor.startsWith("rgba(")) {
		const match = cssColor.match(/rgba\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*\)/);
		if (match) {
			r = parseFloat(match[1]) / 255;
			g = parseFloat(match[2]) / 255;
			b = parseFloat(match[3]) / 255;
			a = parseFloat(match[4]);
		}
	} else if (cssColor.startsWith("rgb(")) {
		const match = cssColor.match(/rgb\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*\)/);
		if (match) {
			r = parseFloat(match[1]) / 255;
			g = parseFloat(match[2]) / 255;
			b = parseFloat(match[3]) / 255;
		}
	}
	const result = [
		r,
		g,
		b,
		a
	];
	colorCache.set(cssColor, result);
	return result;
}

//#endregion
//#region src/gl-renderer/polygon-batcher.ts
/**
* Triangulate a polygon with optional holes using earcut.
* Accepts Int32Array interleaved coords or {x,y}[] for screen-space points.
*/
function triangulatePolygon(outer, holes) {
	const coords = [];
	const holeIndices = [];
	if (outer instanceof Int32Array) for (let i = 0; i < outer.length; i++) coords.push(outer[i]);
	else for (const p of outer) coords.push(p.x, p.y);
	if (holes) for (const hole of holes) {
		holeIndices.push(coords.length / 2);
		if (hole instanceof Int32Array) for (let i = 0; i < hole.length; i++) coords.push(hole[i]);
		else for (const p of hole) coords.push(p.x, p.y);
	}
	const indices = earcut(coords, holeIndices.length > 0 ? holeIndices : void 0, 2);
	const vertices = new Float32Array(coords);
	return {
		vertices,
		indices: new Uint32Array(indices)
	};
}
const AA_EXPAND = 1.5;
/**
* Expand a polyline into thick triangle geometry with round joins/caps.
* Each vertex stores (x, y, lineDist, edgeDist) where edgeDist is the
* signed perpendicular distance from the line center (used for AA in the
* fragment shader).
*/
function expandLineStrip(points, width, closed) {
	if (points.length < 2) return {
		vertices: new Float32Array(0),
		indices: new Uint32Array(0)
	};
	const halfW = width / 2;
	const expandedHalf = halfW + AA_EXPAND;
	const verts = [];
	const idxs = [];
	let cumDist = 0;
	const segCount = closed ? points.length : points.length - 1;
	const FAN_SEGMENTS = 8;
	for (let i = 0; i < segCount; i++) {
		const p0 = points[i];
		const p1 = points[(i + 1) % points.length];
		const dx = p1.x - p0.x;
		const dy = p1.y - p0.y;
		const len = Math.sqrt(dx * dx + dy * dy);
		if (len < 1e-8) continue;
		const ux = -dy / len;
		const uy = dx / len;
		const nx = ux * expandedHalf;
		const ny = uy * expandedHalf;
		const baseIdx = verts.length / 4;
		verts.push(p0.x + nx, p0.y + ny, cumDist, expandedHalf);
		verts.push(p0.x - nx, p0.y - ny, cumDist, -expandedHalf);
		const dist1 = cumDist + len;
		verts.push(p1.x + nx, p1.y + ny, dist1, expandedHalf);
		verts.push(p1.x - nx, p1.y - ny, dist1, -expandedHalf);
		idxs.push(baseIdx, baseIdx + 1, baseIdx + 2);
		idxs.push(baseIdx + 1, baseIdx + 3, baseIdx + 2);
		const hasNext = closed || i < segCount - 1;
		if (hasNext && points.length > 2) {
			const p2 = points[(i + 2) % points.length];
			const dx2 = p2.x - p1.x;
			const dy2 = p2.y - p1.y;
			const len2 = Math.sqrt(dx2 * dx2 + dy2 * dy2);
			if (len2 > 1e-8) {
				const angle1 = Math.atan2(uy, ux);
				const ux2 = -dy2 / len2;
				const uy2 = dx2 / len2;
				const angle2 = Math.atan2(uy2, ux2);
				let angleDiff = angle2 - angle1;
				while (angleDiff > Math.PI) angleDiff -= 2 * Math.PI;
				while (angleDiff < -Math.PI) angleDiff += 2 * Math.PI;
				if (Math.abs(angleDiff) > .01) {
					const fanCenter = verts.length / 4;
					verts.push(p1.x, p1.y, dist1, 0);
					const steps = Math.max(2, Math.min(FAN_SEGMENTS, Math.ceil(Math.abs(angleDiff) / (Math.PI / 8))));
					for (let s = 0; s <= steps; s++) {
						const a = angle1 + angleDiff * s / steps;
						verts.push(p1.x + Math.cos(a) * expandedHalf, p1.y + Math.sin(a) * expandedHalf, dist1, expandedHalf);
					}
					for (let s = 0; s < steps; s++) idxs.push(fanCenter, fanCenter + 1 + s, fanCenter + 2 + s);
				}
			}
		}
		cumDist = dist1;
	}
	if (!closed && points.length >= 2) {
		const p0 = points[0];
		const p1 = points[1];
		const dx0 = p1.x - p0.x;
		const dy0 = p1.y - p0.y;
		const len0 = Math.sqrt(dx0 * dx0 + dy0 * dy0);
		if (len0 > 1e-8) {
			const baseAngle = Math.atan2(-(dy0 / len0), dx0 / len0) + Math.PI / 2;
			const centerIdx = verts.length / 4;
			verts.push(p0.x, p0.y, 0, 0);
			for (let s = 0; s <= FAN_SEGMENTS; s++) {
				const a = baseAngle + Math.PI * s / FAN_SEGMENTS;
				verts.push(p0.x + Math.cos(a) * expandedHalf, p0.y + Math.sin(a) * expandedHalf, 0, expandedHalf);
			}
			for (let s = 0; s < FAN_SEGMENTS; s++) idxs.push(centerIdx, centerIdx + 1 + s, centerIdx + 2 + s);
		}
		const pLast = points[points.length - 1];
		const pPrev = points[points.length - 2];
		const dxE = pLast.x - pPrev.x;
		const dyE = pLast.y - pPrev.y;
		const lenE = Math.sqrt(dxE * dxE + dyE * dyE);
		if (lenE > 1e-8) {
			const baseAngle = Math.atan2(-(dyE / lenE), dxE / lenE) - Math.PI / 2;
			const centerIdx = verts.length / 4;
			verts.push(pLast.x, pLast.y, cumDist, 0);
			for (let s = 0; s <= FAN_SEGMENTS; s++) {
				const a = baseAngle + Math.PI * s / FAN_SEGMENTS;
				verts.push(pLast.x + Math.cos(a) * expandedHalf, pLast.y + Math.sin(a) * expandedHalf, cumDist, expandedHalf);
			}
			for (let s = 0; s < FAN_SEGMENTS; s++) idxs.push(centerIdx, centerIdx + 1 + s, centerIdx + 2 + s);
		}
	}
	return {
		vertices: new Float32Array(verts),
		indices: new Uint32Array(idxs)
	};
}
/**
* Generate a unit quad [-1,-1] to [1,1] for circle rendering.
*/
function generateCircleQuad() {
	return {
		vertices: new Float32Array([
			-1,
			-1,
			1,
			-1,
			1,
			1,
			-1,
			1
		]),
		indices: new Uint16Array([
			0,
			1,
			2,
			0,
			2,
			3
		])
	};
}
/**
* Compute bounding box of a set of points.
*/
function computeBBox(points) {
	let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
	if (points instanceof Int32Array) for (let i = 0; i < points.length; i += 2) {
		const x = points[i];
		const y = points[i + 1];
		if (x < minX) minX = x;
		if (y < minY) minY = y;
		if (x > maxX) maxX = x;
		if (y > maxY) maxY = y;
	}
	else for (const p of points) {
		if (p.x < minX) minX = p.x;
		if (p.y < minY) minY = p.y;
		if (p.x > maxX) maxX = p.x;
		if (p.y > maxY) maxY = p.y;
	}
	return {
		minX,
		minY,
		maxX,
		maxY
	};
}

//#endregion
//#region src/gl-renderer/shader-compiler.ts
/**
* WebGL2 shader compilation and program linking utilities.
*/
function compileShader(gl, type, source) {
	const shader = gl.createShader(type);
	if (!shader) throw new Error("Failed to create shader");
	gl.shaderSource(shader, source);
	gl.compileShader(shader);
	if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
		const log = gl.getShaderInfoLog(shader) ?? "unknown error";
		gl.deleteShader(shader);
		throw new Error(`Shader compile error: ${log}`);
	}
	return shader;
}
function linkProgram(gl, vert, frag) {
	const program = gl.createProgram();
	if (!program) throw new Error("Failed to create program");
	gl.attachShader(program, vert);
	gl.attachShader(program, frag);
	gl.linkProgram(program);
	if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
		const log = gl.getProgramInfoLog(program) ?? "unknown error";
		gl.deleteProgram(program);
		throw new Error(`Program link error: ${log}`);
	}
	return program;
}
function getUniformLocations(gl, program, names) {
	const result = {};
	for (const name of names) {
		const loc = gl.getUniformLocation(program, name);
		if (loc === null) throw new Error(`Uniform '${name}' not found in program`);
		result[name] = loc;
	}
	return result;
}
function getAttribLocations(gl, program, names) {
	const result = {};
	for (const name of names) {
		const loc = gl.getAttribLocation(program, name);
		if (loc === -1) throw new Error(`Attribute '${name}' not found in program`);
		result[name] = loc;
	}
	return result;
}

//#endregion
//#region src/gl-renderer/shaders.ts
/**
* GLSL ES 300 shader source code for all rendering programs.
*/
const TILE_VERT = `#version 300 es
precision highp float;
in vec2 a_position;
in vec2 a_texCoord;
uniform mat3 u_projection;
out vec2 v_texCoord;
void main() {
  vec3 pos = u_projection * vec3(a_position, 1.0);
  gl_Position = vec4(pos.xy, 0.0, 1.0);
  v_texCoord = a_texCoord;
}
`;
const TILE_FRAG = `#version 300 es
precision highp float;
in vec2 v_texCoord;
uniform sampler2D u_texture;
out vec4 fragColor;
void main() {
  fragColor = texture(u_texture, v_texCoord);
}
`;
const SOLID_VERT = `#version 300 es
precision highp float;
in vec2 a_position;
uniform mat3 u_projection;
void main() {
  vec3 pos = u_projection * vec3(a_position, 1.0);
  gl_Position = vec4(pos.xy, 0.0, 1.0);
}
`;
const SOLID_FRAG = `#version 300 es
precision highp float;
uniform vec4 u_color;
out vec4 fragColor;
void main() {
  fragColor = u_color;
}
`;
const LINE_VERT = `#version 300 es
precision highp float;
in vec2 a_position;
in float a_lineDist;
in float a_edgeDist;
uniform mat3 u_projection;
out float v_lineDist;
out float v_edgeDist;
void main() {
  vec3 pos = u_projection * vec3(a_position, 1.0);
  gl_Position = vec4(pos.xy, 0.0, 1.0);
  v_lineDist = a_lineDist;
  v_edgeDist = a_edgeDist;
}
`;
const LINE_FRAG = `#version 300 es
precision highp float;
uniform vec4 u_color;
uniform float u_dashLength;
uniform float u_gapLength;
uniform float u_halfWidth;
in float v_lineDist;
in float v_edgeDist;
out vec4 fragColor;
void main() {
  float cycle = u_dashLength + u_gapLength;
  if (cycle > 0.0) {
    float t = mod(v_lineDist, cycle);
    if (t > u_dashLength) discard;
  }
  float edgeAlpha = 1.0 - smoothstep(u_halfWidth - 0.75, u_halfWidth + 0.75, abs(v_edgeDist));
  if (edgeAlpha < 0.004) discard;
  fragColor = vec4(u_color.rgb, u_color.a * edgeAlpha);
}
`;
const CIRCLE_VERT = `#version 300 es
precision highp float;
in vec2 a_position;
uniform mat3 u_projection;
uniform vec2 u_center;
uniform float u_radius;
out vec2 v_localPos;
void main() {
  vec2 worldPos = u_center + a_position * (u_radius + 2.0);
  vec3 pos = u_projection * vec3(worldPos, 1.0);
  gl_Position = vec4(pos.xy, 0.0, 1.0);
  v_localPos = a_position * (u_radius + 2.0);
}
`;
const CIRCLE_FRAG = `#version 300 es
precision highp float;
uniform float u_radius;
uniform vec4 u_fillColor;
uniform vec4 u_strokeColor;
uniform float u_strokeWidth;
in vec2 v_localPos;
out vec4 fragColor;
void main() {
  float dist = length(v_localPos);
  float outerEdge = u_radius + u_strokeWidth * 0.5;
  float innerEdge = u_radius - u_strokeWidth * 0.5;

  if (dist > outerEdge + 1.0) discard;

  float outerAlpha = 1.0 - smoothstep(outerEdge - 0.5, outerEdge + 0.5, dist);
  float innerMask = smoothstep(innerEdge - 0.5, innerEdge + 0.5, dist);

  vec4 fill = u_fillColor;
  vec4 stroke = u_strokeColor;

  // Blend stroke ring over fill
  vec4 color = mix(fill, stroke, innerMask * stroke.a);
  color.a = max(fill.a * (1.0 - innerMask), stroke.a * innerMask);
  color.a *= outerAlpha;

  if (color.a < 0.004) discard;
  fragColor = color;
}
`;
const COLORMAP_FRAG = `#version 300 es
precision highp float;
in vec2 v_texCoord;
uniform sampler2D u_texture;
uniform vec4 u_colormap[256];
uniform float u_opacity;
out vec4 fragColor;
void main() {
  float label = texture(u_texture, v_texCoord).r;
  int index = int(label * 255.0 + 0.5);
  vec4 color = u_colormap[index];
  color.a *= u_opacity;
  if (color.a < 0.004) discard;
  fragColor = color;
}
`;

//#endregion
//#region src/gl-renderer/texture-manager.ts
var TextureManager = class {
	constructor(gl, maxTextures = 600) {
		this.gl = void 0;
		this.textures = new Map();
		this.accessCounter = 0;
		this.maxTextures = void 0;
		this.gl = gl;
		this.maxTextures = maxTextures;
	}
	upload(key, source, nearest = false) {
		const existing = this.textures.get(key);
		if (existing) {
			const gl$1 = this.gl;
			gl$1.bindTexture(gl$1.TEXTURE_2D, existing.texture);
			gl$1.texImage2D(gl$1.TEXTURE_2D, 0, gl$1.RGBA, gl$1.RGBA, gl$1.UNSIGNED_BYTE, source);
			existing.accessOrder = ++this.accessCounter;
			return existing.texture;
		}
		this.evictIfNeeded();
		const gl = this.gl;
		const texture = gl.createTexture();
		if (!texture) throw new Error("Failed to create texture");
		const filter = nearest ? gl.NEAREST : gl.LINEAR;
		gl.bindTexture(gl.TEXTURE_2D, texture);
		gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
		const entry = {
			texture,
			key,
			accessOrder: ++this.accessCounter
		};
		this.textures.set(key, entry);
		return texture;
	}
	/**
	* Upload (or replace) a single-channel 8-bit texture. Used for edited label
	* chunks so the colormap shader can index the palette from `.r`.
	*/
	uploadR8(key, width, height, bytes, nearest = true) {
		const gl = this.gl;
		const existing = this.textures.get(key);
		gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
		if (existing) {
			gl.bindTexture(gl.TEXTURE_2D, existing.texture);
			gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8, width, height, 0, gl.RED, gl.UNSIGNED_BYTE, bytes);
			gl.pixelStorei(gl.UNPACK_ALIGNMENT, 4);
			existing.accessOrder = ++this.accessCounter;
			return existing.texture;
		}
		this.evictIfNeeded();
		const texture = gl.createTexture();
		if (!texture) throw new Error("Failed to create texture");
		const filter = nearest ? gl.NEAREST : gl.LINEAR;
		gl.bindTexture(gl.TEXTURE_2D, texture);
		gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8, width, height, 0, gl.RED, gl.UNSIGNED_BYTE, bytes);
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
		gl.pixelStorei(gl.UNPACK_ALIGNMENT, 4);
		this.textures.set(key, {
			texture,
			key,
			accessOrder: ++this.accessCounter
		});
		return texture;
	}
	/** Patch a sub-rectangle of an existing R8 texture. Returns false when the texture is missing. */
	updateR8Region(key, x, y, width, height, bytes) {
		const entry = this.textures.get(key);
		if (!entry) return false;
		const gl = this.gl;
		gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
		gl.bindTexture(gl.TEXTURE_2D, entry.texture);
		gl.texSubImage2D(gl.TEXTURE_2D, 0, x, y, width, height, gl.RED, gl.UNSIGNED_BYTE, bytes);
		gl.pixelStorei(gl.UNPACK_ALIGNMENT, 4);
		entry.accessOrder = ++this.accessCounter;
		return true;
	}
	get(key) {
		const entry = this.textures.get(key);
		if (!entry) return null;
		entry.accessOrder = ++this.accessCounter;
		return entry.texture;
	}
	has(key) {
		return this.textures.has(key);
	}
	evict(key) {
		const entry = this.textures.get(key);
		if (entry) {
			this.gl.deleteTexture(entry.texture);
			this.textures.delete(key);
		}
	}
	clear() {
		for (const entry of this.textures.values()) this.gl.deleteTexture(entry.texture);
		this.textures.clear();
		this.accessCounter = 0;
	}
	get size() {
		return this.textures.size;
	}
	evictIfNeeded() {
		while (this.textures.size >= this.maxTextures) {
			let oldest = null;
			for (const entry of this.textures.values()) if (!oldest || entry.accessOrder < oldest.accessOrder) oldest = entry;
			if (oldest) {
				this.gl.deleteTexture(oldest.texture);
				this.textures.delete(oldest.key);
			} else break;
		}
	}
};

//#endregion
//#region src/gl-renderer/draw-commands.ts
function getSourceWidth(source, fallback) {
	if ("naturalWidth" in source && source.naturalWidth > 0) return source.naturalWidth;
	if ("width" in source) return source.width;
	return fallback;
}
function getSourceHeight(source, fallback) {
	if ("naturalHeight" in source && source.naturalHeight > 0) return source.naturalHeight;
	if ("height" in source) return source.height;
	return fallback;
}
var GLRenderer = class {
	constructor(canvas) {
		this.canvas = void 0;
		this.gl = void 0;
		this.projection = void 0;
		this.textureManager = void 0;
		this.tileProgram = void 0;
		this.solidProgram = void 0;
		this.lineProgram = void 0;
		this.circleProgram = void 0;
		this.colormapProgram = void 0;
		this.quadVBO = void 0;
		this.quadEBO = void 0;
		this.dynamicVBO = void 0;
		this.dynamicEBO = void 0;
		this.quadVerts = new Float32Array(16);
		this.circleVBO = void 0;
		this.circleEBO = void 0;
		this.tempTexture = null;
		this.contextLost = false;
		this.handleContextLost = (e) => {
			e.preventDefault();
			this.contextLost = true;
		};
		this.handleContextRestored = () => {
			this.contextLost = false;
			const gl$1 = this.gl;
			gl$1.enable(gl$1.BLEND);
			gl$1.blendFunc(gl$1.SRC_ALPHA, gl$1.ONE_MINUS_SRC_ALPHA);
			this.tileProgram = this.createTileProgram();
			this.solidProgram = this.createSolidProgram();
			this.lineProgram = this.createLineProgram();
			this.circleProgram = this.createCircleProgram();
			this.colormapProgram = this.createColormapProgram();
			this.quadVBO = gl$1.createBuffer();
			this.quadEBO = gl$1.createBuffer();
			gl$1.bindBuffer(gl$1.ELEMENT_ARRAY_BUFFER, this.quadEBO);
			gl$1.bufferData(gl$1.ELEMENT_ARRAY_BUFFER, new Uint16Array([
				0,
				1,
				2,
				0,
				2,
				3
			]), gl$1.STATIC_DRAW);
			this.dynamicVBO = gl$1.createBuffer();
			this.dynamicEBO = gl$1.createBuffer();
			const circleQuad$1 = generateCircleQuad();
			this.circleVBO = gl$1.createBuffer();
			gl$1.bindBuffer(gl$1.ARRAY_BUFFER, this.circleVBO);
			gl$1.bufferData(gl$1.ARRAY_BUFFER, circleQuad$1.vertices, gl$1.STATIC_DRAW);
			this.circleEBO = gl$1.createBuffer();
			gl$1.bindBuffer(gl$1.ELEMENT_ARRAY_BUFFER, this.circleEBO);
			gl$1.bufferData(gl$1.ELEMENT_ARRAY_BUFFER, circleQuad$1.indices, gl$1.STATIC_DRAW);
			this.textureManager.clear();
			this.tempTexture = null;
			this.projection = orthoProjection(this.canvas.width, this.canvas.height);
		};
		this.canvas = canvas;
		this.gl = createWebGL2Context(canvas);
		this.projection = orthoProjection(canvas.width, canvas.height);
		this.textureManager = new TextureManager(this.gl);
		const gl = this.gl;
		gl.enable(gl.BLEND);
		gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
		this.tileProgram = this.createTileProgram();
		this.solidProgram = this.createSolidProgram();
		this.lineProgram = this.createLineProgram();
		this.circleProgram = this.createCircleProgram();
		this.colormapProgram = this.createColormapProgram();
		this.quadVBO = gl.createBuffer();
		this.quadEBO = gl.createBuffer();
		gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.quadEBO);
		gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array([
			0,
			1,
			2,
			0,
			2,
			3
		]), gl.STATIC_DRAW);
		this.dynamicVBO = gl.createBuffer();
		this.dynamicEBO = gl.createBuffer();
		const circleQuad = generateCircleQuad();
		this.circleVBO = gl.createBuffer();
		gl.bindBuffer(gl.ARRAY_BUFFER, this.circleVBO);
		gl.bufferData(gl.ARRAY_BUFFER, circleQuad.vertices, gl.STATIC_DRAW);
		this.circleEBO = gl.createBuffer();
		gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.circleEBO);
		gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, circleQuad.indices, gl.STATIC_DRAW);
		canvas.addEventListener("webglcontextlost", this.handleContextLost);
		canvas.addEventListener("webglcontextrestored", this.handleContextRestored);
	}
	createTileProgram() {
		const gl = this.gl;
		const vs = compileShader(gl, gl.VERTEX_SHADER, TILE_VERT);
		const fs = compileShader(gl, gl.FRAGMENT_SHADER, TILE_FRAG);
		const program = linkProgram(gl, vs, fs);
		gl.deleteShader(vs);
		gl.deleteShader(fs);
		return {
			program,
			uniforms: getUniformLocations(gl, program, ["u_projection", "u_texture"]),
			attribs: getAttribLocations(gl, program, ["a_position", "a_texCoord"])
		};
	}
	createSolidProgram() {
		const gl = this.gl;
		const vs = compileShader(gl, gl.VERTEX_SHADER, SOLID_VERT);
		const fs = compileShader(gl, gl.FRAGMENT_SHADER, SOLID_FRAG);
		const program = linkProgram(gl, vs, fs);
		gl.deleteShader(vs);
		gl.deleteShader(fs);
		return {
			program,
			uniforms: getUniformLocations(gl, program, ["u_projection", "u_color"]),
			attribs: getAttribLocations(gl, program, ["a_position"])
		};
	}
	createLineProgram() {
		const gl = this.gl;
		const vs = compileShader(gl, gl.VERTEX_SHADER, LINE_VERT);
		const fs = compileShader(gl, gl.FRAGMENT_SHADER, LINE_FRAG);
		const program = linkProgram(gl, vs, fs);
		gl.deleteShader(vs);
		gl.deleteShader(fs);
		return {
			program,
			uniforms: getUniformLocations(gl, program, [
				"u_projection",
				"u_color",
				"u_dashLength",
				"u_gapLength",
				"u_halfWidth"
			]),
			attribs: getAttribLocations(gl, program, [
				"a_position",
				"a_lineDist",
				"a_edgeDist"
			])
		};
	}
	createCircleProgram() {
		const gl = this.gl;
		const vs = compileShader(gl, gl.VERTEX_SHADER, CIRCLE_VERT);
		const fs = compileShader(gl, gl.FRAGMENT_SHADER, CIRCLE_FRAG);
		const program = linkProgram(gl, vs, fs);
		gl.deleteShader(vs);
		gl.deleteShader(fs);
		return {
			program,
			uniforms: getUniformLocations(gl, program, [
				"u_projection",
				"u_center",
				"u_radius",
				"u_fillColor",
				"u_strokeColor",
				"u_strokeWidth"
			]),
			attribs: getAttribLocations(gl, program, ["a_position"])
		};
	}
	createColormapProgram() {
		const gl = this.gl;
		const vs = compileShader(gl, gl.VERTEX_SHADER, TILE_VERT);
		const fs = compileShader(gl, gl.FRAGMENT_SHADER, COLORMAP_FRAG);
		const program = linkProgram(gl, vs, fs);
		gl.deleteShader(vs);
		gl.deleteShader(fs);
		return {
			program,
			uniforms: getUniformLocations(gl, program, [
				"u_projection",
				"u_texture",
				"u_colormap",
				"u_opacity"
			]),
			attribs: getAttribLocations(gl, program, ["a_position", "a_texCoord"])
		};
	}
	resize(w, h) {
		const gl = this.gl;
		resizeViewport(gl, w, h);
		this.projection = orthoProjection(w, h);
	}
	beginFrame(bgColor) {
		if (this.contextLost) return;
		clearCanvas(this.gl, parseColor(bgColor));
	}
	/**
	* Begin a no-overlap stencil session. All draw calls between
	* beginNoOverlap() and endNoOverlap() will only color each pixel
	* once, preventing alpha accumulation from overlapping geometry.
	*/
	beginNoOverlap() {
		if (this.contextLost) return;
		const gl = this.gl;
		gl.enable(gl.STENCIL_TEST);
		gl.stencilFunc(gl.EQUAL, 0, 255);
		gl.stencilOp(gl.KEEP, gl.KEEP, gl.INCR);
		gl.stencilMask(255);
	}
	/**
	* End a no-overlap stencil session and clear the stencil in the
	* given screen-space bounding box.
	*/
	endNoOverlap(minX, minY, maxX, maxY) {
		if (this.contextLost) return;
		const gl = this.gl;
		const canvasW = this.canvas.width;
		const canvasH = this.canvas.height;
		const bx = Math.max(0, Math.floor(minX));
		const by = Math.max(0, Math.floor(canvasH - maxY));
		const bw = Math.max(0, Math.min(canvasW - bx, Math.ceil(maxX) - bx));
		const bh = Math.max(0, Math.min(canvasH - by, Math.ceil(maxY - minY)));
		gl.enable(gl.SCISSOR_TEST);
		gl.scissor(bx, by, bw, bh);
		gl.stencilMask(255);
		gl.clear(gl.STENCIL_BUFFER_BIT);
		gl.disable(gl.SCISSOR_TEST);
		gl.disable(gl.STENCIL_TEST);
	}
	drawTile(texture, x, y, w, h) {
		if (this.contextLost) return;
		const gl = this.gl;
		const prog = this.tileProgram;
		gl.useProgram(prog.program);
		gl.uniformMatrix3fv(prog.uniforms.u_projection, false, this.projection);
		const v = this.quadVerts;
		v[0] = x;
		v[1] = y;
		v[2] = 0;
		v[3] = 0;
		v[4] = x + w;
		v[5] = y;
		v[6] = 1;
		v[7] = 0;
		v[8] = x + w;
		v[9] = y + h;
		v[10] = 1;
		v[11] = 1;
		v[12] = x;
		v[13] = y + h;
		v[14] = 0;
		v[15] = 1;
		gl.bindBuffer(gl.ARRAY_BUFFER, this.quadVBO);
		gl.bufferData(gl.ARRAY_BUFFER, v, gl.DYNAMIC_DRAW);
		const stride = 4 * 4;
		gl.enableVertexAttribArray(prog.attribs.a_position);
		gl.vertexAttribPointer(prog.attribs.a_position, 2, gl.FLOAT, false, stride, 0);
		gl.enableVertexAttribArray(prog.attribs.a_texCoord);
		gl.vertexAttribPointer(prog.attribs.a_texCoord, 2, gl.FLOAT, false, stride, 8);
		gl.activeTexture(gl.TEXTURE0);
		gl.bindTexture(gl.TEXTURE_2D, texture);
		gl.uniform1i(prog.uniforms.u_texture, 0);
		gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.quadEBO);
		gl.drawElements(gl.TRIANGLES, 6, gl.UNSIGNED_SHORT, 0);
	}
	beginColormapPass(colormap, opacity) {
		if (this.contextLost) return;
		const gl = this.gl;
		const prog = this.colormapProgram;
		gl.useProgram(prog.program);
		gl.uniformMatrix3fv(prog.uniforms.u_projection, false, this.projection);
		gl.uniform4fv(prog.uniforms.u_colormap, colormap);
		gl.uniform1f(prog.uniforms.u_opacity, opacity);
	}
	drawColormapTile(texture, x, y, w, h) {
		if (this.contextLost) return;
		const gl = this.gl;
		const prog = this.colormapProgram;
		const v = this.quadVerts;
		v[0] = x;
		v[1] = y;
		v[2] = 0;
		v[3] = 0;
		v[4] = x + w;
		v[5] = y;
		v[6] = 1;
		v[7] = 0;
		v[8] = x + w;
		v[9] = y + h;
		v[10] = 1;
		v[11] = 1;
		v[12] = x;
		v[13] = y + h;
		v[14] = 0;
		v[15] = 1;
		gl.bindBuffer(gl.ARRAY_BUFFER, this.quadVBO);
		gl.bufferData(gl.ARRAY_BUFFER, v, gl.DYNAMIC_DRAW);
		const stride = 4 * 4;
		gl.enableVertexAttribArray(prog.attribs.a_position);
		gl.vertexAttribPointer(prog.attribs.a_position, 2, gl.FLOAT, false, stride, 0);
		gl.enableVertexAttribArray(prog.attribs.a_texCoord);
		gl.vertexAttribPointer(prog.attribs.a_texCoord, 2, gl.FLOAT, false, stride, 8);
		gl.activeTexture(gl.TEXTURE0);
		gl.bindTexture(gl.TEXTURE_2D, texture);
		gl.uniform1i(prog.uniforms.u_texture, 0);
		gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.quadEBO);
		gl.drawElements(gl.TRIANGLES, 6, gl.UNSIGNED_SHORT, 0);
	}
	drawRect(x, y, w, h, color, alpha = 1) {
		if (this.contextLost) return;
		const gl = this.gl;
		const prog = this.solidProgram;
		gl.useProgram(prog.program);
		gl.uniformMatrix3fv(prog.uniforms.u_projection, false, this.projection);
		const [r, g, b, a] = parseColor(color);
		gl.uniform4f(prog.uniforms.u_color, r, g, b, a * alpha);
		const verts = new Float32Array([
			x,
			y,
			x + w,
			y,
			x + w,
			y + h,
			x,
			y + h
		]);
		gl.bindBuffer(gl.ARRAY_BUFFER, this.quadVBO);
		gl.bufferData(gl.ARRAY_BUFFER, verts, gl.DYNAMIC_DRAW);
		gl.enableVertexAttribArray(prog.attribs.a_position);
		gl.vertexAttribPointer(prog.attribs.a_position, 2, gl.FLOAT, false, 0, 0);
		gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.quadEBO);
		gl.drawElements(gl.TRIANGLES, 6, gl.UNSIGNED_SHORT, 0);
	}
	drawStrokeRect(x, y, w, h, color, lineWidth, alpha = 1) {
		if (this.contextLost) return;
		const lw = lineWidth;
		this.drawRect(x, y, w, lw, color, alpha);
		this.drawRect(x, y + h - lw, w, lw, color, alpha);
		this.drawRect(x, y + lw, lw, h - lw * 2, color, alpha);
		this.drawRect(x + w - lw, y + lw, lw, h - lw * 2, color, alpha);
	}
	/**
	* Fill a polygon with holes using the stencil buffer even-odd technique.
	* Points are in screen coordinates.
	*/
	drawPolygonFill(points, holes, color, opacity) {
		if (this.contextLost || points.length < 3) return;
		const hasHoles = holes && holes.length > 0;
		if (!hasHoles) {
			this.drawSimplePolygonFill(points, color, opacity);
			return;
		}
		const gl = this.gl;
		const { vertices, indices } = triangulatePolygon(points, holes);
		if (indices.length === 0) return;
		gl.enable(gl.STENCIL_TEST);
		gl.colorMask(false, false, false, false);
		gl.stencilFunc(gl.ALWAYS, 0, 255);
		gl.stencilOp(gl.KEEP, gl.KEEP, gl.INVERT);
		gl.stencilMask(255);
		const bbox = computeBBox(points);
		if (holes) for (const hole of holes) {
			const hb = computeBBox(hole);
			if (hb.minX < bbox.minX) bbox.minX = hb.minX;
			if (hb.minY < bbox.minY) bbox.minY = hb.minY;
			if (hb.maxX > bbox.maxX) bbox.maxX = hb.maxX;
			if (hb.maxY > bbox.maxY) bbox.maxY = hb.maxY;
		}
		const prog = this.solidProgram;
		gl.useProgram(prog.program);
		gl.uniformMatrix3fv(prog.uniforms.u_projection, false, this.projection);
		gl.uniform4f(prog.uniforms.u_color, 0, 0, 0, 0);
		gl.bindBuffer(gl.ARRAY_BUFFER, this.dynamicVBO);
		gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.DYNAMIC_DRAW);
		gl.enableVertexAttribArray(prog.attribs.a_position);
		gl.vertexAttribPointer(prog.attribs.a_position, 2, gl.FLOAT, false, 0, 0);
		gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.dynamicEBO);
		gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, indices, gl.DYNAMIC_DRAW);
		gl.drawElements(gl.TRIANGLES, indices.length, gl.UNSIGNED_INT, 0);
		gl.colorMask(true, true, true, true);
		gl.stencilFunc(gl.EQUAL, 1, 1);
		gl.stencilOp(gl.KEEP, gl.KEEP, gl.KEEP);
		const [r, g, b, a] = parseColor(color);
		gl.uniform4f(prog.uniforms.u_color, r, g, b, a * opacity);
		const pad = 1;
		const bx = bbox.minX - pad;
		const by = bbox.minY - pad;
		const bw = bbox.maxX - bbox.minX + pad * 2;
		const bh = bbox.maxY - bbox.minY + pad * 2;
		const bboxVerts = new Float32Array([
			bx,
			by,
			bx + bw,
			by,
			bx + bw,
			by + bh,
			bx,
			by + bh
		]);
		gl.bindBuffer(gl.ARRAY_BUFFER, this.dynamicVBO);
		gl.bufferData(gl.ARRAY_BUFFER, bboxVerts, gl.DYNAMIC_DRAW);
		gl.vertexAttribPointer(prog.attribs.a_position, 2, gl.FLOAT, false, 0, 0);
		gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.quadEBO);
		gl.drawElements(gl.TRIANGLES, 6, gl.UNSIGNED_SHORT, 0);
		gl.enable(gl.SCISSOR_TEST);
		const canvasW = this.canvas.width;
		const canvasH = this.canvas.height;
		const sx = Math.max(0, Math.floor(bx));
		const sy = Math.max(0, Math.floor(canvasH - by - bh));
		const sw = Math.max(0, Math.min(canvasW - sx, Math.ceil(bx + bw) - sx));
		const sh = Math.max(0, Math.min(canvasH - sy, Math.ceil(canvasH - by) - sy));
		gl.scissor(sx, sy, sw, sh);
		gl.stencilMask(255);
		gl.clear(gl.STENCIL_BUFFER_BIT);
		gl.disable(gl.SCISSOR_TEST);
		gl.disable(gl.STENCIL_TEST);
	}
	drawSimplePolygonFill(points, color, opacity) {
		const gl = this.gl;
		const { vertices, indices } = triangulatePolygon(points);
		if (indices.length === 0) return;
		const prog = this.solidProgram;
		gl.useProgram(prog.program);
		gl.uniformMatrix3fv(prog.uniforms.u_projection, false, this.projection);
		const [r, g, b, a] = parseColor(color);
		gl.uniform4f(prog.uniforms.u_color, r, g, b, a * opacity);
		gl.bindBuffer(gl.ARRAY_BUFFER, this.dynamicVBO);
		gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.DYNAMIC_DRAW);
		gl.enableVertexAttribArray(prog.attribs.a_position);
		gl.vertexAttribPointer(prog.attribs.a_position, 2, gl.FLOAT, false, 0, 0);
		gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.dynamicEBO);
		gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, indices, gl.DYNAMIC_DRAW);
		gl.drawElements(gl.TRIANGLES, indices.length, gl.UNSIGNED_INT, 0);
	}
	/**
	* Draw a thick polygon stroke, optionally dashed.
	* Points are in screen coordinates.
	*/
	drawPolygonStroke(points, color, opacity, lineWidth, dash) {
		if (this.contextLost || points.length < 2) return;
		const gl = this.gl;
		const { vertices, indices } = expandLineStrip(points, lineWidth, points.length >= 3);
		if (indices.length === 0) return;
		const prog = this.lineProgram;
		gl.useProgram(prog.program);
		gl.uniformMatrix3fv(prog.uniforms.u_projection, false, this.projection);
		const [r, g, b, a] = parseColor(color);
		gl.uniform4f(prog.uniforms.u_color, r, g, b, a * opacity);
		gl.uniform1f(prog.uniforms.u_dashLength, dash?.dashLength ?? 0);
		gl.uniform1f(prog.uniforms.u_gapLength, dash?.gapLength ?? 0);
		gl.uniform1f(prog.uniforms.u_halfWidth, lineWidth / 2);
		gl.bindBuffer(gl.ARRAY_BUFFER, this.dynamicVBO);
		gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.DYNAMIC_DRAW);
		const stride = 4 * 4;
		gl.enableVertexAttribArray(prog.attribs.a_position);
		gl.vertexAttribPointer(prog.attribs.a_position, 2, gl.FLOAT, false, stride, 0);
		gl.enableVertexAttribArray(prog.attribs.a_lineDist);
		gl.vertexAttribPointer(prog.attribs.a_lineDist, 1, gl.FLOAT, false, stride, 8);
		gl.enableVertexAttribArray(prog.attribs.a_edgeDist);
		gl.vertexAttribPointer(prog.attribs.a_edgeDist, 1, gl.FLOAT, false, stride, 12);
		gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.dynamicEBO);
		gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, indices, gl.DYNAMIC_DRAW);
		gl.drawElements(gl.TRIANGLES, indices.length, gl.UNSIGNED_INT, 0);
	}
	/**
	* Draw a line strip (open polyline) with thick line rendering.
	*/
	drawLineStrip(points, color, opacity, lineWidth, dash) {
		if (this.contextLost || points.length < 2) return;
		const gl = this.gl;
		const { vertices, indices } = expandLineStrip(points, lineWidth, false);
		if (indices.length === 0) return;
		const prog = this.lineProgram;
		gl.useProgram(prog.program);
		gl.uniformMatrix3fv(prog.uniforms.u_projection, false, this.projection);
		const [r, g, b, a] = parseColor(color);
		gl.uniform4f(prog.uniforms.u_color, r, g, b, a * opacity);
		gl.uniform1f(prog.uniforms.u_dashLength, dash?.dashLength ?? 0);
		gl.uniform1f(prog.uniforms.u_gapLength, dash?.gapLength ?? 0);
		gl.uniform1f(prog.uniforms.u_halfWidth, lineWidth / 2);
		gl.bindBuffer(gl.ARRAY_BUFFER, this.dynamicVBO);
		gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.DYNAMIC_DRAW);
		const stride = 4 * 4;
		gl.enableVertexAttribArray(prog.attribs.a_position);
		gl.vertexAttribPointer(prog.attribs.a_position, 2, gl.FLOAT, false, stride, 0);
		gl.enableVertexAttribArray(prog.attribs.a_lineDist);
		gl.vertexAttribPointer(prog.attribs.a_lineDist, 1, gl.FLOAT, false, stride, 8);
		gl.enableVertexAttribArray(prog.attribs.a_edgeDist);
		gl.vertexAttribPointer(prog.attribs.a_edgeDist, 1, gl.FLOAT, false, stride, 12);
		gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.dynamicEBO);
		gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, indices, gl.DYNAMIC_DRAW);
		gl.drawElements(gl.TRIANGLES, indices.length, gl.UNSIGNED_INT, 0);
	}
	/**
	* Draw a line strip where overlapping geometry (joins/caps) does not
	* accumulate alpha. Uses the stencil buffer so each pixel is only
	* colored once.
	*/
	drawLineStripNoOverlap(points, color, opacity, lineWidth) {
		if (this.contextLost || points.length < 2) return;
		const gl = this.gl;
		const { vertices, indices } = expandLineStrip(points, lineWidth, false);
		if (indices.length === 0) return;
		const prog = this.lineProgram;
		gl.useProgram(prog.program);
		gl.uniformMatrix3fv(prog.uniforms.u_projection, false, this.projection);
		const [r, g, b, a] = parseColor(color);
		gl.uniform4f(prog.uniforms.u_color, r, g, b, a * opacity);
		gl.uniform1f(prog.uniforms.u_dashLength, 0);
		gl.uniform1f(prog.uniforms.u_gapLength, 0);
		gl.uniform1f(prog.uniforms.u_halfWidth, lineWidth / 2);
		gl.bindBuffer(gl.ARRAY_BUFFER, this.dynamicVBO);
		gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.DYNAMIC_DRAW);
		const stride = 4 * 4;
		gl.enableVertexAttribArray(prog.attribs.a_position);
		gl.vertexAttribPointer(prog.attribs.a_position, 2, gl.FLOAT, false, stride, 0);
		gl.enableVertexAttribArray(prog.attribs.a_lineDist);
		gl.vertexAttribPointer(prog.attribs.a_lineDist, 1, gl.FLOAT, false, stride, 8);
		gl.enableVertexAttribArray(prog.attribs.a_edgeDist);
		gl.vertexAttribPointer(prog.attribs.a_edgeDist, 1, gl.FLOAT, false, stride, 12);
		gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.dynamicEBO);
		gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, indices, gl.DYNAMIC_DRAW);
		gl.enable(gl.STENCIL_TEST);
		gl.stencilFunc(gl.EQUAL, 0, 255);
		gl.stencilOp(gl.KEEP, gl.KEEP, gl.INCR);
		gl.stencilMask(255);
		gl.drawElements(gl.TRIANGLES, indices.length, gl.UNSIGNED_INT, 0);
		const bbox = computeBBox(points);
		const pad = lineWidth + 2;
		const canvasH = this.canvas.height;
		const bx = Math.max(0, Math.floor(bbox.minX - pad));
		const by = Math.max(0, Math.floor(canvasH - (bbox.maxY + pad)));
		const bw = Math.max(0, Math.ceil(bbox.maxX - bbox.minX + pad * 2));
		const bh = Math.max(0, Math.ceil(bbox.maxY - bbox.minY + pad * 2));
		gl.enable(gl.SCISSOR_TEST);
		gl.scissor(bx, by, bw, bh);
		gl.stencilMask(255);
		gl.clear(gl.STENCIL_BUFFER_BIT);
		gl.disable(gl.SCISSOR_TEST);
		gl.disable(gl.STENCIL_TEST);
	}
	drawCircle(cx, cy, radius, fill, stroke) {
		if (this.contextLost || radius <= 0) return;
		const gl = this.gl;
		const prog = this.circleProgram;
		gl.useProgram(prog.program);
		gl.uniformMatrix3fv(prog.uniforms.u_projection, false, this.projection);
		gl.uniform2f(prog.uniforms.u_center, cx, cy);
		gl.uniform1f(prog.uniforms.u_radius, radius);
		if (fill) {
			const [r, g, b, a] = parseColor(fill.color);
			gl.uniform4f(prog.uniforms.u_fillColor, r, g, b, a * fill.alpha);
		} else gl.uniform4f(prog.uniforms.u_fillColor, 0, 0, 0, 0);
		if (stroke) {
			const [r, g, b, a] = parseColor(stroke.color);
			gl.uniform4f(prog.uniforms.u_strokeColor, r, g, b, a * stroke.alpha);
			gl.uniform1f(prog.uniforms.u_strokeWidth, stroke.width);
		} else {
			gl.uniform4f(prog.uniforms.u_strokeColor, 0, 0, 0, 0);
			gl.uniform1f(prog.uniforms.u_strokeWidth, 0);
		}
		gl.bindBuffer(gl.ARRAY_BUFFER, this.circleVBO);
		gl.enableVertexAttribArray(prog.attribs.a_position);
		gl.vertexAttribPointer(prog.attribs.a_position, 2, gl.FLOAT, false, 0, 0);
		gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.circleEBO);
		gl.drawElements(gl.TRIANGLES, 6, gl.UNSIGNED_SHORT, 0);
	}
	/**
	* Draw an image source (for minimap thumbnail).
	* Uploads to a temporary texture and draws as textured quad.
	*/
	drawImage(source, sx, sy, sw, sh, dx, dy, dw, dh) {
		if (this.contextLost) return;
		const gl = this.gl;
		if (!this.tempTexture) this.tempTexture = gl.createTexture();
		gl.bindTexture(gl.TEXTURE_2D, this.tempTexture);
		gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
		const srcW = getSourceWidth(source, sw);
		const srcH = getSourceHeight(source, sh);
		const u0 = sx / srcW;
		const v0 = sy / srcH;
		const u1 = (sx + sw) / srcW;
		const v1 = (sy + sh) / srcH;
		const prog = this.tileProgram;
		gl.useProgram(prog.program);
		gl.uniformMatrix3fv(prog.uniforms.u_projection, false, this.projection);
		const verts = new Float32Array([
			dx,
			dy,
			u0,
			v0,
			dx + dw,
			dy,
			u1,
			v0,
			dx + dw,
			dy + dh,
			u1,
			v1,
			dx,
			dy + dh,
			u0,
			v1
		]);
		gl.bindBuffer(gl.ARRAY_BUFFER, this.quadVBO);
		gl.bufferData(gl.ARRAY_BUFFER, verts, gl.DYNAMIC_DRAW);
		const stride = 4 * 4;
		gl.enableVertexAttribArray(prog.attribs.a_position);
		gl.vertexAttribPointer(prog.attribs.a_position, 2, gl.FLOAT, false, stride, 0);
		gl.enableVertexAttribArray(prog.attribs.a_texCoord);
		gl.vertexAttribPointer(prog.attribs.a_texCoord, 2, gl.FLOAT, false, stride, 8);
		gl.activeTexture(gl.TEXTURE0);
		gl.bindTexture(gl.TEXTURE_2D, this.tempTexture);
		gl.uniform1i(prog.uniforms.u_texture, 0);
		gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.quadEBO);
		gl.drawElements(gl.TRIANGLES, 6, gl.UNSIGNED_SHORT, 0);
	}
	uploadTileTexture(key, source, nearest = false) {
		return this.textureManager.upload(key, source, nearest);
	}
	getTileTexture(key) {
		return this.textureManager.get(key);
	}
	/** Upload a single-channel (R8) label texture; values are palette indices 0..255. */
	uploadLabelTexture(key, width, height, bytes) {
		return this.textureManager.uploadR8(key, width, height, bytes, true);
	}
	/** Patch a sub-rectangle of a label texture. Returns false if the texture was evicted. */
	updateLabelTextureRegion(key, x, y, width, height, bytes) {
		if (this.contextLost) return false;
		return this.textureManager.updateR8Region(key, x, y, width, height, bytes);
	}
	hasTileTexture(key) {
		return this.textureManager.has(key);
	}
	evictTileTexture(key) {
		this.textureManager.evict(key);
	}
	destroy() {
		const gl = this.gl;
		this.canvas.removeEventListener("webglcontextlost", this.handleContextLost);
		this.canvas.removeEventListener("webglcontextrestored", this.handleContextRestored);
		this.textureManager.clear();
		gl.deleteBuffer(this.quadVBO);
		gl.deleteBuffer(this.quadEBO);
		gl.deleteBuffer(this.dynamicVBO);
		gl.deleteBuffer(this.dynamicEBO);
		gl.deleteBuffer(this.circleVBO);
		gl.deleteBuffer(this.circleEBO);
		if (this.tempTexture) gl.deleteTexture(this.tempTexture);
		gl.deleteProgram(this.tileProgram.program);
		gl.deleteProgram(this.solidProgram.program);
		gl.deleteProgram(this.lineProgram.program);
		gl.deleteProgram(this.circleProgram.program);
		gl.deleteProgram(this.colormapProgram.program);
	}
};

//#endregion
//#region src/minimap.ts
function fitMinimapSize(sourceWidth, sourceHeight, maxWidth, maxHeight) {
	if (sourceWidth <= 0 || sourceHeight <= 0 || maxWidth <= 0 || maxHeight <= 0) return {
		width: 0,
		height: 0
	};
	const scale = Math.min(maxWidth / sourceWidth, maxHeight / sourceHeight);
	return {
		width: sourceWidth * scale,
		height: sourceHeight * scale
	};
}
function computeAnchoredMinimapRect(canvasWidth, canvasHeight, mapWidth, mapHeight, margin, anchor) {
	const clampedWidth = Math.max(0, mapWidth);
	const clampedHeight = Math.max(0, mapHeight);
	const x = anchor === "top-right" || anchor === "bottom-right" ? canvasWidth - clampedWidth - margin : margin;
	const y = anchor === "bottom-left" || anchor === "bottom-right" ? canvasHeight - clampedHeight - margin : margin;
	return {
		x,
		y,
		width: clampedWidth,
		height: clampedHeight
	};
}
function mapViewportToMinimapRect(viewport, imageWidth, imageHeight, minimap) {
	if (imageWidth <= 0 || imageHeight <= 0 || minimap.width <= 0 || minimap.height <= 0) return {
		x: minimap.x,
		y: minimap.y,
		width: 0,
		height: 0
	};
	const scaleX = minimap.width / imageWidth;
	const scaleY = minimap.height / imageHeight;
	const rawLeft = minimap.x + viewport.left * scaleX;
	const rawTop = minimap.y + viewport.top * scaleY;
	const rawRight = minimap.x + viewport.right * scaleX;
	const rawBottom = minimap.y + viewport.bottom * scaleY;
	const minX = minimap.x;
	const minY = minimap.y;
	const maxX = minimap.x + minimap.width;
	const maxY = minimap.y + minimap.height;
	const left = clamp$1(rawLeft, minX, maxX);
	const top = clamp$1(rawTop, minY, maxY);
	const right = clamp$1(rawRight, minX, maxX);
	const bottom = clamp$1(rawBottom, minY, maxY);
	return {
		x: Math.min(left, right),
		y: Math.min(top, bottom),
		width: Math.abs(right - left),
		height: Math.abs(bottom - top)
	};
}
function computeMinimapWorldWindow(imageWidth, imageHeight, centerX, centerY, zoomFactor) {
	if (imageWidth <= 0 || imageHeight <= 0) return {
		left: 0,
		top: 0,
		width: 0,
		height: 0
	};
	const safeZoomFactor = Math.max(1, zoomFactor);
	const width = imageWidth / safeZoomFactor;
	const height = imageHeight / safeZoomFactor;
	const maxLeft = Math.max(0, imageWidth - width);
	const maxTop = Math.max(0, imageHeight - height);
	const left = clamp$1(centerX - width / 2, 0, maxLeft);
	const top = clamp$1(centerY - height / 2, 0, maxTop);
	return {
		left,
		top,
		width,
		height
	};
}
function clamp$1(value, min, max) {
	return Math.max(min, Math.min(max, value));
}

//#endregion
//#region src/sam-crop.ts
async function extractCrop(imageX, imageY, info, level, cropSize, priorityCache, tileClient) {
	const level0 = info.levels[0];
	const levelData = info.levels[level];
	const scaleToLevel0 = level0.width / levelData.width;
	const levelX = imageX / scaleToLevel0;
	const levelY = imageY / scaleToLevel0;
	const halfCrop = cropSize / 2;
	let left = Math.round(levelX - halfCrop);
	let top = Math.round(levelY - halfCrop);
	left = Math.max(0, Math.min(left, levelData.width - cropSize));
	top = Math.max(0, Math.min(top, levelData.height - cropSize));
	const width = Math.min(cropSize, levelData.width - left);
	const height = Math.min(cropSize, levelData.height - top);
	const tileW = levelData.tileWidth;
	const tileH = levelData.tileHeight;
	const tileXStart = Math.floor(left / tileW);
	const tileXEnd = Math.ceil((left + width) / tileW);
	const tileYStart = Math.floor(top / tileH);
	const tileYEnd = Math.ceil((top + height) / tileH);
	const tileImages = [];
	const fetchPromises = [];
	for (let ty = tileYStart; ty < tileYEnd; ty++) for (let tx = tileXStart; tx < tileXEnd; tx++) {
		const key = PriorityCache.getKey(info.name, level, tx, ty);
		const cached = priorityCache.get(key);
		if (cached) tileImages.push({
			img: cached,
			tileX: tx,
			tileY: ty
		});
		else {
			const capturedTx = tx;
			const capturedTy = ty;
			fetchPromises.push(tileClient.loadTile(info.name, level, capturedTx, capturedTy).then((img) => {
				if (img) tileImages.push({
					img,
					tileX: capturedTx,
					tileY: capturedTy
				});
			}));
		}
	}
	if (fetchPromises.length > 0) await Promise.all(fetchPromises);
	const canvas = new OffscreenCanvas(width, height);
	const ctx = canvas.getContext("2d");
	for (const { img, tileX, tileY } of tileImages) {
		const drawX = tileX * tileW - left;
		const drawY = tileY * tileH - top;
		ctx.drawImage(img, drawX, drawY);
	}
	const blob = await canvas.convertToBlob({
		type: "image/jpeg",
		quality: .9
	});
	const arrayBuffer = await blob.arrayBuffer();
	const bytes = new Uint8Array(arrayBuffer);
	const CHUNK = 32768;
	const chunks = [];
	for (let i = 0; i < bytes.length; i += CHUNK) chunks.push(String.fromCharCode(...bytes.subarray(i, i + CHUNK)));
	const b64img = btoa(chunks.join(""));
	return {
		b64img,
		region: {
			level,
			left,
			top,
			width,
			height,
			scaleToLevel0
		}
	};
}

//#endregion
//#region src/velocity-tracker.ts
/**
* Velocity tracking using two-tier exponential moving average filters.
* Based on neuroglancer's approach for predicting viewport movement.
*
* Uses two filters:
* - Short-term (50ms half-life): Responsive to immediate velocity changes
* - Long-term (1000ms half-life): Tracks trends and variance for probability estimation
*/
/** Half-life for short-term velocity filter (milliseconds) */
const SHORT_TERM_HALF_LIFE_MS = 50;
/** Half-life for long-term model filter (milliseconds) */
const LONG_TERM_HALF_LIFE_MS = 1e3;
/** Minimum time delta to prevent division issues */
const MIN_DELTA_MS = 1;
/**
* Compute exponential decay alpha for a given time delta and half-life.
* alpha = 1 - 2^(-deltaT/halfLife)
*
* @param deltaMs Time delta in milliseconds
* @param halfLifeMs Half-life in milliseconds
* @returns Alpha value for exponential moving average
*/
function computeAlpha(deltaMs, halfLifeMs) {
	return 1 - 2 ** (-deltaMs / halfLifeMs);
}
/**
* Create initial velocity state for a dimension
*/
function createVelocityState() {
	return {
		velocity: 0,
		mean: 0,
		variance: 0
	};
}
/**
* Update velocity state for a single dimension using exponential moving averages.
*
* @param state Current velocity state
* @param currentValue Current position value
* @param lastValue Previous position value
* @param deltaMs Time since last update
* @returns Updated velocity state
*/
function updateDimensionVelocity(state, currentValue, lastValue, deltaMs) {
	const instantVelocity = (currentValue - lastValue) / deltaMs;
	const velocityAlpha = computeAlpha(deltaMs, SHORT_TERM_HALF_LIFE_MS);
	const modelAlpha = computeAlpha(deltaMs, LONG_TERM_HALF_LIFE_MS);
	const newVelocity = state.velocity + velocityAlpha * (instantVelocity - state.velocity);
	const delta = newVelocity - state.mean;
	const newMean = state.mean + modelAlpha * delta;
	const newVariance = (1 - modelAlpha) * (state.variance + modelAlpha * delta * delta);
	return {
		velocity: newVelocity,
		mean: newMean,
		variance: newVariance
	};
}
/**
* Tracks viewport velocity using two-tier exponential moving average filters.
* Provides velocity estimates and variance for prefetch probability calculations.
*/
var VelocityTracker = class {
	constructor() {
		this.state = void 0;
		this.lastX = 0;
		this.lastY = 0;
		this.lastZoom = 1;
		this.initialized = false;
		this.state = {
			x: createVelocityState(),
			y: createVelocityState(),
			zoom: createVelocityState(),
			lastTime: 0
		};
	}
	/**
	* Update velocity tracking with new viewport position.
	*
	* @param x Current X position (image coordinates)
	* @param y Current Y position (image coordinates)
	* @param zoom Current zoom level
	* @returns Updated velocity state
	*/
	update(x, y, zoom) {
		const now = performance.now();
		if (!this.initialized) {
			this.lastX = x;
			this.lastY = y;
			this.lastZoom = zoom;
			this.state.lastTime = now;
			this.initialized = true;
			return this.state;
		}
		const deltaMs = Math.max(MIN_DELTA_MS, now - this.state.lastTime);
		this.state.x = updateDimensionVelocity(this.state.x, x, this.lastX, deltaMs);
		this.state.y = updateDimensionVelocity(this.state.y, y, this.lastY, deltaMs);
		this.state.zoom = updateDimensionVelocity(this.state.zoom, zoom, this.lastZoom, deltaMs);
		this.lastX = x;
		this.lastY = y;
		this.lastZoom = zoom;
		this.state.lastTime = now;
		return this.state;
	}
	/**
	* Get current velocity state without updating.
	*/
	getState() {
		return this.state;
	}
	/**
	* Get time since last update in milliseconds.
	*/
	getTimeSinceUpdate() {
		if (!this.initialized) return Infinity;
		return performance.now() - this.state.lastTime;
	}
	/**
	* Check if the viewport is currently moving (has significant velocity).
	*
	* @param threshold Minimum velocity magnitude to consider as moving
	*/
	isMoving(threshold = .001) {
		const vx = this.state.x.velocity;
		const vy = this.state.y.velocity;
		return Math.sqrt(vx * vx + vy * vy) > threshold;
	}
	/**
	* Reset all velocity tracking state.
	*/
	reset() {
		this.state = {
			x: createVelocityState(),
			y: createVelocityState(),
			zoom: createVelocityState(),
			lastTime: 0
		};
		this.lastX = 0;
		this.lastY = 0;
		this.lastZoom = 1;
		this.initialized = false;
	}
	/**
	* Get standard deviation for a dimension's velocity.
	*
	* @param dimension Which dimension to query
	* @returns Standard deviation of velocity for that dimension
	*/
	getStddev(dimension) {
		return Math.sqrt(this.state[dimension].variance);
	}
	/**
	* Get the last known position values.
	*/
	getLastPosition() {
		return {
			x: this.lastX,
			y: this.lastY,
			zoom: this.lastZoom
		};
	}
};

//#endregion
//#region src/tile-viewer.ts
const DEFAULT_MIN_ZOOM = .1;
const DEFAULT_MAX_ZOOM = 200;
const DEFAULT_LEVEL_THRESHOLD = .5;
const DEFAULT_CACHE_SIZE = 500;
const DEFAULT_ANNOTATION_MIN_POINT_DISTANCE_PX = 2;
const DEFAULT_ANNOTATION_MIN_AREA_PX2 = 16;
const DEFAULT_ANNOTATION_EXPORT_PRECISION = 0;
const DEFAULT_ANNOTATION_ERASER_RADIUS_PX = 24;
const MIN_ANNOTATION_ERASER_RADIUS_PX = 1;
const DEFAULT_MAX_ANNOTATION_ERASER_RADIUS_PX = 2048;
const DEFAULT_ANNOTATION_DRAW_RADIUS_PX = 1;
const MIN_ANNOTATION_DRAW_RADIUS_PX = 1;
const DEFAULT_MAX_ANNOTATION_DRAW_RADIUS_PX = 512;
const DEFAULT_OVERLAY_CACHE_SIZE = 400;
const DEFAULT_OVERLAY_MAX_VISIBLE_TILES = 200;
const DEFAULT_LABEL_RASTER_MAX_RADIUS_PX = 2048;
/** Cursor ring colour signalling overwrite mode (amber). */
const LABEL_RASTER_OVERWRITE_COLOR = "#f59e0b";
/** Dim drawn over everything outside the edit regions. */
const EDIT_REGION_DIM_COLOR = "#000000";
const EDIT_REGION_DIM_ALPHA = .55;
const EDIT_REGION_STROKE = "#ffffff";
/** Minimap outlines sit on the light thumbnail, so they are black. */
const EDIT_REGION_MINIMAP_STROKE = "#000000";
const MIN_LABEL_RASTER_RADIUS_PX = .5;
const DEFAULT_LABEL_RASTER_RADIUS_PX = 24;
const DEFAULT_OVERLAY_LABELS = Array.from({ length: 255 }, (_, index) => ({ value: index + 1 }));
/**
* Legend entries for an overlay: background (0, always listed: it is a real
* class annotators may paint, distinct from "unannotated"), every named /
* coloured class, and the declared `unknown_id` ("unannotated", no colour →
* renders transparent) so erased pixels are listed even though production
* palettes omit them. Alias ids are folded into their canonical class's entry
* (`aliases`) instead of listed.
*/
function buildOverlayLabels(regions, aliases) {
	const values = new Set([
		0,
		...regions.names.keys(),
		...regions.palette.keys()
	]);
	if (regions.unknownId !== null) values.add(regions.unknownId);
	for (const canonical of aliases.values()) values.add(canonical);
	return [...values].filter((value) => Number.isInteger(value) && value >= 0 && !aliases.has(value)).sort((a, b) => a - b).map((value) => {
		const label = {
			value,
			name: getRegionLabelName(regions, value),
			color: regions.palette.get(value)
		};
		const collapsed = labelValuesForCanonical(aliases, value).filter((v) => v !== value);
		if (collapsed.length > 0) label.aliases = collapsed;
		return label;
	});
}
function rgbToHex(rgb) {
	return `#${rgb.map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0")).join("")}`;
}
const ANNOTATION_EPSILON = 1e-6;
const DEFAULT_MINIMAP_ANCHOR = "bottom-right";
const MINIMAP_MAX_WIDTH = 180;
const MINIMAP_MAX_HEIGHT = 140;
const MINIMAP_MARGIN = 12;
const MINIMAP_BG_COLOR = "rgba(0, 0, 0, 0.55)";
const MINIMAP_BORDER_COLOR = "rgba(255, 255, 255, 0.9)";
const MINIMAP_VIEWPORT_STROKE = "#4a9eff";
const MINIMAP_VIEWPORT_LINE_WIDTH = 2;
const MINIMAP_ZOOM_GAIN = .12;
const MINIMAP_MAX_ZOOM_FACTOR = 3;
const DEFAULT_THEME = {
	backgroundColor: "#111",
	placeholderColor: "#ffffff",
	placeholderBorderColor: "#333"
};
var TileViewer = class TileViewer extends EventEmitter {
	constructor(canvas, config) {
		super();
		this.canvas = void 0;
		this.gl = void 0;
		this.tileClient = void 0;
		this.priorityCache = void 0;
		this.velocityTracker = void 0;
		this.prefetchManager = void 0;
		this.tileScheduler = void 0;
		this.info = null;
		this.minZoom = void 0;
		this.maxZoom = void 0;
		this.levelThreshold = void 0;
		this.theme = void 0;
		this.minimapEnabled = false;
		this.minimapAnchor = DEFAULT_MINIMAP_ANCHOR;
		this.viewX = 0;
		this.viewY = 0;
		this.zoom = 1;
		this.currentLevel = 0;
		this.isDragging = false;
		this.lastMouseX = 0;
		this.lastMouseY = 0;
		this.mouseImageX = -1;
		this.mouseImageY = -1;
		this.overlaysPeekHidden = false;
		this.annotationDrawEnabled = false;
		this.annotationTool = "pan";
		this.annotationDefaultStyle = { ...DEFAULT_ANNOTATION_STYLE };
		this.annotationDefaultLabel = null;
		this.annotationDefaultGroup = null;
		this.annotationMinPointDistancePx = DEFAULT_ANNOTATION_MIN_POINT_DISTANCE_PX;
		this.annotationMinAreaPx2 = DEFAULT_ANNOTATION_MIN_AREA_PX2;
		this.annotationExportPrecision = DEFAULT_ANNOTATION_EXPORT_PRECISION;
		this.annotationEraserRadiusPx = DEFAULT_ANNOTATION_ERASER_RADIUS_PX;
		this.annotationMaxEraserRadiusPx = DEFAULT_MAX_ANNOTATION_ERASER_RADIUS_PX;
		this.annotationDrawRadiusPx = DEFAULT_ANNOTATION_DRAW_RADIUS_PX;
		this.annotationMaxDrawRadiusPx = DEFAULT_MAX_ANNOTATION_DRAW_RADIUS_PX;
		this.isDrawingAnnotation = false;
		this.isErasingAnnotation = false;
		this.drawingPoints = [];
		this.frozenLoopPoints = null;
		this.drawMergePreview = null;
		this.erasePathPoints = [];
		this.eraseBeforeImageAnnotations = null;
		this.eraseTrackedByOriginalId = new Map();
		this.eraseDidChange = false;
		this.selectedAnnotationId = null;
		this.annotationsById = new Map();
		this.annotationOrderByImage = new Map();
		this.annotationIndex = new AnnotationSpatialIndex();
		this.annotationIdCounter = 0;
		this.annotationUndoStack = [];
		this.annotationRedoStack = [];
		this.samClient = null;
		this.samConfig = {};
		this.samPending = false;
		this.samAbortController = null;
		this.samClickPoint = null;
		this.samClickTime = 0;
		this.samDragStart = null;
		this.samDragEnd = null;
		this.isSamDragging = false;
		this.minimapThumbnail = null;
		this.minimapThumbnailLoading = false;
		this.minimapThumbnailFailed = false;
		this.minimapThumbnailLoadId = 0;
		this.tileFailureCounts = new Map();
		this.tileFailureBackoffUntil = new Map();
		this.tileFailureMaxBackoffMs = 3e4;
		this.tileClientConfig = void 0;
		this.overlays = new Map();
		this.overlayOrder = [];
		this.overlayCaches = new Map();
		this.overlayClients = new Map();
		this.overlayMetadataColormaps = new Map();
		this.overlayRenderedColormaps = new Map();
		this.overlayLabelOpacities = new Map();
		this.overlayRegions = new Map();
		this.overlayLabelAliasResolvers = new Map();
		this.overlayLabelAliases = new Map();
		this.overlayColormapOverrides = new Set();
		this.labelRasterConfig = void 0;
		this.labelRasterSessions = new Map();
		this.labelRasterLoadWaits = new Set();
		this.labelRasterRenderScheduled = false;
		this.labelRasterEditTextureKeys = new Map();
		this.labelRasterTool = null;
		this.labelRasterBrushRadiusPx = DEFAULT_LABEL_RASTER_RADIUS_PX;
		this.labelRasterMaxRadiusPx = DEFAULT_LABEL_RASTER_MAX_RADIUS_PX;
		this.isLabelStroking = false;
		this.labelStrokeOverlayId = null;
		this.labelFillOverlayId = null;
		this.editRegions = null;
		this.editRegionsHidden = false;
		this.annotationCursorOutsideRegion = null;
		this.boundHandlers = void 0;
		this.canvas = canvas;
		if (!this.canvas.hasAttribute("tabindex")) this.canvas.tabIndex = 0;
		this.canvas.style.outline = "none";
		this.gl = new GLRenderer(canvas);
		this.tileClientConfig = config.tileClient;
		this.tileClient = new TileClient(config.tileClient);
		this.labelRasterConfig = config.labelRaster ?? {};
		this.labelRasterMaxRadiusPx = Math.max(MIN_LABEL_RASTER_RADIUS_PX, config.labelRaster?.maxBrushRadiusPx ?? DEFAULT_LABEL_RASTER_MAX_RADIUS_PX);
		this.minZoom = config.minZoom ?? DEFAULT_MIN_ZOOM;
		this.maxZoom = config.maxZoom ?? DEFAULT_MAX_ZOOM;
		this.levelThreshold = config.levelThreshold ?? DEFAULT_LEVEL_THRESHOLD;
		this.theme = {
			...DEFAULT_THEME,
			...config.theme
		};
		this.minimapEnabled = Boolean(config.minimap?.enabled);
		this.minimapAnchor = config.minimap?.anchor ?? DEFAULT_MINIMAP_ANCHOR;
		if (config.annotation) {
			this.annotationDrawEnabled = Boolean(config.annotation.enabled);
			this.annotationTool = this.annotationDrawEnabled ? "draw" : "pan";
			this.annotationDefaultStyle = normalizeStyle(config.annotation.defaultStyle ?? {}, DEFAULT_ANNOTATION_STYLE);
			this.annotationDefaultLabel = config.annotation.defaultLabel ?? null;
			this.annotationDefaultGroup = config.annotation.defaultGroup ?? null;
			this.annotationMinPointDistancePx = config.annotation.minPointDistancePx ?? DEFAULT_ANNOTATION_MIN_POINT_DISTANCE_PX;
			this.annotationMinAreaPx2 = config.annotation.minAreaPx2 ?? DEFAULT_ANNOTATION_MIN_AREA_PX2;
			this.annotationExportPrecision = Math.max(0, Math.floor(config.annotation.exportPrecision ?? DEFAULT_ANNOTATION_EXPORT_PRECISION));
			this.annotationEraserRadiusPx = Math.max(MIN_ANNOTATION_ERASER_RADIUS_PX, Math.min(this.annotationMaxEraserRadiusPx, config.annotation.eraserRadiusPx ?? DEFAULT_ANNOTATION_ERASER_RADIUS_PX));
		}
		if (config.sam) {
			this.samConfig = config.sam;
			this.samClient = this.createSamClient(config.sam);
		}
		this.priorityCache = new PriorityCache({
			maxTiles: config.cache?.maxTiles ?? DEFAULT_CACHE_SIZE,
			visibleReserve: config.cache?.visibleReserve,
			prefetchReserve: config.cache?.prefetchReserve
		});
		this.velocityTracker = new VelocityTracker();
		this.prefetchManager = new PrefetchManager(config.prefetch);
		this.tileScheduler = new TileScheduler((imageName, level, x, y, signal, query, clientId) => {
			const client = (clientId ? this.overlayClients.get(clientId) : void 0) ?? this.tileClient;
			return client.loadTile(imageName, level, x, y, signal, query);
		}, (request, image, error) => this.handleTileLoaded(request, image, error), config.scheduler);
		this.boundHandlers = {
			mousedown: this.handleMouseDown.bind(this),
			mousemove: this.handleMouseMove.bind(this),
			mouseup: this.handleMouseUp.bind(this),
			canvasMousemove: this.handleCanvasMouseMove.bind(this),
			mouseleave: this.handleMouseLeave.bind(this),
			contextmenu: this.handleContextMenu.bind(this),
			wheel: this.handleWheel.bind(this),
			resize: this.handleResize.bind(this),
			keydown: this.handleKeyDown.bind(this)
		};
		this.setupEventListeners();
		this.resize();
		this.emit("annotation-draw-state-change", {
			enabled: this.annotationDrawEnabled,
			drawing: false
		});
		this.emit("annotation-tool-change", {
			tool: this.annotationTool,
			eraserRadiusPx: this.annotationEraserRadiusPx,
			drawRadiusPx: this.annotationDrawRadiusPx
		});
	}
	setupEventListeners() {
		this.canvas.addEventListener("mousedown", this.boundHandlers.mousedown);
		window.addEventListener("mousemove", this.boundHandlers.mousemove);
		window.addEventListener("mouseup", this.boundHandlers.mouseup);
		this.canvas.addEventListener("mousemove", this.boundHandlers.canvasMousemove);
		this.canvas.addEventListener("mouseleave", this.boundHandlers.mouseleave);
		this.canvas.addEventListener("contextmenu", this.boundHandlers.contextmenu);
		this.canvas.addEventListener("wheel", this.boundHandlers.wheel);
		window.addEventListener("resize", this.boundHandlers.resize);
		this.canvas.addEventListener("keydown", this.boundHandlers.keydown);
	}
	handleMouseDown(e) {
		if (!this.info) return;
		const imagePoint = this.getImagePointFromClient(e.clientX, e.clientY);
		if (!imagePoint) return;
		if (e.button === 2) {
			this.isDragging = true;
			this.lastMouseX = e.clientX;
			this.lastMouseY = e.clientY;
			this.emit("drag-start");
			return;
		}
		if (e.button !== 0) return;
		if (this.labelRasterTool && this.beginLabelRasterInteraction(imagePoint)) return;
		if (this.annotationDrawEnabled && this.annotationTool === "draw") {
			if (this.rejectAnnotationPressOutsideRegions(imagePoint)) return;
			this.isDrawingAnnotation = true;
			this.drawingPoints = [imagePoint];
			this.frozenLoopPoints = null;
			this.drawMergePreview = null;
			this.emit("annotation-draw-state-change", {
				enabled: true,
				drawing: true
			});
			this.render();
			return;
		}
		if (this.annotationDrawEnabled && this.annotationTool === "erase") {
			this.startAnnotationErase(imagePoint);
			this.render();
			return;
		}
		if (this.annotationDrawEnabled && this.annotationTool === "sam") {
			if (!this.samPending && this.annotationDefaultLabel) {
				if (this.rejectAnnotationPressOutsideRegions(imagePoint)) return;
				this.samDragStart = imagePoint;
				this.samDragEnd = null;
				this.isSamDragging = false;
			}
			return;
		}
		const hitId = this.hitTestAnnotation(imagePoint);
		if (hitId) {
			this.selectAnnotation(hitId);
			return;
		}
		this.selectAnnotation(null);
		this.isDragging = true;
		this.lastMouseX = e.clientX;
		this.lastMouseY = e.clientY;
		this.emit("drag-start");
	}
	handleMouseMove(e) {
		if (this.isLabelStroking && this.info) {
			const imagePoint = this.getImagePointFromClient(e.clientX, e.clientY);
			if (!imagePoint) return;
			this.continueLabelRasterStroke(imagePoint);
			return;
		}
		if (this.isDrawingAnnotation && this.info) {
			const imagePoint = this.getImagePointFromClient(e.clientX, e.clientY);
			if (!imagePoint) return;
			const previous = this.drawingPoints[this.drawingPoints.length - 1];
			const useRadiusBrush = this.annotationDrawRadiusPx > 1;
			const minDistance = useRadiusBrush ? Math.max(.25, Math.min(2, this.annotationDrawRadiusPx * .05)) / this.getScale() : this.annotationMinPointDistancePx / this.getScale();
			const minDistanceSq = minDistance * minDistance;
			if (!previous) this.drawingPoints.push(imagePoint);
			else {
				const dx$1 = imagePoint.x - previous.x;
				const dy$1 = imagePoint.y - previous.y;
				if (dx$1 * dx$1 + dy$1 * dy$1 >= minDistanceSq) this.drawingPoints.push(imagePoint);
			}
			if (!useRadiusBrush) {
				if (!this.frozenLoopPoints && this.drawingPoints.length >= 4) {
					const buf = fromPointArray(this.drawingPoints);
					const intersection = findFirstSelfIntersection(buf);
					if (intersection) {
						this.frozenLoopPoints = extractClosedLoop(buf, intersection);
						this.updateDrawingMergePreview();
					}
				}
				if (this.frozenLoopPoints) this.updateDrawingMergePreview();
			}
			this.render();
			return;
		}
		if (this.isErasingAnnotation && this.info) {
			const imagePoint = this.getImagePointFromClient(e.clientX, e.clientY);
			if (!imagePoint) return;
			this.updateAnnotationErase(imagePoint);
			this.render();
			return;
		}
		if (this.samDragStart && !this.samPending && this.info) {
			const imagePoint = this.getImagePointFromClient(e.clientX, e.clientY);
			if (imagePoint) {
				const dx$1 = imagePoint.x - this.samDragStart.x;
				const dy$1 = imagePoint.y - this.samDragStart.y;
				const scale$1 = this.getScale();
				if (!this.isSamDragging && (dx$1 * dx$1 + dy$1 * dy$1) * scale$1 * scale$1 > 25) this.isSamDragging = true;
				if (this.isSamDragging) {
					this.samDragEnd = imagePoint;
					this.render();
				}
			}
			return;
		}
		if (!(this.isDragging && this.info)) return;
		const dx = e.clientX - this.lastMouseX;
		const dy = e.clientY - this.lastMouseY;
		this.lastMouseX = e.clientX;
		this.lastMouseY = e.clientY;
		const scale = this.getScale();
		this.viewX -= dx / scale;
		this.viewY -= dy / scale;
		this.clampView();
		this.render();
		this.emitViewportChange();
	}
	handleMouseUp() {
		if (this.isLabelStroking) {
			this.finishLabelRasterStroke();
			return;
		}
		if (this.isDrawingAnnotation) {
			this.finalizeAnnotationDrawing();
			return;
		}
		if (this.isErasingAnnotation) {
			this.finalizeAnnotationErase();
			return;
		}
		if (this.samDragStart && !this.samPending) {
			const start = this.samDragStart;
			const end = this.samDragEnd;
			this.samDragStart = null;
			if (this.isSamDragging && end) {
				const x1 = Math.min(start.x, end.x);
				const y1 = Math.min(start.y, end.y);
				const x2 = Math.max(start.x, end.x);
				const y2 = Math.max(start.y, end.y);
				const cx = (x1 + x2) / 2;
				const cy = (y1 + y2) / 2;
				this.handleSamClick({
					x: cx,
					y: cy
				}, {
					x1,
					y1,
					x2,
					y2
				});
			} else {
				this.samDragEnd = null;
				this.handleSamClick(start);
			}
			this.isSamDragging = false;
			return;
		}
		if (this.isDragging) {
			this.isDragging = false;
			this.emit("drag-end");
		}
	}
	handleCanvasMouseMove(e) {
		if (!this.info) return;
		const rect = this.canvas.getBoundingClientRect();
		const screenX = e.clientX - rect.left;
		const screenY = e.clientY - rect.top;
		const scale = this.getScale();
		this.mouseImageX = this.viewX + (screenX - this.canvas.width / 2) / scale;
		this.mouseImageY = this.viewY + (screenY - this.canvas.height / 2) / scale;
		this.emit("pointer-move", {
			screenX,
			screenY,
			imageX: this.mouseImageX,
			imageY: this.mouseImageY
		});
		if (this.labelRasterTool) {
			this.prefetchLabelRasterAround(this.mouseImageX, this.mouseImageY);
			if (this.editRegions) this.applyEditCursorStyle();
			this.render();
			return;
		}
		if (this.editRegions) this.updateAnnotationRegionCursor();
		if (this.annotationDrawEnabled && this.needsCursorOverlay()) this.render();
	}
	handleMouseLeave() {
		const hadCursor = Boolean(this.labelRasterTool) || this.annotationDrawEnabled && this.needsCursorOverlay();
		this.mouseImageX = -1;
		this.mouseImageY = -1;
		this.updateAnnotationRegionCursor();
		this.emit("pointer-leave");
		if (hadCursor) this.render();
	}
	needsCursorOverlay() {
		return this.annotationTool === "erase" || this.annotationTool === "draw" && this.annotationDrawRadiusPx > 1 || this.annotationTool === "sam" && (this.isSamDragging || this.samPending);
	}
	handleContextMenu(e) {
		e.preventDefault();
	}
	handleKeyDown(e) {
		if (this.labelRasterTool && this.mouseImageX >= 0 && this.mouseImageY >= 0) {
			if (e.metaKey || e.ctrlKey || e.altKey) return;
			const key = e.key.toLowerCase();
			if (key === "o") {
				if (!e.repeat) this.setLabelRasterOverwrite(!this.labelRasterTool.overwrite);
				return;
			}
			const current = this.labelRasterTool.tool;
			const fillMode = isLabelRasterFillTool(current);
			const nextTool = key === "b" ? withLabelRasterFillMode("brush", fillMode) : key === "e" ? withLabelRasterFillMode("erase", fillMode) : key === "i" ? "pick" : key === "f" ? withLabelRasterFillMode(labelRasterToolBase(current), !fillMode) : null;
			if (nextTool && nextTool !== this.labelRasterTool.tool) this.setLabelRasterTool({
				...this.labelRasterTool,
				tool: nextTool
			});
			return;
		}
		if (this.annotationDrawEnabled && this.mouseImageX >= 0 && this.mouseImageY >= 0) {
			if (e.key === "e" || e.key === "E") this.setAnnotationTool("erase");
			else if (e.key === "d" || e.key === "D") this.setAnnotationTool("draw");
		}
	}
	handleWheel(e) {
		e.preventDefault();
		if (!this.info) return;
		if ((e.ctrlKey || e.metaKey) && this.labelRasterTool) {
			const factor = e.deltaY > 0 ? .9 : 1.1;
			this.setLabelRasterBrushRadiusPx(this.labelRasterBrushRadiusPx * factor);
			return;
		}
		if ((e.ctrlKey || e.metaKey) && this.annotationDrawEnabled) {
			const factor = e.deltaY > 0 ? .9 : 1.1;
			if (this.annotationTool === "erase") this.setAnnotationEraserRadiusPx(this.annotationEraserRadiusPx * factor);
			else if (this.annotationTool === "draw") this.setAnnotationDrawRadiusPx(this.annotationDrawRadiusPx * factor);
			return;
		}
		const rect = this.canvas.getBoundingClientRect();
		const mouseX = e.clientX - rect.left;
		const mouseY = e.clientY - rect.top;
		const scale = this.getScale();
		const imgX = this.viewX + (mouseX - this.canvas.width / 2) / scale;
		const imgY = this.viewY + (mouseY - this.canvas.height / 2) / scale;
		const zoomFactor = e.deltaY > 0 ? .9 : 1.1;
		this.zoom = Math.max(this.minZoom, Math.min(this.maxZoom, this.zoom * zoomFactor));
		const newScale = this.getScale();
		this.viewX = imgX - (mouseX - this.canvas.width / 2) / newScale;
		this.viewY = imgY - (mouseY - this.canvas.height / 2) / newScale;
		this.clampView();
		this.updateLevel();
		this.render();
		this.emitViewportChange();
	}
	handleResize() {
		this.resize();
	}
	emitViewportChange() {
		if (!this.info) return;
		const velocityState = this.velocityTracker.update(this.viewX, this.viewY, this.zoom);
		Logger.velocity(velocityState.x.velocity, velocityState.y.velocity, velocityState.x.variance, velocityState.y.variance);
		const scale = this.getScale();
		const halfWidth = this.canvas.width / 2 / scale;
		const halfHeight = this.canvas.height / 2 / scale;
		const event = {
			zoom: this.zoom,
			centerX: this.viewX,
			centerY: this.viewY,
			level: this.currentLevel,
			viewport: {
				left: this.viewX - halfWidth,
				top: this.viewY - halfHeight,
				right: this.viewX + halfWidth,
				bottom: this.viewY + halfHeight
			}
		};
		this.emit("viewport-change", event);
	}
	/**
	* Resize canvas to fill parent container
	*/
	resize() {
		const parent = this.canvas.parentElement;
		if (parent) {
			const rect = parent.getBoundingClientRect();
			this.canvas.width = rect.width;
			this.canvas.height = rect.height;
			this.gl.resize(rect.width, rect.height);
		}
		if (this.info) this.render();
	}
	resetMinimapThumbnailState() {
		this.minimapThumbnail = null;
		this.minimapThumbnailLoading = false;
		this.minimapThumbnailFailed = false;
		this.minimapThumbnailLoadId += 1;
	}
	loadMinimapThumbnail() {
		if (!(this.minimapEnabled && this.info) || this.minimapThumbnail || this.minimapThumbnailLoading || this.minimapThumbnailFailed) return;
		if (this.info.sourceType === "omezarr") {
			this.minimapThumbnailFailed = true;
			return;
		}
		const imageName = this.info.name;
		const loadId = this.minimapThumbnailLoadId + 1;
		this.minimapThumbnailLoadId = loadId;
		this.minimapThumbnailLoading = true;
		this.tileClient.loadThumbnail(imageName).then((img) => {
			if (loadId !== this.minimapThumbnailLoadId || this.info?.name !== imageName) return;
			if (!img) {
				this.minimapThumbnailLoading = false;
				this.minimapThumbnailFailed = true;
				this.render();
				return;
			}
			this.minimapThumbnail = img;
			this.minimapThumbnailLoading = false;
			this.minimapThumbnailFailed = false;
			this.render();
		}).catch(() => {
			if (loadId !== this.minimapThumbnailLoadId || this.info?.name !== imageName) return;
			this.minimapThumbnailLoading = false;
			this.minimapThumbnailFailed = true;
			this.render();
		});
	}
	getScale() {
		if (!this.info) return 1;
		const level0 = this.info.levels[0];
		const fitScale = Math.min(this.canvas.width / level0.width, this.canvas.height / level0.height);
		return fitScale * this.zoom;
	}
	clampView() {
		if (!this.info) return;
		const level0 = this.info.levels[0];
		const scale = this.getScale();
		const halfViewWidth = this.canvas.width / 2 / scale;
		const halfViewHeight = this.canvas.height / 2 / scale;
		this.viewX = Math.max(-halfViewWidth, Math.min(level0.width + halfViewWidth, this.viewX));
		this.viewY = Math.max(-halfViewHeight, Math.min(level0.height + halfViewHeight, this.viewY));
	}
	updateLevel() {
		if (!this.info) return;
		const scale = this.getScale();
		const level0 = this.info.levels[0];
		let bestLevel = this.info.levels.length - 1;
		for (let i = 0; i < this.info.levels.length; i++) {
			const level = this.info.levels[i];
			const levelScale = level0.width / level.width;
			const effectiveScale = scale * levelScale;
			if (effectiveScale >= this.levelThreshold) {
				bestLevel = i;
				break;
			}
		}
		this.currentLevel = bestLevel;
	}
	getTileKey(level, x, y) {
		return PriorityCache.getKey(this.info?.name ?? "", level, x, y);
	}
	getImagePointFromClient(clientX, clientY) {
		if (!this.info) return null;
		const rect = this.canvas.getBoundingClientRect();
		const screenX = clientX - rect.left;
		const screenY = clientY - rect.top;
		const scale = this.getScale();
		const level0 = this.info.levels[0];
		return {
			x: Math.max(0, Math.min(level0.width, this.viewX + (screenX - this.canvas.width / 2) / scale)),
			y: Math.max(0, Math.min(level0.height, this.viewY + (screenY - this.canvas.height / 2) / scale))
		};
	}
	getOrderForImage(imageName) {
		let order = this.annotationOrderByImage.get(imageName);
		if (!order) {
			order = [];
			this.annotationOrderByImage.set(imageName, order);
		}
		return order;
	}
	emitAnnotationHistoryChange() {
		this.emit("annotation-history-change", {
			canUndo: this.annotationUndoStack.length > 0,
			canRedo: this.annotationRedoStack.length > 0
		});
	}
	pushAnnotationHistory(action) {
		this.annotationUndoStack.push(action);
		this.annotationRedoStack.length = 0;
		this.emitAnnotationHistoryChange();
	}
	createAnnotationId() {
		if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
		this.annotationIdCounter += 1;
		return `annotation-${Date.now()}-${this.annotationIdCounter}`;
	}
	addAnnotationInternal(annotation, emitEvent) {
		const sanitized = sanitizeAnnotation(annotation);
		const existing = this.annotationsById.get(sanitized.id);
		if (existing) {
			if (existing.imageName !== sanitized.imageName) {
				const previousOrder = this.annotationOrderByImage.get(existing.imageName);
				if (previousOrder) {
					const index = previousOrder.indexOf(existing.id);
					if (index >= 0) previousOrder.splice(index, 1);
				}
			}
		}
		this.annotationsById.set(sanitized.id, sanitized);
		this.annotationIndex.upsert(sanitized);
		const order = this.getOrderForImage(sanitized.imageName);
		if (!order.includes(sanitized.id)) order.push(sanitized.id);
		if (emitEvent) if (existing) this.emit("annotation-updated", { annotation: cloneAnnotation(sanitized) });
		else this.emit("annotation-created", { annotation: cloneAnnotation(sanitized) });
	}
	removeAnnotationInternal(id, emitEvent) {
		const existing = this.annotationsById.get(id);
		if (!existing) return null;
		this.annotationsById.delete(id);
		this.annotationIndex.remove(id);
		const order = this.annotationOrderByImage.get(existing.imageName);
		if (order) {
			const index = order.indexOf(id);
			if (index >= 0) order.splice(index, 1);
			if (order.length === 0) this.annotationOrderByImage.delete(existing.imageName);
		}
		if (this.selectedAnnotationId === id) {
			this.selectedAnnotationId = null;
			this.emit("annotation-selected", { id: null });
		}
		if (emitEvent) this.emit("annotation-deleted", {
			id,
			imageName: existing.imageName
		});
		return cloneAnnotation(existing);
	}
	replaceAnnotationsForImages(imageNames, annotations) {
		const imageNameSet = new Set(imageNames);
		for (const imageName of imageNameSet) {
			const existingOrder = [...this.annotationOrderByImage.get(imageName) ?? []];
			for (const id of existingOrder) this.removeAnnotationInternal(id, false);
		}
		for (const annotation of annotations) this.addAnnotationInternal(annotation, false);
		if (this.selectedAnnotationId && !this.annotationsById.has(this.selectedAnnotationId)) {
			this.selectedAnnotationId = null;
			this.emit("annotation-selected", { id: null });
		}
	}
	collectAnnotationsForImages(imageNames) {
		const collected = [];
		for (const imageName of imageNames) {
			const order = this.annotationOrderByImage.get(imageName) ?? [];
			for (const id of order) {
				const annotation = this.annotationsById.get(id);
				if (annotation) collected.push(cloneAnnotation(annotation));
			}
		}
		return collected;
	}
	hitTestAnnotation(point) {
		if (!this.info) return null;
		const imageName = this.info.name;
		const candidates = this.annotationIndex.query(imageName, {
			left: point.x,
			top: point.y,
			right: point.x,
			bottom: point.y
		});
		const order = this.annotationOrderByImage.get(imageName) ?? [];
		for (let i = order.length - 1; i >= 0; i--) {
			const id = order[i];
			if (!candidates.has(id)) continue;
			const annotation = this.annotationsById.get(id);
			if (!annotation) continue;
			if (pointInPolygon(point, annotation.points)) return id;
		}
		return null;
	}
	getEraseOriginalIdForCurrentId(id) {
		for (const [originalId, tracked] of this.eraseTrackedByOriginalId.entries()) if (tracked.currentIds.includes(id)) return originalId;
		return null;
	}
	startAnnotationErase(startPoint) {
		if (!this.info) return;
		this.isErasingAnnotation = true;
		this.erasePathPoints = [startPoint];
		this.eraseBeforeImageAnnotations = this.collectAnnotationsForImages([this.info.name]);
		this.eraseTrackedByOriginalId.clear();
		this.eraseDidChange = false;
		this.selectAnnotation(null);
		this.updateAnnotationErase(startPoint, true);
	}
	updateAnnotationErase(imagePoint, force = false) {
		if (!(this.info && this.isErasingAnnotation)) return;
		const previous = this.erasePathPoints[this.erasePathPoints.length - 1];
		if (previous) {
			const dx = imagePoint.x - previous.x;
			const dy = imagePoint.y - previous.y;
			const minStep = Math.max(.25, Math.min(2, this.annotationEraserRadiusPx * .05));
			if (!force && dx * dx + dy * dy < minStep * minStep) return;
		}
		if (!previous || Math.abs(previous.x - imagePoint.x) > ANNOTATION_EPSILON || Math.abs(previous.y - imagePoint.y) > ANNOTATION_EPSILON) this.erasePathPoints.push(imagePoint);
		const radius = this.annotationEraserRadiusPx;
		const minX = previous ? Math.min(previous.x, imagePoint.x) : imagePoint.x;
		const minY = previous ? Math.min(previous.y, imagePoint.y) : imagePoint.y;
		const maxX = previous ? Math.max(previous.x, imagePoint.x) : imagePoint.x;
		const maxY = previous ? Math.max(previous.y, imagePoint.y) : imagePoint.y;
		const candidates = this.annotationIndex.query(this.info.name, {
			left: minX - radius,
			top: minY - radius,
			right: maxX + radius,
			bottom: maxY + radius
		});
		const originalIds = new Set();
		for (const id of candidates) {
			let originalId = this.getEraseOriginalIdForCurrentId(id);
			if (!originalId) {
				const annotation = this.annotationsById.get(id);
				if (!annotation || annotation.imageName !== this.info.name) continue;
				originalId = annotation.id;
				this.eraseTrackedByOriginalId.set(originalId, {
					original: cloneAnnotation(annotation),
					currentIds: [annotation.id]
				});
			}
			originalIds.add(originalId);
		}
		for (const originalId of originalIds) {
			const tracked = this.eraseTrackedByOriginalId.get(originalId);
			if (!tracked) continue;
			const eraseBuf = fromPointArray(this.erasePathPoints);
			const nextPolygons = clipperSubtractBrush({
				outer: tracked.original.points,
				holes: tracked.original.holes
			}, eraseBuf, radius).filter((poly) => coordCount(poly.outer) >= 3).sort((left, right) => polygonArea(right.outer) - polygonArea(left.outer));
			const currentAnnotations = tracked.currentIds.map((id) => this.annotationsById.get(id)).filter((annotation) => Boolean(annotation));
			const unchanged = currentAnnotations.length === nextPolygons.length && currentAnnotations.every((annotation, index) => {
				const next = nextPolygons[index];
				if (!next) return false;
				if (!equalCoords(annotation.points, next.outer)) return false;
				const currentHoles = annotation.holes ?? [];
				if (currentHoles.length !== next.holes.length) return false;
				return currentHoles.every((hole, holeIndex) => equalCoords(hole, next.holes[holeIndex] ?? new Int32Array(0)));
			});
			if (unchanged) continue;
			const timestamp = new Date().toISOString();
			const nextIds = [];
			const pool = [...tracked.currentIds];
			for (let i = 0; i < nextPolygons.length; i++) {
				const poly = nextPolygons[i];
				const id = pool[i] ?? this.createAnnotationId();
				const existing = this.annotationsById.get(id);
				const updated = {
					id,
					imageName: tracked.original.imageName,
					points: new Int32Array(poly.outer),
					holes: poly.holes.length > 0 ? poly.holes : void 0,
					style: { ...tracked.original.style },
					label: tracked.original.label,
					group: tracked.original.group,
					createdAt: existing?.createdAt ?? tracked.original.createdAt,
					updatedAt: timestamp,
					bbox: computeBoundingBox(poly.outer)
				};
				this.addAnnotationInternal(updated, true);
				nextIds.push(id);
			}
			for (const id of tracked.currentIds) if (!nextIds.includes(id)) this.removeAnnotationInternal(id, true);
			tracked.currentIds = nextIds;
			this.eraseDidChange = true;
		}
	}
	finalizeAnnotationErase() {
		this.isErasingAnnotation = false;
		this.erasePathPoints = [];
		this.eraseTrackedByOriginalId.clear();
		if (this.info && this.eraseDidChange && this.eraseBeforeImageAnnotations) {
			const imageNames = [this.info.name];
			const after = this.collectAnnotationsForImages(imageNames);
			this.pushAnnotationHistory({
				type: "batch",
				imageNames,
				before: this.eraseBeforeImageAnnotations,
				after
			});
		}
		this.eraseBeforeImageAnnotations = null;
		this.eraseDidChange = false;
		this.render();
	}
	computeMergeForPoints(drawingPoints) {
		if (!this.info || coordCount(drawingPoints) < 3) return null;
		const activeLabel = this.annotationDefaultLabel;
		const activeGroup = this.annotationDefaultGroup;
		if (!activeLabel) return null;
		const bbox = computeBoundingBox(drawingPoints);
		const candidates = this.annotationIndex.query(this.info.name, bbox);
		const targetIds = [];
		const mergeInputs = [{ outer: drawingPoints }];
		const order = this.annotationOrderByImage.get(this.info.name) ?? [];
		for (let i = order.length - 1; i >= 0; i--) {
			const id = order[i];
			if (!candidates.has(id)) continue;
			const annotation = this.annotationsById.get(id);
			if (!annotation) continue;
			if (annotation.label !== activeLabel) continue;
			if (activeGroup !== null && annotation.group !== activeGroup) continue;
			if (!polygonsIntersect(drawingPoints, annotation.points)) continue;
			targetIds.push(annotation.id);
			mergeInputs.push({
				outer: annotation.points,
				holes: annotation.holes
			});
		}
		if (targetIds.length === 0) return null;
		const polygons = clipperUnion(mergeInputs).filter((poly) => coordCount(poly.outer) >= 3 && polygonArea(poly.outer) >= this.annotationMinAreaPx2);
		if (polygons.length === 0) return null;
		return {
			targetIds,
			polygons
		};
	}
	updateDrawingMergePreview() {
		if (!this.frozenLoopPoints || coordCount(this.frozenLoopPoints) < 3) {
			this.drawMergePreview = null;
			return;
		}
		this.drawMergePreview = this.computeMergeForPoints(new Int32Array(this.frozenLoopPoints));
	}
	finalizeAnnotationDrawing() {
		const isEnabled$1 = this.annotationDrawEnabled;
		const useRadiusBrush = this.annotationDrawRadiusPx > 1;
		const drawnBuf = useRadiusBrush ? fromPointArray(this.drawingPoints) : this.frozenLoopPoints ?? fromPointArray(this.drawingPoints);
		this.isDrawingAnnotation = false;
		this.drawingPoints = [];
		this.frozenLoopPoints = null;
		this.drawMergePreview = null;
		this.emit("annotation-draw-state-change", {
			enabled: isEnabled$1,
			drawing: false
		});
		if (!this.info || coordCount(drawnBuf) < 2) {
			this.render();
			return;
		}
		const imageName = this.info.name;
		if (useRadiusBrush) {
			const level0 = this.info.levels[0];
			let inflatedPolygons = clipperIntersectRect(clipperInflatePath(drawnBuf, this.annotationDrawRadiusPx), {
				width: level0.width,
				height: level0.height
			});
			if (this.editRegions) inflatedPolygons = clipperIntersectRects(inflatedPolygons, this.editRegions);
			inflatedPolygons = inflatedPolygons.filter((poly) => coordCount(poly.outer) >= 3 && polygonArea(poly.outer) >= this.annotationMinAreaPx2);
			if (inflatedPolygons.length === 0) {
				this.render();
				return;
			}
			const before = this.collectAnnotationsForImages([imageName]);
			this.commitDrawnPolygons(inflatedPolygons, imageName, before, new Date().toISOString());
			return;
		}
		if (coordCount(drawnBuf) < 3) {
			this.render();
			return;
		}
		this.finalizePolygonPoints(drawnBuf, imageName);
	}
	/**
	* Commit freshly drawn polygons (brush stroke, or a lasso / SAM result clipped
	* into several pieces by the edit regions): union with same-label
	* annotations, cut other labels of the group, one history entry, select the
	* largest result. Returns its id, or null when nothing survived.
	*/
	commitDrawnPolygons(polygons, imageName, before, timestamp) {
		const activeGroup = this.annotationDefaultGroup;
		const activeLabel = this.annotationDefaultLabel;
		const imageNames = [imageName];
		const existingForMerge = [];
		const mergeTargetIds = [];
		let bLeft = Infinity, bTop = Infinity, bRight = -Infinity, bBottom = -Infinity;
		for (const p of polygons) {
			const b = computeBoundingBox(p.outer);
			if (b.left < bLeft) bLeft = b.left;
			if (b.top < bTop) bTop = b.top;
			if (b.right > bRight) bRight = b.right;
			if (b.bottom > bBottom) bBottom = b.bottom;
		}
		const overallBbox = {
			left: bLeft,
			top: bTop,
			right: bRight,
			bottom: bBottom
		};
		const candidates = this.annotationIndex.query(imageName, overallBbox);
		const order = this.annotationOrderByImage.get(imageName) ?? [];
		for (let i = order.length - 1; i >= 0; i--) {
			const id = order[i];
			if (!candidates.has(id)) continue;
			const ann = this.annotationsById.get(id);
			if (!ann) continue;
			if (ann.label !== activeLabel) continue;
			if (activeGroup !== null && ann.group !== activeGroup) continue;
			if (!polygons.some((poly) => polygonsIntersect(poly.outer, ann.points))) continue;
			mergeTargetIds.push(id);
			existingForMerge.push({
				outer: ann.points,
				holes: ann.holes
			});
		}
		const allInputs = [...existingForMerge, ...polygons];
		const merged = clipperUnion(allInputs).filter((poly) => coordCount(poly.outer) >= 3 && polygonArea(poly.outer) >= this.annotationMinAreaPx2);
		if (merged.length === 0) {
			this.render();
			return null;
		}
		const sortedPolygons = [...merged].sort((left, right) => polygonArea(right.outer) - polygonArea(left.outer));
		const reusableIds = [...mergeTargetIds];
		const nextIds = [];
		for (let i = 0; i < sortedPolygons.length; i++) {
			const poly = sortedPolygons[i];
			const id = reusableIds[i] ?? this.createAnnotationId();
			const existing = this.annotationsById.get(id);
			const annotation = {
				id,
				imageName,
				points: new Int32Array(poly.outer),
				holes: poly.holes.length > 0 ? poly.holes : void 0,
				style: { ...this.annotationDefaultStyle },
				label: activeLabel ?? void 0,
				group: activeGroup ?? void 0,
				createdAt: existing?.createdAt ?? timestamp,
				updatedAt: timestamp,
				bbox: computeBoundingBox(poly.outer)
			};
			this.addAnnotationInternal(annotation, true);
			nextIds.push(id);
		}
		for (const id of mergeTargetIds) if (!nextIds.includes(id)) this.removeAnnotationInternal(id, true);
		if (activeGroup !== null) for (const poly of polygons) this.applyDifferenceToGroup(imageName, activeLabel, activeGroup, poly.outer, timestamp);
		const primaryId = nextIds[0] ?? null;
		this.selectAnnotation(primaryId);
		const after = this.collectAnnotationsForImages(imageNames);
		this.pushAnnotationHistory({
			type: "batch",
			imageNames,
			before,
			after
		});
		this.render();
		return primaryId;
	}
	/**
	* Shared finalization for polygon points: area check, same-label union,
	* different-label difference, history, selection, and render.
	* Returns the primary annotation ID or null if the polygon was too small.
	*/
	finalizePolygonPoints(drawnPoints, imageName) {
		let points = drawnPoints;
		const activeLabel = this.annotationDefaultLabel;
		const activeGroup = this.annotationDefaultGroup;
		const imageNames = [imageName];
		const before = this.collectAnnotationsForImages(imageNames);
		const timestamp = new Date().toISOString();
		if (this.editRegions && !this.polygonInsideEditRegion(points)) {
			const pieces = clipperIntersectRects([{
				outer: points,
				holes: []
			}], this.editRegions).filter((poly) => coordCount(poly.outer) >= 3 && polygonArea(poly.outer) >= this.annotationMinAreaPx2);
			if (pieces.length === 0) {
				this.emit("annotation-outside-region", { tool: this.annotationTool });
				this.render();
				return null;
			}
			if (pieces.length > 1 || pieces[0].holes.length > 0) return this.commitDrawnPolygons(pieces, imageName, before, timestamp);
			points = pieces[0].outer;
		}
		const area = polygonArea(points);
		if (area < this.annotationMinAreaPx2) {
			this.render();
			return null;
		}
		const mergePreview = this.computeMergeForPoints(points);
		if (mergePreview && mergePreview.polygons.length > 0) {
			const sortedPolygons = [...mergePreview.polygons].sort((left, right) => polygonArea(right.outer) - polygonArea(left.outer));
			const reusableIds = [...mergePreview.targetIds];
			const nextIds = [];
			for (let i = 0; i < sortedPolygons.length; i++) {
				const poly = sortedPolygons[i];
				const id = reusableIds[i] ?? this.createAnnotationId();
				const existing = this.annotationsById.get(id);
				const annotation$1 = {
					id,
					imageName,
					points: new Int32Array(poly.outer),
					holes: poly.holes.length > 0 ? poly.holes : void 0,
					style: { ...this.annotationDefaultStyle },
					label: activeLabel ?? void 0,
					group: activeGroup ?? void 0,
					createdAt: existing?.createdAt ?? timestamp,
					updatedAt: timestamp,
					bbox: computeBoundingBox(poly.outer)
				};
				this.addAnnotationInternal(annotation$1, true);
				nextIds.push(id);
			}
			for (const id of mergePreview.targetIds) if (!nextIds.includes(id)) this.removeAnnotationInternal(id, true);
			if (activeGroup !== null) this.applyDifferenceToGroup(imageName, activeLabel, activeGroup, points, timestamp);
			const primaryId = nextIds[0] ?? null;
			if (primaryId) this.selectAnnotation(primaryId);
			else this.selectAnnotation(null);
			const after$1 = this.collectAnnotationsForImages(imageNames);
			this.pushAnnotationHistory({
				type: "batch",
				imageNames,
				before,
				after: after$1
			});
			this.render();
			return primaryId;
		}
		const annotation = {
			id: this.createAnnotationId(),
			imageName,
			points,
			style: { ...this.annotationDefaultStyle },
			label: activeLabel ?? void 0,
			group: activeGroup ?? void 0,
			createdAt: timestamp,
			updatedAt: timestamp,
			bbox: computeBoundingBox(points)
		};
		this.addAnnotationInternal(annotation, true);
		this.selectAnnotation(annotation.id);
		if (activeGroup !== null) this.applyDifferenceToGroup(imageName, activeLabel, activeGroup, points, timestamp);
		const after = this.collectAnnotationsForImages(imageNames);
		this.pushAnnotationHistory({
			type: "batch",
			imageNames,
			before,
			after
		});
		this.render();
		return annotation.id;
	}
	applyDifferenceToGroup(imageName, activeLabel, activeGroup, clipPoints, timestamp) {
		const clipPoly = { outer: clipPoints };
		const bbox = computeBoundingBox(clipPoints);
		const candidates = this.annotationIndex.query(imageName, bbox);
		const order = this.annotationOrderByImage.get(imageName) ?? [];
		for (let i = order.length - 1; i >= 0; i--) {
			const id = order[i];
			if (!candidates.has(id)) continue;
			const annotation = this.annotationsById.get(id);
			if (!annotation) continue;
			if (annotation.group !== activeGroup) continue;
			if (annotation.label === activeLabel) continue;
			if (!polygonsIntersect(clipPoints, annotation.points)) continue;
			const pieces = clipperDifference({
				outer: annotation.points,
				holes: annotation.holes
			}, clipPoly).filter((poly) => coordCount(poly.outer) >= 3 && polygonArea(poly.outer) >= this.annotationMinAreaPx2);
			this.removeAnnotationInternal(id, true);
			for (const piece of pieces) {
				const pieceAnnotation = {
					id: this.createAnnotationId(),
					imageName,
					points: piece.outer,
					holes: piece.holes.length > 0 ? piece.holes : void 0,
					style: { ...annotation.style },
					label: annotation.label,
					group: annotation.group,
					createdAt: annotation.createdAt,
					updatedAt: timestamp,
					bbox: computeBoundingBox(piece.outer)
				};
				this.addAnnotationInternal(pieceAnnotation, true);
			}
		}
	}
	applyHistoryAction(action, direction) {
		switch (action.type) {
			case "create": {
				if (direction === "undo") this.removeAnnotationInternal(action.annotation.id, true);
				else this.addAnnotationInternal(action.annotation, true);
				break;
			}
			case "delete": {
				if (direction === "undo") this.addAnnotationInternal(action.annotation, true);
				else this.removeAnnotationInternal(action.annotation.id, true);
				break;
			}
			case "update": {
				this.addAnnotationInternal(direction === "undo" ? action.before : action.after, true);
				break;
			}
			case "batch": {
				if (direction === "undo") this.replaceAnnotationsForImages(action.imageNames, action.before);
				else this.replaceAnnotationsForImages(action.imageNames, action.after);
				break;
			}
		}
		this.render();
	}
	moveAnnotationInternal(id, dx, dy, emitEvent) {
		const annotation = this.annotationsById.get(id);
		if (!annotation) return null;
		const movedPoints = translatePoints(annotation.points, dx, dy);
		const moved = {
			...annotation,
			points: movedPoints,
			bbox: computeBoundingBox(movedPoints),
			updatedAt: new Date().toISOString()
		};
		this.addAnnotationInternal(moved, emitEvent);
		return moved;
	}
	toScreenPoints(ring, centerScreenX, centerScreenY, scale) {
		if (ring instanceof Int32Array) {
			const n = coordCount(ring);
			const result = new Array(n);
			for (let i = 0; i < n; i++) result[i] = {
				x: centerScreenX + (ring[i * 2] - this.viewX) * scale,
				y: centerScreenY + (ring[i * 2 + 1] - this.viewY) * scale
			};
			return result;
		}
		return ring.map((p) => ({
			x: centerScreenX + (p.x - this.viewX) * scale,
			y: centerScreenY + (p.y - this.viewY) * scale
		}));
	}
	drawAnnotationFill(style, screenPoints, screenHoles) {
		this.gl.drawPolygonFill(screenPoints, screenHoles, style.fillColor, style.fillOpacity);
	}
	drawAnnotationStroke(style, screenPoints, screenHoles, selected) {
		const strokeWidth = style.strokeWidth ?? 2;
		this.gl.drawPolygonStroke(screenPoints, style.strokeColor, style.strokeOpacity, strokeWidth);
		if (screenHoles) for (const hole of screenHoles) this.gl.drawPolygonStroke(hole, style.strokeColor, style.strokeOpacity, strokeWidth);
		if (selected) {
			this.gl.drawPolygonStroke(screenPoints, "#ffffff", 1, strokeWidth + 1.5, {
				dashLength: 6,
				gapLength: 4
			});
			if (screenHoles) for (const hole of screenHoles) this.gl.drawPolygonStroke(hole, "#ffffff", 1, strokeWidth + 1.5, {
				dashLength: 6,
				gapLength: 4
			});
		}
	}
	drawDrawingOverlay(centerScreenX, centerScreenY, scale) {
		if (!this.isDrawingAnnotation) return;
		const strokeWidth = this.annotationDefaultStyle.strokeWidth ?? 2;
		if (this.annotationDrawRadiusPx > 1) {
			if (this.drawingPoints.length >= 2) {
				const level0 = this.info?.levels[0];
				const brushBuf = fromPointArray(this.drawingPoints);
				let inflated = clipperIntersectRect(clipperInflatePath(brushBuf, this.annotationDrawRadiusPx), {
					width: level0.width,
					height: level0.height
				});
				if (this.editRegions) inflated = clipperIntersectRects(inflated, this.editRegions);
				if (inflated.length > 0) for (const poly of inflated) {
					if (coordCount(poly.outer) < 3) continue;
					const screenOuter = this.toScreenPoints(poly.outer, centerScreenX, centerScreenY, scale);
					const screenHoles = poly.holes.map((h) => this.toScreenPoints(h, centerScreenX, centerScreenY, scale));
					this.gl.drawPolygonFill(screenOuter, screenHoles, this.annotationDefaultStyle.fillColor, this.annotationDefaultStyle.fillOpacity);
					this.gl.drawPolygonStroke(screenOuter, this.annotationDefaultStyle.strokeColor, this.annotationDefaultStyle.strokeOpacity, strokeWidth);
					for (const h of screenHoles) this.gl.drawPolygonStroke(h, this.annotationDefaultStyle.strokeColor, this.annotationDefaultStyle.strokeOpacity, strokeWidth);
				}
			}
			return;
		}
		if (this.drawMergePreview && this.drawMergePreview.polygons.length > 0) {
			for (const poly of this.drawMergePreview.polygons) {
				if (coordCount(poly.outer) < 3) continue;
				const screenOuter = this.toScreenPoints(poly.outer, centerScreenX, centerScreenY, scale);
				const screenHoles = poly.holes.map((h) => this.toScreenPoints(h, centerScreenX, centerScreenY, scale));
				this.gl.drawPolygonFill(screenOuter, screenHoles, this.annotationDefaultStyle.fillColor, this.annotationDefaultStyle.fillOpacity);
				this.gl.drawPolygonStroke(screenOuter, this.annotationDefaultStyle.strokeColor, this.annotationDefaultStyle.strokeOpacity, strokeWidth);
				for (const h of screenHoles) this.gl.drawPolygonStroke(h, this.annotationDefaultStyle.strokeColor, this.annotationDefaultStyle.strokeOpacity, strokeWidth);
			}
			return;
		}
		const previewBuf = this.frozenLoopPoints;
		const previewPoints = previewBuf ?? this.drawingPoints;
		const previewLen = previewBuf ? coordCount(previewBuf) : this.drawingPoints.length;
		if (previewLen < 2) return;
		const screenPoints = this.toScreenPoints(previewPoints, centerScreenX, centerScreenY, scale);
		const isClosed = Boolean(this.frozenLoopPoints);
		if (isClosed && previewLen >= 3) this.gl.drawPolygonFill(screenPoints, void 0, this.annotationDefaultStyle.fillColor, this.annotationDefaultStyle.fillOpacity);
		else if (previewLen >= 3) this.gl.drawPolygonFill(screenPoints, void 0, this.annotationDefaultStyle.fillColor, this.annotationDefaultStyle.fillOpacity * .3);
		this.gl.drawLineStrip(screenPoints, this.annotationDefaultStyle.strokeColor, this.annotationDefaultStyle.strokeOpacity, strokeWidth);
		if (!isClosed && this.drawingPoints.length >= 3) {
			const first = screenPoints[0];
			const last = screenPoints[screenPoints.length - 1];
			this.gl.drawLineStrip([last, first], this.annotationDefaultStyle.strokeColor, this.annotationDefaultStyle.strokeOpacity * .5, strokeWidth, {
				dashLength: 6,
				gapLength: 4
			});
		}
	}
	drawEraserOverlay(centerScreenX, centerScreenY, scale) {
		if (!this.annotationDrawEnabled || this.annotationTool !== "erase") return;
		if (this.mouseImageX < 0 || this.mouseImageY < 0) return;
		const radius = this.annotationEraserRadiusPx * scale;
		const pointerX = centerScreenX + (this.mouseImageX - this.viewX) * scale;
		const pointerY = centerScreenY + (this.mouseImageY - this.viewY) * scale;
		const circleRadius = Math.max(1, radius);
		this.gl.drawCircle(pointerX, pointerY, circleRadius, null, {
			color: "#000000",
			alpha: .3,
			width: 3
		});
		this.gl.drawCircle(pointerX, pointerY, circleRadius, null, {
			color: "#ffffff",
			alpha: .85,
			width: 1.5
		});
	}
	drawDrawBrushOverlay(centerScreenX, centerScreenY, scale) {
		if (!this.annotationDrawEnabled || this.annotationTool !== "draw") return;
		if (this.annotationDrawRadiusPx <= 1) return;
		if (this.mouseImageX < 0 || this.mouseImageY < 0) return;
		const radius = this.annotationDrawRadiusPx * scale;
		const pointerX = centerScreenX + (this.mouseImageX - this.viewX) * scale;
		const pointerY = centerScreenY + (this.mouseImageY - this.viewY) * scale;
		this.gl.drawCircle(pointerX, pointerY, Math.max(1, radius), {
			color: this.annotationDefaultStyle.fillColor,
			alpha: this.annotationDefaultStyle.fillOpacity * .3
		}, {
			color: this.annotationDefaultStyle.strokeColor,
			alpha: this.annotationDefaultStyle.strokeOpacity,
			width: 1.5
		});
	}
	drawSamOverlay(centerScreenX, centerScreenY, scale) {
		if (!this.annotationDrawEnabled || this.annotationTool !== "sam") return;
		const toScreenX = (ix) => centerScreenX + (ix - this.viewX) * scale;
		const toScreenY = (iy) => centerScreenY + (iy - this.viewY) * scale;
		const showBox = this.samDragStart && this.samDragEnd && (this.isSamDragging || this.samPending);
		if (showBox) {
			const sx1 = toScreenX(this.samDragStart.x);
			const sy1 = toScreenY(this.samDragStart.y);
			const sx2 = toScreenX(this.samDragEnd.x);
			const sy2 = toScreenY(this.samDragEnd.y);
			const boxCorners = [
				{
					x: sx1,
					y: sy1
				},
				{
					x: sx2,
					y: sy1
				},
				{
					x: sx2,
					y: sy2
				},
				{
					x: sx1,
					y: sy2
				},
				{
					x: sx1,
					y: sy1
				}
			];
			this.gl.drawLineStrip(boxCorners, "#000000", .4, 4, {
				dashLength: 8,
				gapLength: 4
			});
			this.gl.drawLineStrip(boxCorners, "#ffffff", .8, 2, {
				dashLength: 8,
				gapLength: 4
			});
		}
		if (this.samClickPoint && this.samPending) {
			const elapsed = (performance.now() - this.samClickTime) / 1e3;
			const phase = elapsed % 1;
			const alpha = .4 + .6 * (.5 + .5 * Math.sin(phase * Math.PI * 2));
			const sx = toScreenX(this.samClickPoint.x);
			const sy = toScreenY(this.samClickPoint.y);
			const arm = 12;
			const hLine = [{
				x: sx - arm,
				y: sy
			}, {
				x: sx + arm,
				y: sy
			}];
			const vLine = [{
				x: sx,
				y: sy - arm
			}, {
				x: sx,
				y: sy + arm
			}];
			this.gl.drawLineStrip(hLine, "#000000", alpha * .5, 3.5);
			this.gl.drawLineStrip(vLine, "#000000", alpha * .5, 3.5);
			this.gl.drawLineStrip(hLine, "#ffffff", alpha, 1.5);
			this.gl.drawLineStrip(vLine, "#ffffff", alpha, 1.5);
			this.gl.drawCircle(sx, sy, 4, {
				color: "#ffffff",
				alpha
			}, {
				color: "#000000",
				alpha: alpha * .6,
				width: 1.5
			});
			requestAnimationFrame(() => {
				if (this.samClickPoint) this.render();
			});
		}
	}
	/**
	* Handle tile load completion from scheduler
	*/
	handleTileLoaded(request, image, error) {
		if (request.key.startsWith("overlay:")) {
			const overlayId = request.key.split(":")[1];
			const cache = this.overlayCaches.get(overlayId);
			if (cache) {
				cache.doneLoading(request.key);
				if (image) {
					this.clearTileFailure(request.key);
					cache.set(request.key, image, request.priority, request.probability);
					this.render();
				} else this.recordTileFailure(request.key);
			}
			return;
		}
		this.priorityCache.doneLoading(request.key);
		if (image) {
			this.clearTileFailure(request.key);
			this.priorityCache.set(request.key, image, request.priority, request.probability);
			const priorityName = request.priority === TilePriority.VISIBLE ? "visible" : request.priority === TilePriority.PREFETCH ? "prefetch" : "recent";
			Logger.tileLoaded(request.imageName, request.level, request.x, request.y, priorityName);
			this.emit("tile-load", {
				imageName: request.imageName,
				level: request.level,
				x: request.x,
				y: request.y,
				cached: false
			});
			this.render();
		} else {
			this.recordTileFailure(request.key);
			this.emit("tile-error", {
				imageName: request.imageName,
				level: request.level,
				x: request.x,
				y: request.y,
				error: error ?? new Error(`Failed to load tile ${request.level}/${request.x}/${request.y}`)
			});
		}
	}
	/**
	* Load an image into the viewer
	*/
	loadImage(imageInfo) {
		this.tileClient.setSourceType(imageInfo.sourceType ?? "tiff");
		this.tileClient.primeImageInfo(imageInfo);
		this.setPrefetchEnabled(imageInfo.sourceType !== "omezarr");
		this.info = imageInfo;
		this.priorityCache.clear();
		this.tileScheduler.cancelAll();
		this.velocityTracker.reset();
		this.resetMinimapThumbnailState();
		if (imageInfo.sourceType === "omezarr") this.minimapThumbnailFailed = true;
		this.tileFailureCounts.clear();
		this.tileFailureBackoffUntil.clear();
		this.clearAllOverlays();
		this.editRegions = null;
		if (this.labelRasterTool) {
			this.labelRasterTool = null;
			this.canvas.style.cursor = "";
			this.emit("label-raster-tool-change", { state: null });
		}
		const level0 = imageInfo.levels[0];
		this.viewX = level0.width / 2;
		this.viewY = level0.height / 2;
		this.zoom = 1;
		this.currentLevel = 0;
		this.isDrawingAnnotation = false;
		this.isErasingAnnotation = false;
		this.drawingPoints = [];
		this.frozenLoopPoints = null;
		this.drawMergePreview = null;
		this.erasePathPoints = [];
		this.eraseBeforeImageAnnotations = null;
		this.eraseTrackedByOriginalId.clear();
		this.eraseDidChange = false;
		if (imageInfo.sourceType !== "omezarr") this.loadMinimapThumbnail();
		this.updateLevel();
		this.render();
		this.emitViewportChange();
		this.emit("annotation-draw-state-change", {
			enabled: this.annotationDrawEnabled,
			drawing: false
		});
		this.emit("annotation-tool-change", {
			tool: this.annotationTool,
			eraserRadiusPx: this.annotationEraserRadiusPx,
			drawRadiusPx: this.annotationDrawRadiusPx
		});
	}
	/**
	* Get current zoom level
	*/
	getZoom() {
		return this.zoom;
	}
	/**
	* Set zoom level
	*/
	setZoom(zoom) {
		this.zoom = Math.max(this.minZoom, Math.min(this.maxZoom, zoom));
		this.clampView();
		this.updateLevel();
		this.render();
		this.emitViewportChange();
	}
	/**
	* Get current view center in image coordinates
	*/
	getCenter() {
		return {
			x: this.viewX,
			y: this.viewY
		};
	}
	/**
	* Set view center in image coordinates
	*/
	setCenter(x, y) {
		this.viewX = x;
		this.viewY = y;
		this.clampView();
		this.render();
		this.emitViewportChange();
	}
	/**
	* Fit the view to a level-0 image rectangle (e.g. an edit region): centers on
	* it and picks the largest zoom that shows the whole rectangle plus a padding
	* of `paddingFraction` × its size on every side.
	*/
	fitToRect(left, top, right, bottom, paddingFraction = .1) {
		if (!this.info) return;
		const level0 = this.info.levels[0];
		const width = Math.abs(right - left);
		const height = Math.abs(bottom - top);
		if (!(width > 0 && height > 0) || level0.width <= 0 || level0.height <= 0) return;
		const pad = 1 + Math.max(0, paddingFraction) * 2;
		const fitScale = Math.min(this.canvas.width / level0.width, this.canvas.height / level0.height);
		const targetScale = Math.min(this.canvas.width / (width * pad), this.canvas.height / (height * pad));
		if (!(fitScale > 0 && targetScale > 0)) return;
		this.viewX = (left + right) / 2;
		this.viewY = (top + bottom) / 2;
		this.zoom = Math.max(this.minZoom, Math.min(this.maxZoom, targetScale / fitScale));
		this.clampView();
		this.updateLevel();
		this.render();
		this.emitViewportChange();
	}
	/**
	* Get current viewport bounds in image coordinates
	*/
	getViewport() {
		const scale = this.getScale();
		const halfWidth = this.canvas.width / 2 / scale;
		const halfHeight = this.canvas.height / 2 / scale;
		return {
			left: this.viewX - halfWidth,
			top: this.viewY - halfHeight,
			right: this.viewX + halfWidth,
			bottom: this.viewY + halfHeight
		};
	}
	/**
	* Get current pyramid level
	*/
	getCurrentLevel() {
		return this.currentLevel;
	}
	/**
	* Get loaded image info
	*/
	getImageInfo() {
		return this.info;
	}
	setChannels(indices) {
		this.tileClient.setSelectedChannels(indices);
		this.priorityCache.clear();
		this.tileScheduler.cancelAll();
		this.resetMinimapThumbnailState();
		this.loadMinimapThumbnail();
		this.render();
	}
	getChannels() {
		return this.tileClient.getSelectedChannels();
	}
	getAvailableChannels() {
		if (!this.info) return [];
		return this.tileClient.getAvailableChannels(this.info.name);
	}
	setAnnotationDrawEnabled(enabled) {
		if (enabled) {
			this.setAnnotationTool("draw");
			return;
		}
		if (this.isDrawingAnnotation) {
			this.isDrawingAnnotation = false;
			this.drawingPoints = [];
			this.frozenLoopPoints = null;
			this.drawMergePreview = null;
		}
		if (this.isErasingAnnotation) this.finalizeAnnotationErase();
		this.annotationTool = "pan";
		this.annotationDrawEnabled = false;
		this.emit("annotation-draw-state-change", {
			enabled: false,
			drawing: false
		});
		this.emit("annotation-tool-change", {
			tool: this.annotationTool,
			eraserRadiusPx: this.annotationEraserRadiusPx,
			drawRadiusPx: this.annotationDrawRadiusPx
		});
		this.render();
	}
	isAnnotationDrawEnabled() {
		return this.annotationDrawEnabled;
	}
	setMinimapEnabled(enabled) {
		this.minimapEnabled = enabled;
		this.render();
	}
	isMinimapEnabled() {
		return this.minimapEnabled;
	}
	setAnnotationTool(tool) {
		const nextTool = tool;
		if (nextTool === this.annotationTool && this.annotationDrawEnabled === (nextTool !== "pan")) return;
		if (this.isDrawingAnnotation && nextTool !== "draw") {
			this.isDrawingAnnotation = false;
			this.drawingPoints = [];
			this.frozenLoopPoints = null;
			this.drawMergePreview = null;
		}
		if (this.isErasingAnnotation && nextTool !== "erase") this.finalizeAnnotationErase();
		if (this.samPending && nextTool !== "sam") this.cancelSamRequest();
		this.annotationTool = nextTool;
		this.annotationDrawEnabled = nextTool !== "pan";
		this.canvas.style.cursor = nextTool === "sam" ? "crosshair" : "";
		this.annotationCursorOutsideRegion = null;
		this.emit("annotation-draw-state-change", {
			enabled: this.annotationDrawEnabled,
			drawing: this.isDrawingAnnotation
		});
		this.emit("annotation-tool-change", {
			tool: this.annotationTool,
			eraserRadiusPx: this.annotationEraserRadiusPx,
			drawRadiusPx: this.annotationDrawRadiusPx
		});
		this.render();
	}
	getAnnotationTool() {
		return this.annotationTool;
	}
	setAnnotationEraserRadiusPx(radius) {
		this.annotationEraserRadiusPx = Math.max(MIN_ANNOTATION_ERASER_RADIUS_PX, Math.min(this.annotationMaxEraserRadiusPx, radius));
		this.emit("annotation-tool-change", {
			tool: this.annotationTool,
			eraserRadiusPx: this.annotationEraserRadiusPx,
			drawRadiusPx: this.annotationDrawRadiusPx
		});
		this.render();
	}
	getAnnotationEraserRadiusPx() {
		return this.annotationEraserRadiusPx;
	}
	setAnnotationMaxEraserRadiusPx(max) {
		this.annotationMaxEraserRadiusPx = Math.max(MIN_ANNOTATION_ERASER_RADIUS_PX, max);
		if (this.annotationEraserRadiusPx > this.annotationMaxEraserRadiusPx) this.setAnnotationEraserRadiusPx(this.annotationEraserRadiusPx);
	}
	setAnnotationDrawRadiusPx(radius) {
		this.annotationDrawRadiusPx = Math.max(MIN_ANNOTATION_DRAW_RADIUS_PX, Math.min(this.annotationMaxDrawRadiusPx, radius));
		this.emit("annotation-tool-change", {
			tool: this.annotationTool,
			eraserRadiusPx: this.annotationEraserRadiusPx,
			drawRadiusPx: this.annotationDrawRadiusPx
		});
		this.render();
	}
	getAnnotationDrawRadiusPx() {
		return this.annotationDrawRadiusPx;
	}
	setAnnotationMaxDrawRadiusPx(max) {
		this.annotationMaxDrawRadiusPx = Math.max(MIN_ANNOTATION_DRAW_RADIUS_PX, max);
		if (this.annotationDrawRadiusPx > this.annotationMaxDrawRadiusPx) this.setAnnotationDrawRadiusPx(this.annotationDrawRadiusPx);
	}
	setAnnotationDefaultLabel(label) {
		this.annotationDefaultLabel = label;
	}
	getAnnotationDefaultLabel() {
		return this.annotationDefaultLabel;
	}
	setAnnotationGroup(group) {
		this.annotationDefaultGroup = group;
	}
	getAnnotationGroup() {
		return this.annotationDefaultGroup;
	}
	setAnnotationDefaultStyle(style) {
		this.annotationDefaultStyle = normalizeStyle(style, this.annotationDefaultStyle);
	}
	getAnnotationDefaultStyle() {
		return { ...this.annotationDefaultStyle };
	}
	setAnnotationExportPrecision(precision) {
		this.annotationExportPrecision = Math.max(0, Math.floor(precision));
	}
	setSamConfig(config) {
		this.samConfig = config;
		this.samClient = this.createSamClient(config);
	}
	createSamClient(config) {
		const proxyPath = config.proxyPath ?? "/api/sam";
		return new SamClient$1(proxyPath, config.fetch, { confidenceThreshold: config.confidenceThreshold });
	}
	isSamPending() {
		return this.samPending;
	}
	cancelSamRequest() {
		if (this.samAbortController) {
			this.samAbortController.abort();
			this.samAbortController = null;
		}
		this.samPending = false;
		this.samClickPoint = null;
		this.samDragStart = null;
		this.samDragEnd = null;
		this.isSamDragging = false;
		if (this.annotationTool === "sam") this.canvas.style.cursor = "crosshair";
	}
	async handleSamClick(imagePoint, imageBox) {
		if (!this.samClient || this.samPending || !this.info) return;
		this.samPending = true;
		this.samAbortController = new AbortController();
		this.samClickPoint = imagePoint;
		this.samClickTime = performance.now();
		this.canvas.style.cursor = "wait";
		this.render();
		const { x: imageX, y: imageY } = imagePoint;
		const cropLevel = this.currentLevel;
		const cropSize = this.samConfig.cropSize ?? 1024;
		this.emit("sam-segment-start", {
			imageX,
			imageY,
			cropLevel,
			box: imageBox
		});
		try {
			const crop = await extractCrop(imageX, imageY, this.info, cropLevel, cropSize, this.priorityCache, this.tileClient);
			if (this.samAbortController?.signal.aborted) return;
			const relX = imageX / crop.region.scaleToLevel0 - crop.region.left;
			const relY = imageY / crop.region.scaleToLevel0 - crop.region.top;
			let bbox;
			if (imageBox) bbox = [
				imageBox.x1 / crop.region.scaleToLevel0 - crop.region.left,
				imageBox.y1 / crop.region.scaleToLevel0 - crop.region.top,
				imageBox.x2 / crop.region.scaleToLevel0 - crop.region.left,
				imageBox.y2 / crop.region.scaleToLevel0 - crop.region.top
			];
			const predictions = await this.samClient.predict({
				b64img: crop.b64img,
				pointCoords: [[relX, relY]],
				pointLabels: [1],
				bbox,
				modelType: this.samConfig.modelType,
				multimaskOutput: this.samConfig.multimaskOutput
			}, this.samAbortController.signal);
			if (this.samAbortController?.signal.aborted) return;
			const minQuality = this.samConfig.minQuality ?? 0;
			const valid = predictions.filter((p) => p.quality >= minQuality);
			if (valid.length === 0) {
				this.emit("sam-segment-error", {
					error: "No mask found at click location",
					imageX,
					imageY
				});
				return;
			}
			const best = valid.reduce((a, b) => a.quality >= b.quality ? a : b);
			const { left, top, scaleToLevel0 } = crop.region;
			const n = coordCount(best.points);
			const pointsBuf = new Int32Array(n * 2);
			for (let i = 0; i < n; i++) {
				pointsBuf[i * 2] = Math.round((left + best.points[i * 2]) * scaleToLevel0);
				pointsBuf[i * 2 + 1] = Math.round((top + best.points[i * 2 + 1]) * scaleToLevel0);
			}
			const resultId = this.finalizePolygonPoints(pointsBuf, this.info.name);
			const resultAnnotation = resultId ? this.annotationsById.get(resultId) : null;
			if (resultAnnotation) this.emit("sam-segment-complete", {
				annotation: cloneAnnotation(resultAnnotation),
				quality: best.quality
			});
		} catch (err) {
			if (err.name === "AbortError") return;
			this.emit("sam-segment-error", {
				error: err instanceof Error ? err.message : String(err),
				imageX,
				imageY
			});
		} finally {
			this.samPending = false;
			this.samAbortController = null;
			this.samClickPoint = null;
			this.samDragEnd = null;
			this.canvas.style.cursor = "crosshair";
			this.render();
		}
	}
	getAnnotationExportPrecision() {
		return this.annotationExportPrecision;
	}
	getAnnotations(imageName) {
		if (imageName) return this.collectAnnotationsForImages([imageName]);
		const all = [];
		for (const key of this.annotationOrderByImage.keys()) all.push(...this.collectAnnotationsForImages([key]));
		return all;
	}
	exportAnnotations(imageName, options) {
		const precision = Math.max(0, Math.floor(options?.precision ?? this.annotationExportPrecision));
		return this.getAnnotations(imageName).map((annotation) => quantizeAnnotation(annotation, precision));
	}
	setAnnotations(annotations, mode = "replace") {
		if (annotations.length === 0) return;
		const sanitized = [];
		const imageNames = new Set();
		for (const annotation of annotations) {
			if (!annotation.imageName || coordCount(annotation.points) < 3) throw new Error(`Invalid annotation ${annotation.id}: requires imageName and >= 3 points`);
			const clean = sanitizeAnnotation(annotation);
			imageNames.add(clean.imageName);
			const existing = this.annotationsById.get(clean.id);
			if (existing && existing.imageName !== clean.imageName) imageNames.add(existing.imageName);
			sanitized.push(clean);
		}
		const images = [...imageNames];
		const before = this.collectAnnotationsForImages(images);
		if (mode === "replace") this.replaceAnnotationsForImages(images, sanitized);
		else for (const annotation of sanitized) this.addAnnotationInternal(annotation, false);
		const after = this.collectAnnotationsForImages(images);
		this.pushAnnotationHistory({
			type: "batch",
			imageNames: images,
			before,
			after
		});
		this.render();
	}
	clearAnnotations(imageName) {
		const imageNames = imageName ? [imageName] : [...this.annotationOrderByImage.keys()];
		if (imageNames.length === 0) return;
		const before = this.collectAnnotationsForImages(imageNames);
		this.replaceAnnotationsForImages(imageNames, []);
		this.pushAnnotationHistory({
			type: "batch",
			imageNames,
			before,
			after: []
		});
		this.render();
	}
	removeAnnotation(id) {
		const removed = this.removeAnnotationInternal(id, true);
		if (!removed) return;
		this.pushAnnotationHistory({
			type: "delete",
			annotation: removed
		});
		this.render();
	}
	selectAnnotation(id) {
		const nextId = id && this.annotationsById.has(id) ? id : null;
		if (this.selectedAnnotationId === nextId) return;
		this.selectedAnnotationId = nextId;
		this.emit("annotation-selected", { id: nextId });
		this.render();
	}
	getSelectedAnnotationId() {
		return this.selectedAnnotationId;
	}
	updateAnnotationStyle(id, style) {
		const annotation = this.annotationsById.get(id);
		if (!annotation) return;
		const before = cloneAnnotation(annotation);
		const after = {
			...annotation,
			style: normalizeStyle(style, annotation.style),
			updatedAt: new Date().toISOString()
		};
		this.addAnnotationInternal(after, true);
		this.pushAnnotationHistory({
			type: "update",
			before,
			after: cloneAnnotation(after)
		});
		this.render();
	}
	moveAnnotation(id, dx, dy) {
		if (dx === 0 && dy === 0) return;
		const annotation = this.annotationsById.get(id);
		if (!annotation) return;
		const before = cloneAnnotation(annotation);
		const moved = this.moveAnnotationInternal(id, dx, dy, true);
		if (!moved) return;
		this.pushAnnotationHistory({
			type: "update",
			before,
			after: cloneAnnotation(moved)
		});
		this.render();
	}
	undoAnnotationAction() {
		const action = this.annotationUndoStack.pop();
		if (!action) return false;
		this.applyHistoryAction(action, "undo");
		this.annotationRedoStack.push(action);
		this.emitAnnotationHistoryChange();
		return true;
	}
	redoAnnotationAction() {
		const action = this.annotationRedoStack.pop();
		if (!action) return false;
		this.applyHistoryAction(action, "redo");
		this.annotationUndoStack.push(action);
		this.emitAnnotationHistoryChange();
		return true;
	}
	canUndoAnnotationAction() {
		return this.annotationUndoStack.length > 0;
	}
	canRedoAnnotationAction() {
		return this.annotationRedoStack.length > 0;
	}
	/**
	* Render the current view
	*/
	render() {
		if (!this.info) return;
		const level0 = this.info.levels[0];
		const levelInfo = this.info.levels[this.currentLevel];
		const scale = this.getScale();
		const levelScale = level0.width / levelInfo.width;
		const effectiveScale = scale * levelScale;
		this.gl.beginFrame(this.theme.backgroundColor);
		const centerScreenX = this.canvas.width / 2;
		const centerScreenY = this.canvas.height / 2;
		const levelViewX = this.viewX / levelScale;
		const levelViewY = this.viewY / levelScale;
		const { startTileX, startTileY, endTileX, endTileY } = this.getVisibleTileRange(levelViewX, levelViewY, effectiveScale, levelInfo);
		const tileWidth = levelInfo.tileWidth;
		const tileHeight = levelInfo.tileHeight;
		const visibleKeys = new Set();
		const visibleRequests = [];
		const now = performance.now();
		for (let ty = startTileY; ty < endTileY; ty++) for (let tx = startTileX; tx < endTileX; tx++) {
			const key = this.getTileKey(this.currentLevel, tx, ty);
			visibleKeys.add(key);
			const img = this.priorityCache.get(key);
			const tileLeft = tx * tileWidth;
			const tileTop = ty * tileHeight;
			const tileRight = Math.min(tileLeft + tileWidth, levelInfo.width);
			const tileBottom = Math.min(tileTop + tileHeight, levelInfo.height);
			const screenX = Math.floor(centerScreenX + (tileLeft - levelViewX) * effectiveScale);
			const screenY = Math.floor(centerScreenY + (tileTop - levelViewY) * effectiveScale);
			const screenX2 = Math.floor(centerScreenX + (tileRight - levelViewX) * effectiveScale);
			const screenY2 = Math.floor(centerScreenY + (tileBottom - levelViewY) * effectiveScale);
			const screenW = screenX2 - screenX;
			const screenH = screenY2 - screenY;
			if (img) {
				let texture = this.gl.getTileTexture(key);
				if (!texture) texture = this.gl.uploadTileTexture(key, img);
				this.gl.drawTile(texture, screenX, screenY, screenW, screenH);
			} else {
				this.gl.drawRect(screenX, screenY, screenW, screenH, this.theme.placeholderColor);
				if (!(this.priorityCache.isLoading(key) || this.tileScheduler.isPending(key)) && (this.tileFailureBackoffUntil.get(key) ?? 0) <= now) {
					this.priorityCache.startLoading(key);
					Logger.visibleScheduled(this.info.name, this.currentLevel, tx, ty);
					visibleRequests.push({
						key,
						imageName: this.info.name,
						level: this.currentLevel,
						x: tx,
						y: ty,
						priority: TilePriority.VISIBLE
					});
				}
			}
		}
		const centerTileX = levelViewX / tileWidth;
		const centerTileY = levelViewY / tileHeight;
		visibleRequests.sort((a, b) => {
			const distA = (a.x + .5 - centerTileX) ** 2 + (a.y + .5 - centerTileY) ** 2;
			const distB = (b.x + .5 - centerTileX) ** 2 + (b.y + .5 - centerTileY) ** 2;
			return distA - distB;
		});
		this.renderOverlays(centerScreenX, centerScreenY, scale);
		this.drawEditRegions(centerScreenX, centerScreenY, scale);
		this.renderAnnotations(centerScreenX, centerScreenY, scale);
		this.drawDrawingOverlay(centerScreenX, centerScreenY, scale);
		this.drawDrawBrushOverlay(centerScreenX, centerScreenY, scale);
		this.drawEraserOverlay(centerScreenX, centerScreenY, scale);
		this.drawSamOverlay(centerScreenX, centerScreenY, scale);
		this.drawLabelRasterCursor(centerScreenX, centerScreenY, scale);
		this.renderMinimap();
		this.priorityCache.markVisible(visibleKeys);
		const cachedKeys = new Set();
		for (const key of visibleKeys) if (this.priorityCache.has(key)) cachedKeys.add(key);
		const viewportState = {
			centerX: this.viewX,
			centerY: this.viewY,
			zoom: this.zoom,
			level: this.currentLevel,
			levelScale,
			tileWidth,
			tileHeight,
			levelWidth: levelInfo.width,
			levelHeight: levelInfo.height,
			imageName: this.info.name
		};
		const prefetchRequests = this.prefetchManager.computePrefetchTiles(viewportState, this.velocityTracker.getState(), visibleKeys, cachedKeys);
		if (prefetchRequests.length > 0) Logger.prefetchBatch(prefetchRequests.length, prefetchRequests[0]?.probability ?? 0);
		for (const request of prefetchRequests) if (!this.priorityCache.isLoading(request.key) && (this.tileFailureBackoffUntil.get(request.key) ?? 0) <= now) this.priorityCache.startLoading(request.key);
		const stats = this.priorityCache.getStats();
		Logger.cacheStats(stats.visible, stats.prefetch, stats.recent, stats.total);
		const eligiblePrefetch = prefetchRequests.filter((request) => (this.tileFailureBackoffUntil.get(request.key) ?? 0) <= now);
		this.tileScheduler.schedule([...visibleRequests, ...eligiblePrefetch]);
	}
	renderMinimap() {
		if (!(this.minimapEnabled && this.info)) return;
		if (!(this.minimapThumbnail || this.minimapThumbnailLoading || this.minimapThumbnailFailed)) this.loadMinimapThumbnail();
		const level0 = this.info.levels[0];
		const sourceWidth = this.minimapThumbnail?.naturalWidth ?? level0.width;
		const sourceHeight = this.minimapThumbnail?.naturalHeight ?? level0.height;
		const maxMapWidth = Math.max(1, Math.min(MINIMAP_MAX_WIDTH, this.canvas.width - MINIMAP_MARGIN * 2));
		const maxMapHeight = Math.max(1, Math.min(MINIMAP_MAX_HEIGHT, this.canvas.height - MINIMAP_MARGIN * 2));
		const mapSize = fitMinimapSize(sourceWidth, sourceHeight, maxMapWidth, maxMapHeight);
		if (mapSize.width <= 0 || mapSize.height <= 0) return;
		const minimap = computeAnchoredMinimapRect(this.canvas.width, this.canvas.height, mapSize.width, mapSize.height, MINIMAP_MARGIN, this.minimapAnchor);
		if (minimap.width <= 0 || minimap.height <= 0) return;
		const minimapZoomFactor = this.getMinimapZoomFactor();
		const worldWindow = computeMinimapWorldWindow(level0.width, level0.height, this.viewX, this.viewY, minimapZoomFactor);
		this.gl.drawRect(minimap.x, minimap.y, minimap.width, minimap.height, MINIMAP_BG_COLOR);
		if (this.minimapThumbnail) {
			const sx = worldWindow.left / level0.width * this.minimapThumbnail.naturalWidth;
			const sy = worldWindow.top / level0.height * this.minimapThumbnail.naturalHeight;
			const sw = Math.max(1, worldWindow.width / level0.width * this.minimapThumbnail.naturalWidth);
			const sh = Math.max(1, worldWindow.height / level0.height * this.minimapThumbnail.naturalHeight);
			this.gl.drawImage(this.minimapThumbnail, sx, sy, sw, sh, minimap.x, minimap.y, minimap.width, minimap.height);
		}
		const borderWidth = Math.max(0, minimap.width - 1);
		const borderHeight = Math.max(0, minimap.height - 1);
		this.gl.drawStrokeRect(minimap.x + .5, minimap.y + .5, borderWidth, borderHeight, MINIMAP_BORDER_COLOR, 1);
		const viewport = this.getViewport();
		const viewportInWorldWindow = {
			left: viewport.left - worldWindow.left,
			top: viewport.top - worldWindow.top,
			right: viewport.right - worldWindow.left,
			bottom: viewport.bottom - worldWindow.top
		};
		const viewportRect = mapViewportToMinimapRect(viewportInWorldWindow, worldWindow.width, worldWindow.height, minimap);
		if (viewportRect.width > 0 && viewportRect.height > 0) {
			const inset = MINIMAP_VIEWPORT_LINE_WIDTH / 2;
			const clampedWidth = Math.max(0, viewportRect.width - MINIMAP_VIEWPORT_LINE_WIDTH);
			const clampedHeight = Math.max(0, viewportRect.height - MINIMAP_VIEWPORT_LINE_WIDTH);
			this.gl.drawStrokeRect(viewportRect.x + inset, viewportRect.y + inset, clampedWidth, clampedHeight, MINIMAP_VIEWPORT_STROKE, MINIMAP_VIEWPORT_LINE_WIDTH);
		}
		for (const region of this.editRegionsHidden ? [] : this.editRegions ?? []) {
			const regionRect = mapViewportToMinimapRect({
				left: region.left - worldWindow.left,
				top: region.top - worldWindow.top,
				right: region.right - worldWindow.left,
				bottom: region.bottom - worldWindow.top
			}, worldWindow.width, worldWindow.height, minimap);
			if (regionRect.width > 0 && regionRect.height > 0) this.gl.drawStrokeRect(regionRect.x, regionRect.y, regionRect.width, regionRect.height, EDIT_REGION_MINIMAP_STROKE, 1, .9);
		}
	}
	getMinimapZoomFactor() {
		if (this.zoom <= 1) return 1;
		const proportionalBoost = 1 + Math.log2(this.zoom) * MINIMAP_ZOOM_GAIN;
		return Math.max(1, Math.min(MINIMAP_MAX_ZOOM_FACTOR, proportionalBoost));
	}
	renderAnnotations(centerScreenX, centerScreenY, scale) {
		if (!this.info) return;
		const viewport = this.getViewport();
		const imageName = this.info.name;
		const candidateIds = this.annotationIndex.query(imageName, viewport);
		if (candidateIds.size === 0) return;
		const order = this.annotationOrderByImage.get(imageName) ?? [];
		const visible = [];
		for (const id of order) {
			if (!candidateIds.has(id)) continue;
			const annotation = this.annotationsById.get(id);
			if (!annotation || coordCount(annotation.points) < 3) continue;
			visible.push({
				annotation,
				screenPoints: this.toScreenPoints(annotation.points, centerScreenX, centerScreenY, scale),
				screenHoles: annotation.holes?.map((hole) => this.toScreenPoints(hole, centerScreenX, centerScreenY, scale)),
				selected: !this.annotationDrawEnabled && id === this.selectedAnnotationId
			});
		}
		for (const { annotation, screenPoints, screenHoles } of visible) this.drawAnnotationFill(annotation.style, screenPoints, screenHoles);
		const strokeOrder = visible.slice().sort((a, b) => {
			const aBox = a.annotation.bbox;
			const bBox = b.annotation.bbox;
			const aArea = (aBox.right - aBox.left) * (aBox.bottom - aBox.top);
			const bArea = (bBox.right - bBox.left) * (bBox.bottom - bBox.top);
			return bArea - aArea;
		});
		for (const { annotation, screenPoints, screenHoles, selected } of strokeOrder) this.drawAnnotationStroke(annotation.style, screenPoints, screenHoles, selected);
	}
	/**
	* Clean up event listeners and resources
	*/
	destroy() {
		this.canvas.removeEventListener("mousedown", this.boundHandlers.mousedown);
		window.removeEventListener("mousemove", this.boundHandlers.mousemove);
		window.removeEventListener("mouseup", this.boundHandlers.mouseup);
		this.canvas.removeEventListener("mousemove", this.boundHandlers.canvasMousemove);
		this.canvas.removeEventListener("mouseleave", this.boundHandlers.mouseleave);
		this.canvas.removeEventListener("contextmenu", this.boundHandlers.contextmenu);
		this.canvas.removeEventListener("wheel", this.boundHandlers.wheel);
		window.removeEventListener("resize", this.boundHandlers.resize);
		this.canvas.removeEventListener("keydown", this.boundHandlers.keydown);
		this.removeAllListeners();
		this.gl.destroy();
		this.tileScheduler.destroy();
		this.tileClient.destroy();
		this.priorityCache.clear();
		this.info = null;
		this.resetMinimapThumbnailState();
		this.tileFailureCounts.clear();
		this.tileFailureBackoffUntil.clear();
		this.clearAllOverlays();
		this.annotationsById.clear();
		this.annotationOrderByImage.clear();
		this.annotationIndex.clear();
		this.annotationUndoStack.length = 0;
		this.annotationRedoStack.length = 0;
		this.eraseTrackedByOriginalId.clear();
		this.erasePathPoints = [];
		this.eraseBeforeImageAnnotations = null;
		this.drawMergePreview = null;
	}
	recordTileFailure(key) {
		const failures = (this.tileFailureCounts.get(key) ?? 0) + 1;
		this.tileFailureCounts.set(key, failures);
		const backoffMs = Math.min(this.tileFailureMaxBackoffMs, 500 * 2 ** Math.min(failures, 8));
		this.tileFailureBackoffUntil.set(key, performance.now() + backoffMs);
	}
	clearTileFailure(key) {
		this.tileFailureCounts.delete(key);
		this.tileFailureBackoffUntil.delete(key);
	}
	getVisibleTileRange(levelViewX, levelViewY, effectiveScale, levelInfo) {
		const visibleWidth = this.canvas.width / effectiveScale;
		const visibleHeight = this.canvas.height / effectiveScale;
		const visibleLeft = levelViewX - visibleWidth / 2;
		const visibleTop = levelViewY - visibleHeight / 2;
		const visibleRight = levelViewX + visibleWidth / 2;
		const visibleBottom = levelViewY + visibleHeight / 2;
		return {
			startTileX: Math.max(0, Math.floor(visibleLeft / levelInfo.tileWidth)),
			startTileY: Math.max(0, Math.floor(visibleTop / levelInfo.tileHeight)),
			endTileX: Math.min(Math.ceil(levelInfo.width / levelInfo.tileWidth), Math.ceil(visibleRight / levelInfo.tileWidth)),
			endTileY: Math.min(Math.ceil(levelInfo.height / levelInfo.tileHeight), Math.ceil(visibleBottom / levelInfo.tileHeight))
		};
	}
	clearAllOverlays() {
		for (const [, cache] of this.overlayCaches) {
			for (const key of cache.keys()) this.gl.evictTileTexture(key);
			cache.clear();
		}
		for (const id of [...this.labelRasterSessions.keys()]) this.disposeLabelRasterSession(id);
		for (const client of this.overlayClients.values()) client.destroy();
		this.isLabelStroking = false;
		this.labelStrokeOverlayId = null;
		this.labelFillOverlayId = null;
		this.overlays.clear();
		this.overlayCaches.clear();
		this.overlayClients.clear();
		this.overlayMetadataColormaps.clear();
		this.overlayRenderedColormaps.clear();
		this.overlayLabelOpacities.clear();
		this.overlayRegions.clear();
		this.overlayLabelAliasResolvers.clear();
		this.overlayLabelAliases.clear();
		this.overlayColormapOverrides.clear();
		this.overlayOrder = [];
	}
	getOverlayTileKey(overlayId, level, x, y) {
		const overlay = this.overlays.get(overlayId);
		if (!overlay) return "";
		return `overlay:${overlayId}:${PriorityCache.getKey(overlay.sourceName, level, x, y)}`;
	}
	addOverlay(config) {
		if (this.overlays.has(config.id)) this.removeOverlay(config.id);
		const sourceType = config.sourceType ?? this.info?.sourceType ?? this.tileClient.getSourceType();
		const layer = {
			id: config.id,
			sourceName: config.sourceName,
			sourceType,
			colormap: config.colormap,
			hasMetadataColormap: false,
			labels: DEFAULT_OVERLAY_LABELS,
			renderMode: null,
			coverage: "full",
			maxVisibleTiles: Math.max(1, Math.floor(config.maxVisibleTiles ?? DEFAULT_OVERLAY_MAX_VISIBLE_TILES)),
			opacity: config.opacity ?? .5,
			visible: config.visible ?? true,
			zIndex: config.zIndex ?? 0,
			info: null
		};
		this.overlays.set(config.id, layer);
		if (config.labelAliases) this.overlayLabelAliasResolvers.set(config.id, config.labelAliases);
		const labelOpacities = new Float32Array(256);
		labelOpacities.fill(1);
		labelOpacities[0] = 0;
		this.overlayLabelOpacities.set(config.id, labelOpacities);
		this.rebuildOverlayColormap(config.id);
		this.overlayCaches.set(config.id, new PriorityCache({ maxTiles: DEFAULT_OVERLAY_CACHE_SIZE }));
		const overlayClient = new TileClient({
			...this.tileClientConfig,
			sourceType
		});
		this.overlayClients.set(config.id, overlayClient);
		this.updateOverlayOrder();
		this.emit("overlay-added", { id: config.id });
		Promise.all([overlayClient.fetchImageInfo(config.sourceName), overlayClient.getRegionMetadata(config.sourceName)]).then(([info, regions]) => {
			const existing = this.overlays.get(config.id);
			if (!existing || this.overlayClients.get(config.id) !== overlayClient) return;
			existing.info = info;
			existing.renderMode = info.renderMode ?? null;
			if (regions) this.applyOverlayRegions(config.id, regions);
			this.rebuildOverlayColormap(config.id);
			this.render();
		}).catch((_err) => {});
		this.render();
	}
	/**
	* Attach parsed region metadata (labels + palette) to an overlay. Resolves the
	* overlay's label aliases (if a resolver was configured) so alias ids share
	* their canonical class's legend entry, colour and edit rules.
	*/
	applyOverlayRegions(id, regions) {
		const layer = this.overlays.get(id);
		if (!layer) return;
		this.overlayRegions.set(id, regions);
		const aliases = normalizeLabelAliases(this.overlayLabelAliasResolvers.get(id)?.(regions));
		this.overlayLabelAliases.set(id, aliases);
		this.labelRasterSessions.get(id)?.setLabelAliases(aliases);
		const labels = buildOverlayLabels(regions, aliases);
		if (labels.length > 0) {
			layer.labels = labels;
			this.emit("overlay-change", {
				id,
				property: "labels"
			});
		}
		if (regions.palette.size > 0) {
			layer.hasMetadataColormap = true;
			this.overlayMetadataColormaps.set(id, buildLabelColormap(collapseRegionMetadata(regions, aliases).palette));
			if (!this.overlayColormapOverrides.has(id) && layer.colormap !== "metadata") {
				layer.colormap = "metadata";
				this.emit("overlay-change", {
					id,
					property: "colormap"
				});
			}
		}
		this.rebuildOverlayColormap(id);
	}
	removeOverlay(id) {
		this.disposeLabelRasterSession(id);
		if (this.labelRasterTool?.overlayId === id) this.setLabelRasterTool(null);
		const cache = this.overlayCaches.get(id);
		if (cache) {
			for (const key of cache.keys()) this.gl.evictTileTexture(key);
			cache.clear();
		}
		this.overlays.delete(id);
		this.overlayCaches.delete(id);
		this.overlayClients.get(id)?.destroy();
		this.overlayClients.delete(id);
		this.overlayMetadataColormaps.delete(id);
		this.overlayRenderedColormaps.delete(id);
		this.overlayLabelOpacities.delete(id);
		this.overlayRegions.delete(id);
		this.overlayLabelAliasResolvers.delete(id);
		this.overlayLabelAliases.delete(id);
		this.overlayColormapOverrides.delete(id);
		this.updateOverlayOrder();
		this.emit("overlay-removed", { id });
		this.render();
	}
	setOverlayOpacity(id, opacity) {
		const layer = this.overlays.get(id);
		if (!layer) return;
		layer.opacity = Math.max(0, Math.min(1, opacity));
		this.emit("overlay-change", {
			id,
			property: "opacity"
		});
		this.render();
	}
	setOverlayVisible(id, visible) {
		const layer = this.overlays.get(id);
		if (!layer) return;
		layer.visible = visible;
		this.emit("overlay-change", {
			id,
			property: "visible"
		});
		this.render();
	}
	/**
	* Temporarily hides every overlay ("hold Space to peek at the tissue").
	* Purely a render-time switch: no layer's `visible` / `opacity` changes and
	* no `overlay-change` fires, so releasing restores exactly the previous look.
	* The active label-raster / annotation tool keeps working while hidden.
	*/
	setOverlaysPeekHidden(hidden) {
		if (this.overlaysPeekHidden === hidden) return;
		this.overlaysPeekHidden = hidden;
		this.render();
	}
	getOverlaysPeekHidden() {
		return this.overlaysPeekHidden;
	}
	setOverlayColormap(id, colormap) {
		const layer = this.overlays.get(id);
		if (!layer) return;
		if (colormap === "metadata" && !layer.hasMetadataColormap) return;
		layer.colormap = colormap;
		this.overlayColormapOverrides.add(id);
		this.rebuildOverlayColormap(id);
		this.emit("overlay-change", {
			id,
			property: "colormap"
		});
		this.render();
	}
	/**
	* Per-label alpha (0 hides a class). Labels wrap into 1..255 like the colormap.
	* `label` is canonicalized and the alpha applies to every raw id that aliases it.
	*/
	setOverlayLabelOpacity(id, label, opacity) {
		if (!Number.isInteger(label) || label <= 0) return;
		if (!Number.isFinite(opacity)) return;
		const labelOpacities = this.overlayLabelOpacities.get(id);
		if (!(labelOpacities && this.overlays.has(id))) return;
		const aliases = this.overlayLabelAliases.get(id);
		const canonical = canonicalLabelValue(aliases, label);
		const alpha = Math.max(0, Math.min(1, opacity));
		for (const value of labelValuesForCanonical(aliases, canonical)) {
			if (value <= 0) continue;
			labelOpacities[(value - 1) % 255 + 1] = alpha;
		}
		this.rebuildOverlayColormap(id);
		this.emit("overlay-change", {
			id,
			property: "labelOpacity",
			label: canonical
		});
		this.render();
	}
	getOverlayLabelOpacity(id, label) {
		if (!Number.isInteger(label) || label <= 0) return 0;
		const canonical = canonicalLabelValue(this.overlayLabelAliases.get(id), label);
		if (canonical <= 0) return 0;
		return this.overlayLabelOpacities.get(id)?.[(canonical - 1) % 255 + 1] ?? 1;
	}
	/** Parsed region metadata (label names + palette) for an overlay, when known. Raw, before aliasing. */
	getOverlayRegionMetadata(id) {
		return this.overlayRegions.get(id) ?? null;
	}
	/** Raw → canonical label aliases resolved for an overlay (empty when none). */
	getOverlayLabelAliases(id) {
		return this.overlayLabelAliases.get(id) ?? normalizeLabelAliases(null);
	}
	/**
	* Read the topmost visible label-mode overlay value at base-image coordinates.
	* Transparent background and labels hidden by their opacity control do not
	* obscure labels from overlays below them. When no overlay holds a class at
	* the point but the topmost readable one holds background (0), that
	* background is reported as a hit (background is a real class, distinct from
	* "unannotated"); `null` means no readable label overlay covers the point.
	* Prefers in-progress edits.
	*/
	async readOverlayLabelValueAt(imageX, imageY) {
		const baseLevel0 = this.info?.levels[0];
		if (!baseLevel0) return null;
		let background = null;
		for (const overlayId of [...this.overlayOrder].reverse()) {
			const layer = this.overlays.get(overlayId);
			const client = this.overlayClients.get(overlayId);
			const overlayLevel0 = layer?.info?.levels[0];
			if (!layer?.visible || layer.opacity <= 0 || !client || !overlayLevel0) continue;
			if (layer.renderMode !== "label" && !this.labelRasterSessions.has(overlayId)) continue;
			const overlayX = imageX * overlayLevel0.width / baseLevel0.width;
			const overlayY = imageY * overlayLevel0.height / baseLevel0.height;
			const session = this.labelRasterSessions.get(overlayId);
			const rawValue = session ? await session.readValueAt(overlayX, overlayY) : await client.readLabelValueAt(layer.sourceName, overlayX, overlayY);
			if (rawValue === null) continue;
			const value = canonicalLabelValue(this.overlayLabelAliases.get(overlayId), rawValue);
			const regions = this.overlayRegions.get(overlayId);
			if (value === 0) {
				background ?? (background = {
					overlayId,
					value: 0,
					name: getRegionLabelName(regions, 0) ?? BACKGROUND_LABEL_NAME
				});
				continue;
			}
			if (this.getOverlayLabelOpacity(overlayId, value) === 0) continue;
			return {
				overlayId,
				value,
				name: getRegionLabelName(regions, value),
				color: regions?.palette.get(value),
				...rawValue !== value ? { rawValue } : {}
			};
		}
		return background;
	}
	getOverlays() {
		return this.overlayOrder.map((id) => {
			const overlay = this.overlays.get(id);
			return {
				...overlay,
				labels: overlay.labels.map((label) => ({ ...label }))
			};
		});
	}
	rebuildOverlayColormap(id) {
		const layer = this.overlays.get(id);
		const labelOpacities = this.overlayLabelOpacities.get(id);
		if (!(layer && labelOpacities)) return;
		const base = layer.colormap === "metadata" ? this.overlayMetadataColormaps.get(id) ?? getColormapArray("tab10") : getColormapArray(layer.colormap);
		this.overlayRenderedColormaps.set(id, applyLabelOpacities(base, labelOpacities));
	}
	updateOverlayOrder() {
		this.overlayOrder = [...this.overlays.keys()].sort((a, b) => {
			const aZ = this.overlays.get(a).zIndex;
			const bZ = this.overlays.get(b).zIndex;
			return aZ - bZ;
		});
	}
	findBestOverlayLevel(ovInfo, screenPxPerOvL0Px) {
		const ovLevel0 = ovInfo.levels[0];
		let best = ovInfo.levels.length - 1;
		for (let i = 0; i < ovInfo.levels.length; i++) {
			const lvl = ovInfo.levels[i];
			const levelScale = ovLevel0.width / lvl.width;
			const effectiveScale = screenPxPerOvL0Px * levelScale;
			if (effectiveScale >= this.levelThreshold) {
				best = i;
				break;
			}
		}
		return best;
	}
	renderOverlays(centerScreenX, centerScreenY, scale) {
		if (!this.info || this.overlays.size === 0) return;
		if (this.overlaysPeekHidden) return;
		const baseLevel0 = this.info.levels[0];
		for (const overlayId of this.overlayOrder) {
			const layer = this.overlays.get(overlayId);
			const cache = this.overlayCaches.get(overlayId);
			if (!cache) continue;
			if (!layer?.visible || layer.opacity <= 0 || !layer.info) {
				cache.markVisible(new Set());
				continue;
			}
			const ovInfo = layer.info;
			const ovLevel0 = ovInfo.levels[0];
			const scaleX = baseLevel0.width / ovLevel0.width;
			const scaleY = baseLevel0.height / ovLevel0.height;
			const ovL0Scale = scale * scaleX;
			const ovLevelIdx = this.findBestOverlayLevel(ovInfo, ovL0Scale);
			const ovLevel = ovInfo.levels[ovLevelIdx];
			const ovLevelScale = ovLevel0.width / ovLevel.width;
			const ovEffectiveScale = ovL0Scale * ovLevelScale;
			const ovLevelViewX = this.viewX / scaleX / ovLevelScale;
			const ovLevelViewY = this.viewY / scaleY / ovLevelScale;
			const tileWidth = ovLevel.tileWidth;
			const tileHeight = ovLevel.tileHeight;
			const { startTileX, startTileY, endTileX, endTileY } = this.getVisibleTileRange(ovLevelViewX, ovLevelViewY, ovEffectiveScale, ovLevel);
			const visibleCount = Math.max(0, endTileX - startTileX) * Math.max(0, endTileY - startTileY);
			const capped = visibleCount > layer.maxVisibleTiles;
			const coverage = capped ? "capped" : "full";
			if (coverage !== layer.coverage) {
				layer.coverage = coverage;
				this.emit("overlay-change", {
					id: overlayId,
					property: "coverage"
				});
			}
			const session = ovLevelIdx === 0 ? this.labelRasterSessions.get(overlayId) : void 0;
			if (capped) {
				cache.markVisible(new Set());
				session?.setVisibleChunks([]);
				continue;
			}
			if (session) this.syncLabelRasterTextures(overlayId, session);
			const visibleChunks = [];
			const colormap = this.overlayRenderedColormaps.get(overlayId) ?? getColormapArray("tab10");
			this.gl.beginColormapPass(colormap, layer.opacity);
			const visibleKeys = new Set();
			const overlayRequests = [];
			for (let ty = startTileY; ty < endTileY; ty++) for (let tx = startTileX; tx < endTileX; tx++) {
				const key = this.getOverlayTileKey(overlayId, ovLevelIdx, tx, ty);
				if (!key) continue;
				visibleKeys.add(key);
				const tileLeft = tx * tileWidth;
				const tileTop = ty * tileHeight;
				const tileRight = Math.min(tileLeft + tileWidth, ovLevel.width);
				const tileBottom = Math.min(tileTop + tileHeight, ovLevel.height);
				const baseLeft = tileLeft * ovLevelScale * scaleX;
				const baseTop = tileTop * ovLevelScale * scaleY;
				const baseRight = tileRight * ovLevelScale * scaleX;
				const baseBottom = tileBottom * ovLevelScale * scaleY;
				const screenX = Math.floor(centerScreenX + (baseLeft - this.viewX) * scale);
				const screenY = Math.floor(centerScreenY + (baseTop - this.viewY) * scale);
				const screenX2 = Math.floor(centerScreenX + (baseRight - this.viewX) * scale);
				const screenY2 = Math.floor(centerScreenY + (baseBottom - this.viewY) * scale);
				const screenW = screenX2 - screenX;
				const screenH = screenY2 - screenY;
				if (session) {
					visibleChunks.push([ty, tx]);
					const renderState = session.getChunkRenderState(ty, tx);
					if (renderState === "resident") {
						const editKey = this.getLabelRasterEditTextureKey(overlayId, ty, tx);
						let texture = this.gl.getTileTexture(editKey);
						if (!texture) {
							const bytes = session.getIndexedChunk(ty, tx);
							if (bytes) {
								texture = this.gl.uploadLabelTexture(editKey, tileRight - tileLeft, tileBottom - tileTop, bytes);
								this.trackLabelRasterEditTexture(overlayId, editKey);
							}
						}
						if (texture) {
							this.gl.drawColormapTile(texture, screenX, screenY, screenW, screenH);
							continue;
						}
					} else if (renderState === "needsLoad") {
						this.requestLabelRasterChunk(overlayId, session, ty, tx);
						const texture = this.gl.getTileTexture(this.getLabelRasterEditTextureKey(overlayId, ty, tx));
						if (texture) this.gl.drawColormapTile(texture, screenX, screenY, screenW, screenH);
						continue;
					}
				}
				const img = cache.get(key);
				if (img) {
					let texture = this.gl.getTileTexture(key);
					if (!texture) texture = this.gl.uploadTileTexture(key, img, true);
					this.gl.drawColormapTile(texture, screenX, screenY, screenW, screenH);
				} else if (!(cache.isLoading(key) || this.tileScheduler.isPending(key)) && (this.tileFailureBackoffUntil.get(key) ?? 0) <= performance.now()) {
					cache.startLoading(key);
					overlayRequests.push({
						key,
						imageName: layer.sourceName,
						level: ovLevelIdx,
						x: tx,
						y: ty,
						priority: TilePriority.VISIBLE,
						query: TileViewer.OVERLAY_TILE_QUERY,
						clientId: overlayId
					});
				}
			}
			cache.markVisible(visibleKeys);
			session?.setVisibleChunks(visibleChunks);
			if (overlayRequests.length > 0) this.tileScheduler.schedule(overlayRequests);
		}
	}
	getLabelRasterEditTextureKey(overlayId, cy, cx) {
		return `overlay:${overlayId}:edit:${cy}:${cx}`;
	}
	/**
	* Kick decoding of an edited chunk and schedule ONE coalesced re-render when
	* it lands. Rendering runs every frame while chunks load; attaching a new
	* `.then(render)` per frame per chunk multiplies renders exponentially.
	*/
	requestLabelRasterChunk(overlayId, session, cy, cx) {
		const key = this.getLabelRasterEditTextureKey(overlayId, cy, cx);
		if (this.labelRasterLoadWaits.has(key)) return;
		this.labelRasterLoadWaits.add(key);
		session.ensureResident(cy, cx).catch(() => {}).finally(() => {
			this.labelRasterLoadWaits.delete(key);
			this.scheduleLabelRasterRender();
		});
	}
	scheduleLabelRasterRender() {
		if (this.labelRasterRenderScheduled) return;
		this.labelRasterRenderScheduled = true;
		const run = () => {
			this.labelRasterRenderScheduled = false;
			this.render();
		};
		if (typeof requestAnimationFrame === "function") requestAnimationFrame(run);
		else setTimeout(run, 0);
	}
	trackLabelRasterEditTexture(overlayId, key) {
		let keys = this.labelRasterEditTextureKeys.get(overlayId);
		if (!keys) {
			keys = new Set();
			this.labelRasterEditTextureKeys.set(overlayId, keys);
		}
		keys.add(key);
	}
	/** Push pending chunk edits to the GPU (full uploads or sub-rect patches). */
	syncLabelRasterTextures(overlayId, session) {
		for (const dirty of session.consumeDirtyTextures()) {
			const key = this.getLabelRasterEditTextureKey(overlayId, dirty.cy, dirty.cx);
			if (dirty.rect && this.gl.hasTileTexture(key)) {
				const [y0, x0, y1, x1] = dirty.rect;
				if (this.gl.updateLabelTextureRegion(key, x0, y0, x1 - x0, y1 - y0, dirty.indexed)) continue;
			}
			const full = dirty.rect ? session.getIndexedChunk(dirty.cy, dirty.cx) : dirty.indexed;
			if (!full) continue;
			this.gl.uploadLabelTexture(key, dirty.width, dirty.height, full);
			this.trackLabelRasterEditTexture(overlayId, key);
		}
	}
	disposeLabelRasterSession(overlayId) {
		const session = this.labelRasterSessions.get(overlayId);
		if (session) {
			session.dispose();
			this.labelRasterSessions.delete(overlayId);
		}
		const prefix = `overlay:${overlayId}:edit:`;
		for (const key of [...this.labelRasterLoadWaits]) if (key.startsWith(prefix)) this.labelRasterLoadWaits.delete(key);
		if (this.labelStrokeOverlayId === overlayId) {
			this.isLabelStroking = false;
			this.labelStrokeOverlayId = null;
		}
		const keys = this.labelRasterEditTextureKeys.get(overlayId);
		if (keys) {
			for (const key of keys) this.gl.evictTileTexture(key);
			this.labelRasterEditTextureKeys.delete(overlayId);
		}
	}
	/** Base level-0 pixel -> overlay level-0 pixel (dimension-ratio alignment, as in renderOverlays). */
	baseToOverlayPoint(layer, point) {
		const baseLevel0 = this.info?.levels[0];
		const ovLevel0 = layer.info?.levels[0];
		if (!(baseLevel0 && ovLevel0) || baseLevel0.width <= 0 || baseLevel0.height <= 0) return null;
		const scaleX = ovLevel0.width / baseLevel0.width;
		const scaleY = ovLevel0.height / baseLevel0.height;
		return {
			x: point.x * scaleX,
			y: point.y * scaleY,
			scaleX,
			scaleY
		};
	}
	getActiveLabelRaster() {
		const tool = this.labelRasterTool;
		if (!tool) return null;
		const layer = this.overlays.get(tool.overlayId);
		const session = this.labelRasterSessions.get(tool.overlayId);
		if (!(layer && session)) return null;
		return {
			tool,
			layer,
			session
		};
	}
	beginLabelRasterInteraction(imagePoint) {
		const active = this.getActiveLabelRaster();
		if (!active) return false;
		const { tool, layer, session } = active;
		const overlayPoint = this.baseToOverlayPoint(layer, imagePoint);
		if (!overlayPoint) return false;
		if (tool.tool === "pick") {
			const overlayId = tool.overlayId;
			session.readValueAt(overlayPoint.x, overlayPoint.y).then((rawValue) => {
				if (rawValue === null) return;
				const value = canonicalLabelValue(this.overlayLabelAliases.get(overlayId), rawValue);
				const regions$1 = this.overlayRegions.get(overlayId);
				this.emit("label-raster-pick", {
					overlayId,
					value,
					name: getRegionLabelName(regions$1, value),
					...rawValue !== value ? { rawValue } : {}
				});
			}).catch(() => {});
			return true;
		}
		if (this.editRegions && !pointInAnyRegion(this.editRegions, imagePoint.x, imagePoint.y)) {
			this.emit("label-raster-outside-region", {
				overlayId: tool.overlayId,
				tool: tool.tool
			});
			return true;
		}
		const regions = this.getOverlayEditRegions(overlayPoint);
		if (tool.tool === "fill" || tool.tool === "erase_fill") {
			if (!this.labelFillOverlayId) this.startLabelRasterFill(tool, session, overlayPoint, regions);
			return true;
		}
		const radiusOverlayPx = this.labelRasterBrushRadiusPx * overlayPoint.scaleX;
		const rules = session.getEditRules(tool);
		session.beginStroke(tool.tool, tool.classId, radiusOverlayPx, {
			overwritableIds: rules.overwritableIds,
			unannotatedId: rules.unannotatedId,
			overwrite: tool.overwrite ?? false,
			regions
		});
		session.strokeTo(overlayPoint.x, overlayPoint.y);
		this.isLabelStroking = true;
		this.labelStrokeOverlayId = tool.overlayId;
		this.render();
		return true;
	}
	continueLabelRasterStroke(imagePoint) {
		const overlayId = this.labelStrokeOverlayId;
		const layer = overlayId ? this.overlays.get(overlayId) : void 0;
		const session = overlayId ? this.labelRasterSessions.get(overlayId) : void 0;
		if (!(layer && session)) {
			this.isLabelStroking = false;
			this.labelStrokeOverlayId = null;
			return;
		}
		const overlayPoint = this.baseToOverlayPoint(layer, imagePoint);
		if (!overlayPoint) return;
		this.mouseImageX = imagePoint.x;
		this.mouseImageY = imagePoint.y;
		session.strokeTo(overlayPoint.x, overlayPoint.y);
		this.render();
	}
	finishLabelRasterStroke() {
		const overlayId = this.labelStrokeOverlayId;
		this.isLabelStroking = false;
		this.labelStrokeOverlayId = null;
		const session = overlayId ? this.labelRasterSessions.get(overlayId) : void 0;
		if (!(overlayId && session)) return;
		session.endStroke().then(({ chunks, changed }) => {
			if (changed) this.emit("label-raster-change", {
				overlayId,
				chunks,
				reason: "stroke"
			});
			this.render();
		}).catch((error) => {
			this.emit("label-raster-error", {
				overlayId,
				message: error instanceof Error ? error.message : String(error)
			});
		});
	}
	startLabelRasterFill(tool, session, overlayPoint, regions) {
		const overlayId = tool.overlayId;
		const rules = session.getEditRules(tool);
		this.labelFillOverlayId = overlayId;
		this.canvas.style.cursor = "progress";
		session.fill(overlayPoint.x, overlayPoint.y, {
			classId: tool.classId,
			erase: tool.tool === "erase_fill",
			overwrite: tool.overwrite ?? false,
			overwritableIds: rules.overwritableIds,
			unannotatedId: rules.unannotatedId,
			maxPixels: this.labelRasterConfig.maxFillPixels,
			chunkBounds: this.getVisibleLabelChunkBounds(overlayPoint, session, regions) ?? void 0,
			regions,
			onProgress: () => this.render()
		}).then((result) => {
			if (result.changed) this.emit("label-raster-change", {
				overlayId,
				chunks: result.chunks,
				reason: "fill"
			});
			this.emit("label-raster-fill", {
				overlayId,
				status: result.status,
				chunks: result.chunks,
				filledPixels: result.filledPixels,
				maxPixels: result.maxPixels,
				clipped: result.clipped,
				clippedByRegion: result.clippedByRegion
			});
		}).catch((error) => {
			this.emit("label-raster-error", {
				overlayId,
				message: error instanceof Error ? error.message : String(error)
			});
		}).finally(() => {
			if (this.labelFillOverlayId === overlayId) this.labelFillOverlayId = null;
			this.applyEditCursorStyle();
			this.render();
		});
	}
	isLabelRasterFilling() {
		return this.labelFillOverlayId !== null;
	}
	/**
	* Chunk-grid rectangle of the label chunks currently on screen, in the
	* overlay's level-0 chunk grid. The fill tool is confined to it so a click
	* on a large background region never floods past the visible area on a
	* production-sized slide. Computed from the viewport (not from the render
	* bookkeeping), so it is correct while the overlay is drawn from a coarser
	* pyramid level or not drawn at all. The clicked chunk is always included,
	* so rounding at the viewport edge can never turn a valid click into a noop.
	* `null` when there is no base image.
	*/
	getVisibleLabelChunkBounds(overlayPoint, session, regions) {
		if (!this.info) return null;
		const [chunkHeight, chunkWidth] = session.info.chunkShape;
		const [height, width] = session.info.shape;
		const seedCy = Math.floor(overlayPoint.y / chunkHeight);
		const seedCx = Math.floor(overlayPoint.x / chunkWidth);
		const scale = this.getScale();
		const visibleWidth = this.canvas.width / scale * overlayPoint.scaleX;
		const visibleHeight = this.canvas.height / scale * overlayPoint.scaleY;
		const centerX = this.viewX * overlayPoint.scaleX;
		const centerY = this.viewY * overlayPoint.scaleY;
		let left = centerX - visibleWidth / 2;
		let top = centerY - visibleHeight / 2;
		let right = centerX + visibleWidth / 2;
		let bottom = centerY + visibleHeight / 2;
		const regionBounds = regions ? unionRegionBounds(regions) : null;
		if (regionBounds) {
			left = Math.max(left, regionBounds.left);
			top = Math.max(top, regionBounds.top);
			right = Math.min(right, regionBounds.right);
			bottom = Math.min(bottom, regionBounds.bottom);
		}
		const rows = Math.ceil(height / chunkHeight);
		const cols = Math.ceil(width / chunkWidth);
		return {
			cy0: Math.max(0, Math.min(seedCy, Math.floor(top / chunkHeight))),
			cx0: Math.max(0, Math.min(seedCx, Math.floor(left / chunkWidth))),
			cy1: Math.min(rows - 1, Math.max(seedCy, Math.ceil(bottom / chunkHeight) - 1)),
			cx1: Math.min(cols - 1, Math.max(seedCx, Math.ceil(right / chunkWidth) - 1))
		};
	}
	applyEditCursorStyle() {
		const tool = this.labelRasterTool;
		if (!tool) {
			this.canvas.style.cursor = this.annotationTool === "sam" ? "crosshair" : "";
			return;
		}
		if (this.labelFillOverlayId) {
			this.canvas.style.cursor = "progress";
			return;
		}
		if (tool.tool !== "pick" && this.editRegions && this.mouseImageX >= 0 && this.mouseImageY >= 0 && !pointInAnyRegion(this.editRegions, this.mouseImageX, this.mouseImageY)) {
			this.canvas.style.cursor = "not-allowed";
			return;
		}
		this.canvas.style.cursor = tool.tool === "pick" || tool.tool === "fill" || tool.tool === "erase_fill" ? "crosshair" : "cell";
	}
	/**
	* Vector draw / SAM tools: show `not-allowed` while the pointer is outside every
	* edit region, and restore the previous cursor (set by the host) inside.
	*/
	updateAnnotationRegionCursor() {
		const blocked = !this.labelRasterTool && this.annotationDrawEnabled && (this.annotationTool === "draw" || this.annotationTool === "sam") && this.editRegions !== null && this.mouseImageX >= 0 && this.mouseImageY >= 0 && !pointInAnyRegion(this.editRegions, this.mouseImageX, this.mouseImageY);
		if (blocked) {
			if (this.canvas.style.cursor !== "not-allowed") {
				this.annotationCursorOutsideRegion = this.canvas.style.cursor;
				this.canvas.style.cursor = "not-allowed";
			}
			return;
		}
		if (this.annotationCursorOutsideRegion !== null) {
			if (this.canvas.style.cursor === "not-allowed") this.canvas.style.cursor = this.annotationCursorOutsideRegion;
			this.annotationCursorOutsideRegion = null;
		}
	}
	/** True when the polygon's bbox lies inside a single edit region (no clipping needed). */
	polygonInsideEditRegion(points) {
		const regions = this.editRegions;
		if (!regions) return true;
		const bbox = computeBoundingBox(points);
		return regions.some((region) => bbox.left >= region.left && bbox.right <= region.right && bbox.top >= region.top && bbox.bottom <= region.bottom);
	}
	/**
	* A vector draw / SAM press outside every edit region: emit
	* `annotation-outside-region` and report true so the press is ignored.
	*/
	rejectAnnotationPressOutsideRegions(imagePoint) {
		if (!this.editRegions || pointInAnyRegion(this.editRegions, imagePoint.x, imagePoint.y)) return false;
		this.emit("annotation-outside-region", { tool: this.annotationTool });
		return true;
	}
	/**
	* Confine editing to rectangles in BASE level-0 pixels. While set, label
	* raster brush / eraser / fill never write outside them (a press outside
	* emits `label-raster-outside-region`), new vector annotations (draw, brush,
	* SAM) are clipped to them (a press outside emits `annotation-outside-region`),
	* everything outside is dimmed and outlined, and the minimap shows the
	* regions. `null` or an empty list restores unrestricted editing (the
	* default). Pick, selection, vector erase and existing annotations are never
	* restricted.
	*/
	setEditRegions(regions) {
		const next = [];
		for (const region of regions ?? []) {
			const left = Math.min(region.left, region.right);
			const right = Math.max(region.left, region.right);
			const top = Math.min(region.top, region.bottom);
			const bottom = Math.max(region.top, region.bottom);
			if (right > left && bottom > top) next.push({
				id: region.id,
				left,
				top,
				right,
				bottom
			});
		}
		this.editRegions = next.length > 0 ? next : null;
		if (this.labelRasterTool) this.applyEditCursorStyle();
		else this.updateAnnotationRegionCursor();
		this.render();
	}
	/** Active edit regions (BASE level-0 px) or null when editing is unrestricted. */
	getEditRegions() {
		return this.editRegions ? this.editRegions.map((region) => ({ ...region })) : null;
	}
	/**
	* Hides the edit regions' dim and outlines (main canvas and minimap) so the
	* slide and overlay can be seen unobstructed. Purely visual: editing stays
	* confined to the regions, a press outside still emits
	* `label-raster-outside-region` and the cursor still turns `not-allowed`
	* outside them. Survives `setEditRegions` and image loads: it is a
	* viewer preference, not part of the region set.
	*/
	setEditRegionsHidden(hidden) {
		if (this.editRegionsHidden === hidden) return;
		this.editRegionsHidden = hidden;
		this.render();
	}
	getEditRegionsHidden() {
		return this.editRegionsHidden;
	}
	/** Edit regions in the overlay's level-0 pixels (undefined = unrestricted). */
	getOverlayEditRegions(overlayPoint) {
		const regions = this.editRegions;
		if (!regions) return void 0;
		return regions.map((region) => ({
			left: region.left * overlayPoint.scaleX,
			top: region.top * overlayPoint.scaleY,
			right: region.right * overlayPoint.scaleX,
			bottom: region.bottom * overlayPoint.scaleY
		}));
	}
	/** Dim everything outside the edit regions and outline each region (unless hidden). */
	drawEditRegions(centerScreenX, centerScreenY, scale) {
		const regions = this.editRegions;
		if (!regions || this.editRegionsHidden) return;
		const toScreen = (region) => ({
			left: centerScreenX + (region.left - this.viewX) * scale,
			top: centerScreenY + (region.top - this.viewY) * scale,
			right: centerScreenX + (region.right - this.viewX) * scale,
			bottom: centerScreenY + (region.bottom - this.viewY) * scale
		});
		const screenRegions = regions.map(toScreen);
		const outer = {
			left: 0,
			top: 0,
			right: this.canvas.width,
			bottom: this.canvas.height
		};
		for (const rect of computeDimRects(outer, screenRegions)) this.gl.drawRect(rect.x, rect.y, rect.width, rect.height, EDIT_REGION_DIM_COLOR, EDIT_REGION_DIM_ALPHA);
		for (const region of screenRegions) {
			const width = region.right - region.left;
			const height = region.bottom - region.top;
			this.gl.drawStrokeRect(region.left - 3, region.top - 3, width + 6, height + 6, "#000000", 3, .35);
			this.gl.drawStrokeRect(region.left - 1.5, region.top - 1.5, width + 3, height + 3, EDIT_REGION_STROKE, 1.5, .95);
		}
	}
	prefetchLabelRasterAround(imageX, imageY) {
		const active = this.getActiveLabelRaster();
		if (!active || imageX < 0 || imageY < 0) return;
		const overlayPoint = this.baseToOverlayPoint(active.layer, {
			x: imageX,
			y: imageY
		});
		if (!overlayPoint) return;
		const isFill = active.tool.tool === "fill" || active.tool.tool === "erase_fill";
		const radiusBasePx = isFill ? 1 : this.labelRasterBrushRadiusPx;
		const regions = this.editRegions;
		if (regions && !rectIntersectsAnyRegion(regions, {
			left: imageX - radiusBasePx,
			top: imageY - radiusBasePx,
			right: imageX + radiusBasePx + 1,
			bottom: imageY + radiusBasePx + 1
		})) return;
		active.session.prefetchAround(overlayPoint.x, overlayPoint.y, radiusBasePx * overlayPoint.scaleX);
	}
	drawLabelRasterCursor(centerScreenX, centerScreenY, scale) {
		const tool = this.labelRasterTool;
		if (!tool || tool.tool === "pick") return;
		if (this.mouseImageX < 0 || this.mouseImageY < 0) return;
		const radius = Math.max(1, this.labelRasterBrushRadiusPx * scale);
		const pointerX = centerScreenX + (this.mouseImageX - this.viewX) * scale;
		const pointerY = centerScreenY + (this.mouseImageY - this.viewY) * scale;
		const rgb = this.overlayRegions.get(tool.overlayId)?.palette.get(tool.classId);
		const color = rgb ? rgbToHex(rgb) : "#ffffff";
		const overwriteRing = (r) => {
			if (!tool.overwrite) return;
			this.gl.drawCircle(pointerX, pointerY, r + 4, null, {
				color: "#000000",
				alpha: .35,
				width: 3.5
			});
			this.gl.drawCircle(pointerX, pointerY, r + 4, null, {
				color: LABEL_RASTER_OVERWRITE_COLOR,
				alpha: .95,
				width: 1.5
			});
		};
		if (tool.tool === "fill") {
			const marker = 7;
			this.gl.drawCircle(pointerX, pointerY, marker, {
				color,
				alpha: .35
			}, {
				color: "#000000",
				alpha: .35,
				width: 3
			});
			this.gl.drawCircle(pointerX, pointerY, marker, null, {
				color,
				alpha: .95,
				width: 1.5
			});
			overwriteRing(marker);
			return;
		}
		if (tool.tool === "erase_fill") {
			const marker = 7;
			this.gl.drawCircle(pointerX, pointerY, marker, {
				color,
				alpha: .1
			}, {
				color: "#000000",
				alpha: .35,
				width: 3
			});
			this.gl.drawCircle(pointerX, pointerY, marker, null, {
				color: "#ffffff",
				alpha: .9,
				width: 1.5
			});
			this.gl.drawCircle(pointerX, pointerY, 3, null, {
				color,
				alpha: .9,
				width: 1
			});
			overwriteRing(marker);
			return;
		}
		if (tool.tool === "brush") {
			this.gl.drawCircle(pointerX, pointerY, radius, {
				color,
				alpha: .25
			}, {
				color: "#000000",
				alpha: .35,
				width: 3
			});
			this.gl.drawCircle(pointerX, pointerY, radius, null, {
				color,
				alpha: .95,
				width: 1.5
			});
		} else {
			this.gl.drawCircle(pointerX, pointerY, radius, {
				color,
				alpha: .1
			}, {
				color: "#000000",
				alpha: .35,
				width: 3
			});
			this.gl.drawCircle(pointerX, pointerY, radius, null, {
				color: "#ffffff",
				alpha: .9,
				width: 1.5
			});
			this.gl.drawCircle(pointerX, pointerY, Math.max(1, radius * .5), null, {
				color,
				alpha: .9,
				width: 1
			});
		}
		overwriteRing(radius);
	}
	/**
	* Start an in-place edit session for a 2-D uint8/uint16 label overlay.
	* Resolves with the source description; rejects if the overlay is unknown or
	* not an editable label zarr.
	*/
	async enableLabelRasterEditing(overlayId) {
		const layer = this.overlays.get(overlayId);
		if (!layer) throw new Error(`Unknown overlay: ${overlayId}`);
		const existing = this.labelRasterSessions.get(overlayId);
		if (existing) return existing.info;
		let source = this.labelRasterConfig.chunkSourceFactory?.(overlayId, layer.sourceName) ?? null;
		if (!source) {
			const client = this.overlayClients.get(overlayId);
			if (!client) throw new Error(`Overlay ${overlayId} has no tile client`);
			const sourceName = layer.sourceName;
			const info = await client.getLabelRasterSourceInfo(sourceName);
			if (!info) throw new Error("Overlay is not an editable 2-D uint8/uint16 label zarr");
			source = {
				info,
				readChunk: (cy, cx, signal) => client.readLabelChunk(sourceName, cy, cx, signal)
			};
		}
		const current = this.overlays.get(overlayId);
		if (!current) throw new Error(`Unknown overlay: ${overlayId}`);
		const raced = this.labelRasterSessions.get(overlayId);
		if (raced) return raced.info;
		const session = new LabelRasterEditSession(source, {
			maxResidentChunks: this.labelRasterConfig.maxResidentChunks,
			maxFillPixels: this.labelRasterConfig.maxFillPixels
		});
		this.labelRasterSessions.set(overlayId, session);
		if (!current.info) current.info = {
			name: current.sourceName,
			sourceType: current.sourceType,
			renderMode: "label",
			dtype: source.info.dtype,
			levels: [{
				index: 0,
				width: source.info.shape[1],
				height: source.info.shape[0],
				tileWidth: source.info.chunkShape[1],
				tileHeight: source.info.chunkShape[0]
			}]
		};
		current.renderMode ?? (current.renderMode = "label");
		if (source.info.regions && !this.overlayRegions.has(overlayId)) this.applyOverlayRegions(overlayId, source.info.regions);
		session.setLabelAliases(this.overlayLabelAliases.get(overlayId));
		this.render();
		return source.info;
	}
	disableLabelRasterEditing(overlayId) {
		if (!this.labelRasterSessions.has(overlayId)) return;
		if (this.labelRasterTool?.overlayId === overlayId) this.setLabelRasterTool(null);
		this.disposeLabelRasterSession(overlayId);
		this.render();
	}
	isLabelRasterEditingEnabled(overlayId) {
		return this.labelRasterSessions.has(overlayId);
	}
	/** Activate a label tool (forces the vector tool to "pan"); null deactivates. */
	setLabelRasterTool(state) {
		if (state) {
			if (this.annotationTool !== "pan") this.setAnnotationTool("pan");
			if (this.isLabelStroking && this.labelStrokeOverlayId !== state.overlayId) this.finishLabelRasterStroke();
			this.labelRasterBrushRadiusPx = this.clampLabelRasterRadius(state.radiusPx);
			this.labelRasterTool = {
				overlayId: state.overlayId,
				tool: state.tool,
				classId: Math.max(0, Math.round(state.classId)),
				radiusPx: this.labelRasterBrushRadiusPx,
				overwrite: state.overwrite ?? false,
				...state.unannotatedId !== void 0 ? { unannotatedId: Math.max(0, Math.round(state.unannotatedId)) } : {},
				...state.overwritableIds ? { overwritableIds: [...state.overwritableIds] } : {}
			};
		} else {
			if (this.isLabelStroking) this.finishLabelRasterStroke();
			this.labelRasterTool = null;
		}
		this.applyEditCursorStyle();
		this.emit("label-raster-tool-change", { state: this.getLabelRasterTool() });
		this.render();
	}
	/**
	* Active label tool with the eraser value / overwritable set resolved
	* (explicit state, else the overlay's declared `unknown_id`, else 0).
	*/
	getLabelRasterTool() {
		if (!this.labelRasterTool) return null;
		const session = this.labelRasterSessions.get(this.labelRasterTool.overlayId);
		const rules = session ? session.getEditRules(this.labelRasterTool) : resolveLabelRasterEditRules(this.labelRasterTool, null);
		return {
			...this.labelRasterTool,
			radiusPx: this.labelRasterBrushRadiusPx,
			overwrite: this.labelRasterTool.overwrite ?? false,
			unannotatedId: rules.unannotatedId,
			overwritableIds: rules.overwritableIds
		};
	}
	/**
	* Overwrite mode for the active label tool (brush over any class, eraser
	* clears any class, fill replaces the clicked class's blob). Off by default;
	* a UI setting that is never persisted. `O` inside the canvas toggles it.
	* Takes effect for the next stroke / fill; an in-flight stroke keeps its mode.
	*/
	setLabelRasterOverwrite(enabled) {
		if (!this.labelRasterTool) return;
		if ((this.labelRasterTool.overwrite ?? false) === enabled) return;
		this.labelRasterTool.overwrite = enabled;
		this.emit("label-raster-tool-change", { state: this.getLabelRasterTool() });
		this.render();
	}
	clampLabelRasterRadius(radius) {
		const value = Number.isFinite(radius) ? radius : DEFAULT_LABEL_RASTER_RADIUS_PX;
		return Math.max(MIN_LABEL_RASTER_RADIUS_PX, Math.min(this.labelRasterMaxRadiusPx, value));
	}
	/** Brush/eraser radius in base image pixels. */
	setLabelRasterBrushRadiusPx(radius) {
		this.labelRasterBrushRadiusPx = this.clampLabelRasterRadius(radius);
		if (this.labelRasterTool) {
			this.labelRasterTool.radiusPx = this.labelRasterBrushRadiusPx;
			this.emit("label-raster-tool-change", { state: this.getLabelRasterTool() });
		}
		this.render();
	}
	getLabelRasterBrushRadiusPx() {
		return this.labelRasterBrushRadiusPx;
	}
	setLabelRasterMaxBrushRadiusPx(max) {
		this.labelRasterMaxRadiusPx = Math.max(MIN_LABEL_RASTER_RADIUS_PX, max);
		if (this.labelRasterBrushRadiusPx > this.labelRasterMaxRadiusPx) this.setLabelRasterBrushRadiusPx(this.labelRasterBrushRadiusPx);
	}
	/** Sparse diff of the current edits for an overlay (null when none). */
	getLabelRasterDiff(overlayId) {
		return this.labelRasterSessions.get(overlayId)?.getDiff() ?? null;
	}
	/**
	* Replace an overlay's edits with `diff` (null clears). Emits
	* `label-raster-change` (reason "apply" / "reset") only when chunks changed.
	*/
	async setLabelRasterDiff(overlayId, diff) {
		const session = this.labelRasterSessions.get(overlayId);
		if (!session) return {
			appliedChunks: 0,
			changedChunks: [],
			crcMismatches: []
		};
		const before = session.getTrackedChunks();
		const result = await session.applyDiff(diff);
		const trackedKeys = this.labelRasterEditTextureKeys.get(overlayId);
		for (const [cy, cx] of result.changedChunks) {
			const key = this.getLabelRasterEditTextureKey(overlayId, cy, cx);
			this.gl.evictTileTexture(key);
			trackedKeys?.delete(key);
		}
		if (result.appliedChunks > 0) {
			const chunks = diff ? diff.chunks.map((chunk) => [chunk.index[0], chunk.index[1]]) : before;
			this.emit("label-raster-change", {
				overlayId,
				chunks,
				reason: diff ? "apply" : "reset"
			});
		}
		this.render();
		return result;
	}
	resetLabelRasterEdits(overlayId) {
		return this.setLabelRasterDiff(overlayId, null).then(() => {});
	}
	getLabelRasterStats(overlayId) {
		return this.labelRasterSessions.get(overlayId)?.getStats() ?? null;
	}
	/**
	* Enable or disable prefetching
	*/
	setPrefetchEnabled(enabled) {
		this.prefetchManager.setEnabled(enabled);
		if (!enabled) {
			this.tileScheduler.cancelPrefetch();
			this.priorityCache.demotePrefetch();
		}
	}
	/**
	* Check if prefetching is enabled
	*/
	isPrefetchEnabled() {
		return this.prefetchManager.isEnabled();
	}
	/**
	* Get cache statistics
	*/
	getCacheStats() {
		return this.priorityCache.getStats();
	}
	/**
	* Get scheduler statistics
	*/
	getSchedulerStats() {
		return this.tileScheduler.getStats();
	}
};
TileViewer.OVERLAY_TILE_QUERY = "format=png&indexedLabels=true";

//#endregion
//#region src/tissue-detector.ts
const DEFAULT_WHITE_THRESHOLD = 240;
const DEFAULT_PADDING = .02;
/**
* Check if a pixel is background (white/near-white or light gray).
* Uses both brightness threshold and saturation check to handle
* scanner artifacts and slight color variations in the background.
*/
function isBackground(r, g, b, threshold) {
	if (r > threshold && g > threshold && b > threshold) return true;
	const avg = (r + g + b) / 3;
	const maxDiff = Math.max(Math.abs(r - avg), Math.abs(g - avg), Math.abs(b - avg));
	if (avg > 235 && maxDiff < 10) return true;
	return false;
}
/**
* Load an image from URL into an HTMLImageElement.
*/
function loadImage(url) {
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.crossOrigin = "anonymous";
		img.onload = () => resolve(img);
		img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
		img.src = url;
	});
}
/**
* Detect tissue boundaries in an image by analyzing pixel colors.
*
* @param imageUrl URL of the image to analyze (typically a thumbnail)
* @param options Detection options
* @returns Normalized bounds (0-1) of the detected tissue region
*/
async function detectTissueBounds(imageUrl, options = {}) {
	const threshold = options.whiteThreshold ?? DEFAULT_WHITE_THRESHOLD;
	const padding = options.padding ?? DEFAULT_PADDING;
	const img = await loadImage(imageUrl);
	const width = img.naturalWidth;
	const height = img.naturalHeight;
	const canvas = document.createElement("canvas");
	canvas.width = width;
	canvas.height = height;
	const ctx = canvas.getContext("2d");
	ctx.drawImage(img, 0, 0);
	const imageData = ctx.getImageData(0, 0, width, height);
	const data = imageData.data;
	let minX = width;
	let minY = height;
	let maxX = 0;
	let maxY = 0;
	let foundTissue = false;
	for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
		const i = (y * width + x) * 4;
		const r = data[i];
		const g = data[i + 1];
		const b = data[i + 2];
		if (!isBackground(r, g, b, threshold)) {
			foundTissue = true;
			minX = Math.min(minX, x);
			minY = Math.min(minY, y);
			maxX = Math.max(maxX, x);
			maxY = Math.max(maxY, y);
		}
	}
	if (!foundTissue) return {
		left: 0,
		top: 0,
		right: 1,
		bottom: 1
	};
	const tissueWidth = maxX - minX;
	const tissueHeight = maxY - minY;
	const padX = tissueWidth * padding;
	const padY = tissueHeight * padding;
	return {
		left: Math.max(0, minX - padX) / width,
		top: Math.max(0, minY - padY) / height,
		right: Math.min(width, maxX + 1 + padX) / width,
		bottom: Math.min(height, maxY + 1 + padY) / height
	};
}

//#endregion
export { BACKGROUND_LABEL_NAME, COLORMAP_NAMES, DEFAULT_MAX_FILL_PIXELS, EventEmitter, LabelRasterEditSession, Logger, PrefetchManager, PriorityCache, SamClient, TileCache, TileClient, TilePriority, TileScheduler, TileViewer, UNANNOTATED_LABEL_NAME, VelocityTracker, applyChunkRuns, applyLabelOpacities, buildLabelAliasLut, buildLabelColormap, buildZarrChannels, canonicalLabelValue, chunkCrc32, chunkKey, clamp, clippedChunkExtent, clipperDifference, clipperSubtractBrush, clipperUnion, cloneCoords, collapseRegionMetadata, collectClassesUsed, computeChunkRuns, computeDimRects, coordCount, crc32, createCoordBuffer, createDefaultTransform, createLabelArray, detectTissueBounds, diffChunkKeys, equalCoords, erf, fromPointArray, getColormapArray, getRegionLabelName, getX, getY, isLabelRasterFillTool, labelArrayToLEBytes, labelRasterToolBase, labelToColormapIndex, labelToRgba, labelValuesForCanonical, labelsToIndexedBytes, normalCdf, normalCdfWithParams, normalizeEditRegions, normalizeLabelAliases, parseMicronsPerPixel, parseRegionMetadata, pointInAnyRegion, polygonsIntersect, rectIntersectsAnyRegion, resolveLabelRasterEditRules, runsEqual, toPointArray, toRowMajorYX, validateAxes, withLabelRasterFillMode };
//# sourceMappingURL=index.js.map